<template>

  <div class="followup-panel" v-if="!patientData && !loading">
      <div class="followup-error-state">
        <div class="error-icon">
          <i class="fa fa-exclamation-circle"></i>
        </div>
        <h2 class="error-title">Unable to Load Patient Data</h2>
        <p class="error-message">Either this patient has scheduled an appointment, or the claim has been received in Open Dental.</p>
        <p class="error-hint">Close this window and refresh the page to resolve the issue.</p>
        <button type="button" class="followup-btn followup-btn-primary" @click="closeSlideOutPanel">
          <i class="fa fa-times mr-2"></i>Close
        </button>
      </div>
  </div>
  <div class="followup-panel" v-else>
    <!-- Modern Header -->
    <div class="followup-header">
      <div class="followup-header-content">
        <div class="followup-header-icon">
          <i class="fa fa-tasks"></i>
        </div>
        <div class="followup-header-text">
          <h4 class="followup-title">{{this.printTitleOfFollowUp}} Follow-Up</h4>
          <span class="followup-subtitle">Patient follow-up management</span>
        </div>
      </div>
      <button type="button" class="followup-close-btn" @click="closeSlideOutPanel" id="close-tooltip">
        <i class="fa fa-times"></i>
      </button>
      <b-tooltip target="close-tooltip" triggers="hover">Close</b-tooltip>
    </div>

    <CreateFollowupSkeleton v-if="loading" />
    <section class="followup-content" v-if="!loading">
        <!-- Patient Info Card -->
        <div class="patient-info-card">
          <div class="patient-header">
            <div class="patient-avatar">
              <i class="fa fa-user"></i>
            </div>
            <div class="patient-details">
              <div class="patient-name-row">
                <a href="#" @click="gotoPage" class="patient-name">{{formatName(patientData.PatientName)}}</a>
                <span class="patient-demographics">{{formatAge(getAge(patientData.Birthdate))}}{{getGender(patientData.Gender)}}</span>
                <span class="premed-badge" v-if="patientData.Premed === 1">
                  <i class="fa fa-exclamation-triangle"></i> Premed
                </span>
              </div>

              <!-- Contact Info Pills -->
              <div class="contact-pills">
                <span class="contact-pill" v-if="patientData.HmPhone && patientData.HmPhone !== ''">
                  <i class="fa fa-home"></i> {{formatPhone(patientData.HmPhone)}}
                </span>
                <span class="contact-pill" v-if="patientData.WirelessPhone && patientData.WirelessPhone !== ''">
                  <i class="fa fa-mobile"></i> {{formatPhone(patientData.WirelessPhone)}}
                </span>
                <span class="contact-pill" v-if="patientData.WkPhone && patientData.WkPhone !== ''">
                  <i class="fa fa-briefcase"></i> {{formatPhone(patientData.WkPhone)}}
                </span>
                <span class="contact-pill contact-pill-email" v-if="patientData.Email && patientData.Email !== ''">
                  <i class="fa fa-envelope"></i> {{patientData.Email}}
                </span>
              </div>

              <!-- Address -->
              <div class="patient-address" v-if="patientData.Address && patientData.Address !== ''">
                <i class="fa fa-map-marker"></i>
                <span>
                  {{patientData.Address}}<span v-if="patientData.Address2">, {{patientData.Address2}}</span>
                  <span v-if="patientData.City">, {{patientData.City}}</span>
                  <span v-if="patientData.State">, {{patientData.State}}</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Status Indicators -->
          <div class="status-indicators" v-if="isPatientBalancesPage || isOverdueRecallPage || isUnscheduledTreatmentPage">
            <div class="status-item status-balance" v-if="isPatientBalancesPage">
              <i class="fa fa-dollar status-icon"></i>
              <span class="status-label">Balance</span>
              <span class="status-value">{{this.patientBalance | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</span>
            </div>

            <div class="status-item status-info" v-if="isOverdueRecallPage && patientData.RecallDue && (patientData.RecallDue !== '01/01/1901' || patientData.RecallDue !== '01/01/0001')">
              <i class="fa fa-calendar status-icon"></i>
              <span class="status-label">Recall Due</span>
              <span class="status-value">{{patientData.RecallDue | dateformat(dateFormatStr)}}</span>
            </div>

            <div class="status-item status-scheduled" v-if="isUnscheduledTreatmentPage && patientData.AptDateTime">
              <i class="fa fa-calendar-check status-icon"></i>
              <span class="status-label">Recall Scheduled</span>
              <span class="status-value">{{patientData.AptDateTime | dateformat(dateFormatStr)}}</span>
            </div>

            <div class="status-item status-info" v-if="isUnscheduledTreatmentPage && !patientData.AptDateTime && (patientData.RecallDue !== '01/01/1901' || patientData.RecallDue !== '01/01/0001')">
              <i class="fa fa-calendar status-icon"></i>
              <span class="status-label">Recall Due</span>
              <span class="status-value">{{patientData.RecallDue | dateformat(dateFormatStr)}}</span>
            </div>
          </div>
        </div>
          
        <PopupErrorDisplay/>
        <section id="followupslideoutpanel" v-if="allSettings">
            <!-- Actions Card -->
            <div class="followup-actions-card">
              <div class="actions-header">
                <div class="actions-title-section">
                  <span class="actions-title">Follow-up Actions</span>
                </div>
                <div class="attempts-counter">
                  <span class="attempts-current">{{this.totalAttemptsMade}}</span>
                  <span v-if="maxFollowups" class="attempts-separator">/</span>
                  <span v-if="maxFollowups" class="attempts-max">{{maxFollowups}}</span>
                  <span class="attempts-label">completed</span>
                </div>
              </div>

              <div class="action-buttons-row">
                <!-- Phone Followup -->
                <button
                  type="button"
                  class="followup-action-btn"
                  :class="{ 'followup-action-btn-disabled': !addAttemptClass }"
                  @click="showAttempt('phone')"
                >
                  <i class="fa fa-plus"></i>
                  <span>Phone Followup</span>
                </button>

                <!-- SMS Followup -->
                <button
                  type="button"
                  class="followup-action-btn"
                  :class="{ 'followup-action-btn-disabled': !shouldEnableSMSFollowup }"
                  @click="showAttempt('sms')"
                  :disabled="!shouldEnableSMSFollowup"
                >
                  <i class="fa fa-plus"></i>
                  <span>SMS Followup</span>
                  <i v-if="!shouldEnableSMSFollowup" class="fa fa-question-circle btn-help-icon" id="help_sms_disabled"></i>
                  <b-popover target="help_sms_disabled" triggers="hover" placement="auto" :show.sync="showSmsHelpPopOver" custom-class="feature-help-popover">
                    <template v-slot:title>Why is "Add SMS Followup" button disabled?</template>
                    <div class="help_popover" style="min-width: 320px; width: 100%; white-space: normal;">
                      <div v-for="(reason, index) in smsFollowupDisabledReasons" :key="index" class="d-flex mb-2" style="width: 100%; min-width: 300px;">
                        <i :class="`fa ${reason.icon} text-${reason.isFeatureIssue ? 'warning' : 'info'} mr-3`" style="margin-top: 2px; flex-shrink: 0;"></i>
                        <p class="text-muted small mb-0" style="white-space: normal; word-wrap: break-word; flex: 1; min-width: 200px;">{{ reason.text }}</p>
                      </div>

                      <div v-if="smsFollowupDisabledReasons.length === 0" class="d-flex mb-2" style="width: 100%; min-width: 300px;">
                        <i class="fa fa-question-circle text-muted mr-3" style="margin-top: 2px; flex-shrink: 0;"></i>
                        <p class="text-muted small mb-0" style="white-space: normal; word-wrap: break-word; flex: 1; min-width: 200px;">SMS followup appears to be configured but button is still disabled</p>
                      </div>

                      <hr v-if="!smsFollowupFeatureEnabled && !smsOutboundOff" />

                      <div v-if="!smsFollowupFeatureEnabled && !smsOutboundOff" class="followup-request-enable" data-test="followup-sms-upsell">
                        <p class="small mb-2"><strong>Enable SMS Followups</strong> — send automated SMS follow-ups to patients for recalls, treatment, and payment collection.</p>
                        <FeatureRequestButton
                          class="btn btn-sm btn-primary"
                          feature-key="SMS"
                          feature-label="2-Way SMS texting"
                          source-page="Create Followup - SMS Followup"
                        />
                        <FeatureRequestNote feature="SMS" />
                      </div>

                      <div v-else-if="smsFollowupDisabledReasons.some(r => !r.isFeatureIssue)" class="mt-2 p-2 bg-light rounded">
                        <small class="text-muted">
                          <i class="fa fa-lightbulb-o mr-1"></i>
                          <strong>Tip:</strong> Update patient contact information or check follow-up type restrictions.
                        </small>
                      </div>
                    </div>
                  </b-popover>
                </button>

                <!-- Email Followup -->
                <button
                  type="button"
                  class="followup-action-btn"
                  :class="{ 'followup-action-btn-disabled': !shouldEnableEmailFollowup }"
                  @click="showAttempt('email')"
                  :disabled="!shouldEnableEmailFollowup"
                >
                  <i class="fa fa-plus"></i>
                  <span>Email Followup</span>
                  <i v-if="!shouldEnableEmailFollowup" class="fa fa-question-circle btn-help-icon" id="help_email_disabled"></i>
                  <b-popover target="help_email_disabled" triggers="hover" placement="auto" :show.sync="showEmailHelpPopOver" custom-class="feature-help-popover">
                    <template v-slot:title>Why is "Email Followup" button disabled?</template>
                    <div class="help_popover" style="min-width: 320px; width: 100%; white-space: normal;">
                      <div v-for="(reason, index) in emailFollowupDisabledReasons" :key="index" class="d-flex mb-2" style="width: 100%; min-width: 300px;">
                        <i :class="`fa ${reason.icon} text-${reason.isFeatureIssue ? 'warning' : 'info'} mr-3`" style="margin-top: 2px; flex-shrink: 0;"></i>
                        <p class="text-muted small mb-0" style="white-space: normal; word-wrap: break-word; flex: 1; min-width: 200px;">{{ reason.text }}</p>
                      </div>

                      <div v-if="emailFollowupDisabledReasons.length === 0" class="d-flex mb-2" style="width: 100%; min-width: 300px;">
                        <i class="fa fa-question-circle text-muted mr-3" style="margin-top: 2px; flex-shrink: 0;"></i>
                        <p class="text-muted small mb-0" style="white-space: normal; word-wrap: break-word; flex: 1; min-width: 200px;">Email followup appears to be configured but button is still disabled</p>
                      </div>

                      <hr v-if="!emailFollowupFeatureEnabled" />

                      <div v-if="!emailFollowupFeatureEnabled" class="followup-request-enable" data-test="followup-email-upsell">
                        <p class="small mb-2"><strong>Enable Email Followups</strong> — send professional email follow-ups to patients with customizable templates and tracking.</p>
                        <FeatureRequestButton
                          class="btn btn-sm btn-primary"
                          feature-key="EMAIL"
                          feature-label="Patient email"
                          source-page="Create Followup - Email Followup"
                        />
                        <FeatureRequestNote feature="EMAIL" />
                      </div>

                      <div v-else-if="emailFollowupDisabledReasons.some(r => !r.isFeatureIssue)" class="mt-2 p-2 bg-light rounded">
                        <small class="text-muted">
                          <i class="fa fa-lightbulb-o mr-1"></i>
                          <strong>Tip:</strong> Update patient email address or check follow-up type restrictions.
                        </small>
                      </div>
                    </div>
                  </b-popover>
                </button>

                <!-- Collect Payment -->
                <button
                  v-if="shouldShowCollectPayment"
                  type="button"
                  class="followup-action-btn"
                  :class="{ 'followup-action-btn-disabled': !shouldEnableCollectPayment }"
                  @click="showPaymentAttempt"
                  :disabled="!shouldEnableCollectPayment"
                >
                  <i class="fa fa-dollar"></i>
                  <span>Collect Payment</span>
                  <i v-if="!shouldEnableCollectPayment" class="fa fa-question-circle btn-help-icon" id="help_collect_payment"></i>
                  <b-popover target="help_collect_payment" triggers="hover" placement="auto" :show.sync="showCollectPaymentPopOver" >
                    <template v-slot:title>Why is "Collect Payment" button disabled for me?</template>
                    <div class="help_popover">
                      <p>Collect Payment is disabled because:</p>
                      <div class="d-flex" v-for="(reason, idx) in collectPaymentDisabledReasons" :key="idx">
                        <i class="glyphicon glyphicon-chevron-right text-muted mr-3"></i>
                        <p class="text-muted small">{{ reason }}</p>
                      </div>
                    </div>
                  </b-popover>
                </button>
                <!-- AI Call (AutoRecall Simulation) -->
                <button
                  v-if="canShowAICall"
                  type="button"
                  class="followup-action-btn followup-action-btn-ai"
                  :class="{ 'followup-action-btn-disabled': !canAICall }"
                  @click="onAICall"
                  :disabled="!canAICall || aiCallInProgress"
                >
                  <i v-if="aiCallInProgress" class="fa fa-spinner fa-spin"></i>
                  <i v-else class="fa fa-robot"></i>
                  <span>AI Call <span class="badge badge-info" style="font-size: 0.65em; vertical-align: middle;">SIM</span></span>
                </button>
              </div><!-- end action-buttons-row -->

              <!-- Alert Messages -->
              <div class="followup-alerts">
                <div class="alert-card alert-card-danger" v-if="isOverdueRecallPage && patientHasRecallScheduled">
                  <i class="fa fa-exclamation-circle alert-icon"></i>
                  <div class="alert-content">
                    <strong>Recall Appointment Scheduled</strong>
                    <p>Follow-up attempt is not allowed. The patient will disappear from the list when refreshed.</p>
                  </div>
                </div>

                <div class="alert-card alert-card-danger" v-else-if="!isResponsiblePersonMakingAttempt && !collectPaymentFormVisible">
                  <i class="fa fa-user-times alert-icon"></i>
                  <div class="alert-content">
                    <strong>Not Authorized</strong>
                    <p>You are not the assignee for these follow-ups. Please update the settings or contact your administrator.</p>
                  </div>
                </div>

                <div class="alert-card alert-card-danger" v-else-if="doNotFollowup">
                  <i class="fa fa-ban alert-icon"></i>
                  <div class="alert-content">
                    <strong>Do Not Follow-up</strong>
                    <p class="mb-2">You cannot follow up because one or more of the following is true:</p>
                    <ul class="alert-list" v-if="!(isOutstandingClaimsPage || isOutstandingPreAuthPage || isPatientBalancesPage)">
                      <li>The patient has been marked to not follow-up</li>
                      <li>The patient may have an active appointment scheduled</li>
                      <li>The Administrator has decided to stop further follow-ups</li>
                    </ul>
                    <ul class="alert-list" v-if="isOutstandingClaimsPage || isOutstandingPreAuthPage">
                      <li>This claim has been marked with a Processed Status</li>
                      <li>The Administrator has decided to stop further follow-ups</li>
                    </ul>
                    <ul class="alert-list" v-if="isPatientBalancesPage">
                      <li>This Patient Balance Follow-up has been marked as Do Not Follow-up</li>
                    </ul>
                  </div>
                </div>

                <div class="alert-card alert-card-success" v-else-if="maxAttemptsReached">
                  <i class="fa fa-check-circle alert-icon"></i>
                  <div class="alert-content">
                    <strong>All Done!</strong>
                    <p>You have completed the maximum number of follow-ups allowed.</p>
                  </div>
                </div>

                <div class="alert-card alert-card-danger" v-else-if="!attemptsAllowed">
                  <i class="fa fa-clock-o alert-icon"></i>
                  <div class="alert-content">
                    <strong>Already Followed Up</strong>
                    <p>You have already made a follow-up today on this call.</p>
                  </div>
                </div>

                <div class="early-followup-wizard" v-else-if="!followupAllowedToday || earlyFollowupWizardStep === 2">
                  <!-- Wizard Header -->
                  <div class="wizard-header">
                    <i class="fa fa-calendar-times-o wizard-icon"></i>
                    <div class="wizard-title">
                      <strong>Follow-up Date Adjustment Required</strong>
                      <p>The follow-up is scheduled for <strong>{{ tasksData && tasksData.length > 0 ? formatDateForDisplay(tasksData[0].FollowupDate) : 'a future date' }}</strong></p>
                    </div>
                  </div>

                  <!-- Step Indicators -->
                  <div class="wizard-steps">
                    <div class="wizard-step" :class="{ 'active': earlyFollowupWizardStep === 1, 'completed': earlyFollowupWizardStep === 2 }">
                      <div class="step-number">
                        <i v-if="earlyFollowupWizardStep === 2" class="fa fa-check"></i>
                        <span v-else>1</span>
                      </div>
                      <div class="step-label">Change Date</div>
                    </div>
                    <div class="wizard-step-connector" :class="{ 'active': earlyFollowupWizardStep === 2 }"></div>
                    <div class="wizard-step" :class="{ 'active': earlyFollowupWizardStep === 2 }">
                      <div class="step-number">2</div>
                      <div class="step-label">Follow Up</div>
                    </div>
                  </div>

                  <!-- Step 1: Change Date -->
                  <div class="wizard-content" v-if="earlyFollowupWizardStep === 1">
                    <div class="wizard-step-card">
                      <h4><i class="fa fa-calendar"></i> Step 1: Change the Follow-up Date</h4>
                      <p>Select a new date (today or earlier) to proceed with the follow-up.</p>

                      <div class="wizard-date-options">
                        <button type="button" class="wizard-date-btn wizard-date-btn-primary" @click="setDateToTodayAndProceed" :disabled="earlyFollowupLoading">
                          <i class="fa fa-calendar-check-o"></i>
                          <span>Set to Today</span>
                          <small>{{ formatDateForDisplay(new Date()) }}</small>
                        </button>

                        <div class="wizard-date-divider">
                          <span>or choose a date</span>
                        </div>

                        <div class="wizard-date-picker">
                          <DatePicker
                            v-model="earlyFollowupDate"
                            :not-before="null"
                            lang="en"
                            :format="dateFormatStr"
                            placeholder="Select a different date"
                            input-class="form-control wizard-date-input"
                            @change="onWizardDateChange"
                            :disabled="earlyFollowupLoading"
                            append-to-body>
                            <i class="fa fa-calendar" slot="calendar-icon" />
                          </DatePicker>
                        </div>
                      </div>

                      <div class="wizard-loading" v-if="earlyFollowupLoading">
                        <i class="fa fa-spinner fa-spin"></i> Updating follow-up date...
                      </div>
                    </div>
                  </div>

                  <!-- Step 2: Choose Follow-up Action -->
                  <div class="wizard-content" v-if="earlyFollowupWizardStep === 2">
                    <div class="wizard-step-card wizard-step-card-success">
                      <h4><i class="fa fa-check-circle"></i> Step 2: Complete the Follow-up</h4>
                      <p>Date updated to <strong>{{ formatDateForDisplay(tasksData[0].FollowupDate) }}</strong>. Now choose how you'd like to follow up:</p>

                      <div class="wizard-action-buttons">
                        <button type="button" class="wizard-action-btn" @click="completeWizardWithAction('phone')">
                          <i class="fa fa-phone"></i>
                          <span>Phone Call</span>
                        </button>
                        <button type="button" class="wizard-action-btn" @click="completeWizardWithAction('sms')" :disabled="!shouldEnableSMSFollowup" :class="{ 'wizard-action-btn-disabled': !shouldEnableSMSFollowup }">
                          <i class="fa fa-comment"></i>
                          <span>Send SMS</span>
                          <small v-if="!shouldEnableSMSFollowup" class="wizard-btn-hint">No mobile number</small>
                        </button>
                        <button type="button" class="wizard-action-btn" @click="completeWizardWithAction('email')" :disabled="!shouldEnableEmailFollowup" :class="{ 'wizard-action-btn-disabled': !shouldEnableEmailFollowup }">
                          <i class="fa fa-envelope"></i>
                          <span>Send Email</span>
                          <small v-if="!shouldEnableEmailFollowup" class="wizard-btn-hint">No email address</small>
                        </button>
                      </div>

                      <div class="wizard-back-link">
                        <a href="#" @click.prevent="earlyFollowupWizardStep = 1"><i class="fa fa-arrow-left"></i> Back to Step 1</a>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="alert-card alert-card-warning" v-else-if="isLastAttempt">
                  <i class="fa fa-exclamation-triangle alert-icon"></i>
                  <div class="alert-content">
                    <strong>Last Attempt</strong>
                    <p>This will be your last follow-up as allowed according to your settings.</p>
                  </div>
                </div>
              </div>
            </div>

                <!-- Phone Form -->
                <div class="followup-form-card" v-if="phoneAttemptFormVisible">
                  <div class="form-card-header">
                    <div class="form-card-icon form-card-icon-phone">
                      <i class="fa fa-phone"></i>
                    </div>
                    <div class="form-card-title">
                      <h5>Phone Follow-up</h5>
                      <span>Record the outcome of your phone call</span>
                    </div>
                  </div>

                  <div class="form-card-body">
                    <div class="form-field">
                      <label class="form-field-label">Follow-up Status</label>
                      <b-form-select class="form-field-select" v-model="statusID" v-if="statuses" ref="fstatus" @change="updateFollowupNote">
                        <option v-for="status in statuses" :key="status.FollowupStatusID" :value="status.FollowupStatusID">{{getStatus(status)}}</option>
                      </b-form-select>
                    </div>

                    <div class="form-field" v-if="followupDateVisible">
                      <label class="form-field-label">Next Follow-up Date</label>
                      <div class="date-field-wrapper">
                        <DatePicker
                          v-if="followupDateEditable || overridden"
                          @change="updateFollowupNote"
                          v-model="followupDate"
                          id="overrideDate"
                          :notBefore="notBefore"
                          lang="en"
                          :format="dateFormatStr"
                          placeholder="Select a Date"
                          input-class="form-control form-field-input"
                          append-to-body>
                          <i class="fa fa-calendar" slot="calendar-icon" />
                        </DatePicker>

                        <div class="date-display" v-if="!overridden && !followupDateEditable">
                          <i class="fa fa-calendar"></i>
                          <span>{{followupDate | dateformat(dateFormatStr)}}</span>
                        </div>

                        <a v-if="!overridden && (!(this.isOutstandingClaimsPage || this.isOutstandingPreAuthPage || this.isPatientBalancesPage))" class="override-link" href="#" @click="doOverride">Override</a>
                        <a v-if="overridden" class="override-link" href="#" @click="cancelOverride">Cancel</a>
                        <span v-if="!overridden && (!(this.isOutstandingClaimsPage || this.isOutstandingPreAuthPage || this.isPatientBalancesPage))" id="override_help" class="help-tooltip">
                          <i class="fa fa-question-circle"></i>
                          <b-tooltip target="override_help" placement="right" triggers="hover">
                            Use this option to override the date that was auto-selected for you according to your Office Settings. We don't recommend using this unless absolutely necessary.
                          </b-tooltip>
                        </span>
                      </div>
                    </div>

                    <div class="form-field">
                      <label class="form-field-label">Follow-up Note</label>
                      <textarea v-model="followupNote" rows="5" class="form-field-textarea" ref="fnote" placeholder="Add notes about this follow-up..."></textarea>
                      <p class="form-field-hint">This note will be sent to the communication log. You can edit it as needed.</p>
                      <label class="form-checkbox-label">
                        <input type="checkbox" v-model="copyToCommLog" disabled />
                        <span>Copy followup note to communication</span>
                      </label>
                    </div>
                  </div>

                  <div class="form-card-actions">
                    <button type="button" class="btn-form btn-form-success" :disabled="attemptInProgress" @click="save('phone')">
                      <i v-if="attemptInProgress" class="fa fa-spinner fa-spin"></i>
                      <i v-else class="fa fa-check"></i> Save Attempt
                    </button>
                    <button type="button" class="btn-form btn-form-cancel" :disabled="attemptInProgress" @click="closeSlideOutPanel">
                      Cancel
                    </button>
                  </div>
                </div>

                <!-- SMS Form -->
                <div class="followup-form-card" v-else-if="smsAttemptFormVisible">
                  <div class="form-card-header">
                    <div class="form-card-icon form-card-icon-sms">
                      <i class="fa fa-comment"></i>
                    </div>
                    <div class="form-card-title">
                      <h5>SMS Follow-up</h5>
                      <span>Send an SMS message to the patient</span>
                    </div>
                  </div>

                  <div class="form-info-banner">
                    <i class="fa fa-mobile"></i>
                    <span>{{ user && user.SiteName ? user.SiteName : 'Your practice' }}'s outgoing SMS number: <strong>{{ formattedSmsNumber }}</strong></span>
                  </div>

                  <div class="form-card-body">
                    <div class="form-field" v-if="followupDateVisible">
                      <label class="form-field-label">Next Follow-up Date</label>
                      <div class="date-field-wrapper">
                        <DatePicker
                          v-if="followupDateEditable || overridden"
                          @change="updateSMSFollowupNote"
                          v-model="followupDate"
                          id="overrideDateSms"
                          :notBefore="notBefore"
                          lang="en"
                          :format="dateFormatStr"
                          placeholder="Select a Date"
                          input-class="form-control form-field-input"
                          append-to-body>
                          <i class="fa fa-calendar" slot="calendar-icon" />
                        </DatePicker>

                        <div class="date-display" v-if="!overridden && !followupDateEditable">
                          <i class="fa fa-calendar"></i>
                          <span>{{followupDate | dateformat(dateFormatStr)}}</span>
                        </div>

                        <a v-if="!overridden && (!(this.isOutstandingClaimsPage || this.isOutstandingPreAuthPage || this.isPatientBalancesPage))" class="override-link" href="#" @click="doOverride">Override</a>
                        <a v-if="overridden" class="override-link" href="#" @click="cancelOverride">Cancel</a>
                      </div>
                    </div>

                    <div class="form-field">
                      <label class="form-field-label">Message Template</label>
                      <b-form-select class="form-field-select" v-model="selectedWikiPageID" v-if="SMSWikiPagesForFollowup" ref="wikipages" @change="updateSMSFollowupNote">
                        <option v-for="template in SMSWikiPagesForFollowup" :key="template.WikiPageID" :value="template.WikiPageID">{{template.Name}}</option>
                      </b-form-select>
                    </div>

                    <div class="form-field">
                      <label class="form-field-label">Follow-up Note</label>
                      <textarea v-model="followupNote" rows="5" class="form-field-textarea" ref="fnote" placeholder="Add notes about this follow-up..."></textarea>
                      <p class="form-field-hint">This note will be sent to the communication log.</p>
                      <label class="form-checkbox-label">
                        <input type="checkbox" v-model="copyToCommLog" disabled />
                        <span>Copy followup note to communication</span>
                      </label>

                      <div v-if="smsPreviewNote" class="sms-short-url-note">
                        <strong>Note:</strong> The actual text message sent to the patient will contain a shortened link:<br/>
                        <span class="sms-preview-text">"{{ smsPreviewNote }}"</span>
                      </div>

                      <!-- Shared SMS guidance: live credit counter + warn/block banner + tips (template-aware) -->
                      <SmsGuidance :text="followupNote || ''" substitute-tags @change="onSmsGuidance" />
                    </div>
                  </div>

                  <div class="form-card-actions">
                    <button type="button" class="btn-form btn-form-success" :disabled="attemptInProgress || smsBlocked" @click="save('sms')">
                      <i v-if="attemptInProgress" class="fa fa-spinner fa-spin"></i>
                      <i v-else class="fa fa-paper-plane"></i> Send SMS
                    </button>
                    <button type="button" class="btn-form btn-form-cancel" :disabled="attemptInProgress" @click="closeSlideOutPanel">
                      Cancel
                    </button>
                  </div>
                </div>

                <!-- Email Form -->
                <div class="followup-form-card" v-else-if="emailAttemptFormVisible">
                  <div class="form-card-header">
                    <div class="form-card-icon form-card-icon-email">
                      <i class="fa fa-envelope"></i>
                    </div>
                    <div class="form-card-title">
                      <h5>Email Follow-up</h5>
                      <span>Send an email to the patient</span>
                    </div>
                  </div>

                  <div class="form-card-body">
                    <div class="form-field" v-if="followupDateVisible">
                      <label class="form-field-label">Next Follow-up Date</label>
                      <div class="date-field-wrapper">
                        <DatePicker
                          v-if="followupDateEditable || overridden"
                          @change="updateEmailFollowupNote"
                          v-model="followupDate"
                          id="overrideDateEmail"
                          :notBefore="notBefore"
                          lang="en"
                          :format="dateFormatStr"
                          placeholder="Select a Date"
                          input-class="form-control form-field-input"
                          append-to-body>
                          <i class="fa fa-calendar" slot="calendar-icon" />
                        </DatePicker>

                        <div class="date-display" v-if="!overridden && !followupDateEditable">
                          <i class="fa fa-calendar"></i>
                          <span>{{followupDate | dateformat(dateFormatStr)}}</span>
                        </div>

                        <a v-if="!overridden && (!(this.isOutstandingClaimsPage || this.isOutstandingPreAuthPage || this.isPatientBalancesPage))" class="override-link" href="#" @click="doOverride">Override</a>
                        <a v-if="overridden" class="override-link" href="#" @click="cancelOverride">Cancel</a>
                      </div>
                    </div>

                    <div class="form-field">
                      <label class="form-field-label">Email Template</label>
                      <b-form-select v-if="EmailWikiPagesForFollowup.length > 0" class="form-field-select" v-model="selectedWikiPageID" ref="emailwikipages" @change="updateEmailFollowupNote">
                        <option v-for="template in EmailWikiPagesForFollowup" :key="template.WikiPageID" :value="template.WikiPageID">{{template.Name}}</option>
                      </b-form-select>
                      <div class="form-field-warning" v-else>
                        <i class="fa fa-exclamation-triangle"></i>
                        <span>No email templates found. <a href="/app/wiki">Create one</a> first.</span>
                      </div>
                    </div>
                  </div>

                  <div class="form-card-actions">
                    <button type="button" class="btn-form btn-form-success" :disabled="attemptInProgress" @click="save('email')">
                      <i v-if="attemptInProgress" class="fa fa-spinner fa-spin"></i>
                      <i v-else class="fa fa-paper-plane"></i> Send Email
                    </button>
                    <button type="button" class="btn-form btn-form-cancel" :disabled="attemptInProgress" @click="closeSlideOutPanel">
                      Cancel
                    </button>
                  </div>
                </div>

                <!-- *********************************************************Collect Payment Form *************************************************************************** -->

                <b-form class="form-label-left mt-5" v-else-if="collectPaymentFormVisible">
                  <!-- SMS Sender Info Banner -->
                  <div class="sms-sender-info mb-3">
                    <div class="d-flex align-items-center">
                      <i class="fa fa-mobile-phone text-primary mr-2" style="font-size: 1.2rem;"></i>
                      <span class="text-muted small">SMS will be sent from: </span>
                      <span class="badge badge-light ml-2" style="font-size: 0.9rem; font-weight: 600; color: #495057; background-color: #e9ecef; padding: 0.4em 0.8em;">
                        {{this.allSettings.GENERAL_PHONE.PropertyValue}}
                      </span>
                    </div>
                  </div>

                  <b-form-group
                    horizontal
                    label="Pick a template:"
                    label-for="pick-a-template"
                    :label-cols="4"
                  >
                    <b-row>
                      <b-col md='8'>
                        <b-form-select size="sm" v-model="selectedWikiPageID" v-if="paymentWikiPages" ref="wikipages" @change="updateSMSFollowupNote" >
                          <option v-for="template in paymentWikiPages" :key="template.WikiPageID" :value="template.WikiPageID">{{template.Name}}</option>
                        </b-form-select> 
                        <p class="mt-2 text-muted small" v-if="Object.keys(paymentWikiPages).length === 0">You do not have any wiki templates tagged with "payment". Please create one first, tag it and try again</p>
                      </b-col>
                    </b-row>
                  </b-form-group>

                  <b-form-group
                    horizontal
                    label="SMS Text For Payment Collection:"
                    label-for="follow-up-note"
                    :label-cols="4"
                  >
                    <b-row>
                      <b-col md="12">
                        <textarea v-model="followupNote" rows="6" max-rows="6" cols="10" name placeholder="Try to add few new lines..." class="form-control" ref="fnote"></textarea>
                        <p class="mt-2 text-muted small">Above is a text that will be sent to the communication log. You can edit it as you like. Any variables such as "payment_link" will be replaced dynamically when the message goes out</p>
                        <!-- Shared SMS guidance: live credit counter + warn/block banner + tips (template-aware: {{payment_link}} etc.) -->
                        <SmsGuidance :text="followupNote || ''" substitute-tags @change="onSmsGuidance" />
                        <b-form-group class="abc-checkbox">
                            <b-form-checkbox
                            v-model="copyToCommLog"
                            unchecked-value="FALSE"
                            disabled
                            >
                            <span class="fw-semi-bold">Copy followup note to communication</span>
                            </b-form-checkbox>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </b-form-group>

                  <b-row align-h="center">
                    <b-col>
                      <center><button :disabled="attemptInProgress || smsBlocked" type="button" class="btn mb-xs mr-xs btn-success" @click="collectPayment">Send Payment SMS</button> <button type="button" class="ml-2 btn mb-xs mr-xs btn-success" @click="closeSlideOutPanel">Cancel</button></center>
                    </b-col>                    
                  </b-row> 

                </b-form>

                <!-- Cancel button hidden - shows only when a followup form is displayed -->
        </section>
        <!-- Follow-up Activity Panel starts here -->
        <section v-if="this.followupActivities && this.followupActivities.length > 0">
          <Widget>
                <div role="button" class="accordion-toggle cursor-pointer" @click="toggleBlock(0)">
                    <h6 class="fw-semi-bold"><a role="button" class="">Follow-up Activity</a></h6>
                    <i :class="`ml-auto fa fa-angle-down ${this.isShown[0] ? 'expanded' : ''}`"></i>
                </div>
                <b-collapse id="accordion-assets" :visible="this.isShown[0]">
                    <ul class="post-comments mt-sm ml-3 mr-3">
                      <li v-for="(activity,index) in this.followupActivities" :key="index">
                        <span class="thumb-xs avatar pull-left mr-sm">
                          <img class="rounded-circle" src="../../../assets/avatar.png" alt="..." />
                        </span>
                        <div class="comment-body">
                          <div class="d-flex justify-content-between align-items-start">
                            <div class="flex-grow-1">
                              <h6 class="author fs-sm fw-semi-bold mb-2">
                                {{getEmployeeName(activity.CreatedByEmpID)}} <small>on {{activity.CreatedAt | dateformat(dateFormatStr) }}</small>
                              </h6>
                              <p class="mb-0">{{activity.FollowupNote}}</p>
                            </div>
                            <b-dropdown v-if="$can('edit', 'followupNote')" :ref="`${index}-edit-dropdown`" size="sm" variant="link" toggle-class="text-decoration-none p-1" boundary="viewport" @show="beforeShowEditNote(activity)" no-caret>
                              <template #button-content>
                                <i class="fa fa-edit text-success" style="font-size: 14px;"/>
                              </template>
                              <b-dropdown-form>
                                <b-form-group>
                                  <textarea v-model="activity.FollowupNote" rows="10" max-rows="10" cols="20" name class="form-control form-control-sm"></textarea>
                                </b-form-group>                        
                                <b-button :disabled="!readyToSaveFollowupNote(activity)" variant="primary" size="sm" @click="_editFollowupNote(activity, index)">Save</b-button>
                                <b-button variant="primary" size="sm" @click="closeEditDropdown(index)" class="ml-2">Cancel</b-button>
                              </b-dropdown-form>
                            </b-dropdown>
                          </div>                   
                        </div>
                      </li>
                    </ul>            
              </b-collapse>
          </Widget>
        </section>
        <!-- Follow-up Activity Panel ends here -->

        <!--  Auto Recall reminder Activity Panel starts here -->
        <section v-if="apptReminderPaidFor && autoRecallReminderActivity">
          <Widget>
                <div role="button" class="accordion-toggle cursor-pointer" @click="toggleBlock(1)">
                    <h6 class="fw-semi-bold"><a role="button" class="">Auto Recall Reminder Activity</a></h6>
                    <i :class="`ml-auto fa fa-angle-down ${this.isShown[1] ? 'expanded' : ''}`"></i>
                </div>
                <b-collapse id="accordion-assets" :visible="this.isShown[1]">
                    <div v-if="apptReminderPaidFor">
                      <div class="recall-activity-timeline ml-3 mr-3 mt-sm" v-if="autoRecallReminderActivity && autoRecallReminderActivity.length > 0">
                        <div
                          class="recall-timeline-item"
                          v-for="activity in autoRecallReminderActivity"
                          :key="activity.RecallReminderID"
                        >
                          <div class="recall-timeline-icon" :class="getRecallIconClass(activity.ReminderType)">
                            <i :class="getRecallActivityIcon(activity.ReminderType)"></i>
                          </div>
                          <div class="recall-timeline-content">
                            <div class="recall-timeline-header">
                              <span class="recall-activity-type">{{ getRecallActivityLabel(activity.ReminderType) }}</span>
                              <span class="recall-attempt-badge" v-if="activity.AttemptsSoFar">
                                Attempt {{ activity.AttemptsSoFar }}
                              </span>
                            </div>
                            <div class="recall-timeline-details">
                              <i class="fa fa-paper-plane"></i>
                              <span>Sent: {{ getRecallSentDate(activity) | dateformat(dateFormatStr) }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p v-else class="text-muted ml-3 mr-3 mt-sm">No auto recall reminders have been sent yet.</p>
                      <div class="ml-3 mr-3 mt-3">
                        <h6 class="fs-sm fw-semi-bold"><span style="color: #7CA9DD;">Next scheduled reminder: </span> <span class="fw-normal">{{ nextAutoRecallReminderDue | dateformat(dateFormatStr)}}</span></h6>
                      </div>
                    </div>
                    <div v-else>
                      <span class="featureNotEnabled" v-html="featureNotEnabledMsg"></span>
                    </div>             
              </b-collapse>
          </Widget>
        </section>
        <!-- Auto Recall reminder Activity Panel ends here -->

        <!-- TABS starts here -->
        <div class="followup-tabs-container">
          <b-tabs v-model="tabIndex" class="followup-tabs" nav-class="followup-tabs-nav" content-class="followup-tabs-content">
                            <!-- Claim Information -->
                            <b-tab title="Claim Information" v-if="isOutstandingClaimsPage">
                              <div class="table-resposive">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th>Provider</th>
                                      <th>Claim Sent </th>
                                      <th>Date of Service</th>
                                      <th>Patient DOB</th>
                                      <th>Patient Zip Code</th>
                                      <th>Procedures Attached</th>
                                      <th>Insurance Estimate</th>
                                       <th>Type</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td>{{this.claimData.ProviderName}}</td>
                                      <td>{{this.claimData.DateSent | dateformat(dateFormatStr)}}</td>
                                      <td>{{this.claimData.DateService | dateformat(dateFormatStr)}}</td>
                                      <td>{{this.claimData.Birthdate | dateformat(dateFormatStr)}}</td>
                                      <td>{{this.claimData.Zip}}</td>
                                      <td>{{this.claimData.ProceduresAttached}}</td>
                                      <td>{{this.claimData.InsPayEst | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</td>
                                      <td>{{this.claimData.ClaimType}}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              <h6 class="fw-semi-bold mt-4 ml-2">Subscriber Information:</h6>
                              <div class="table-resposive">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th>Subscriber ID</th>
                                      <th>Subscriber</th>
                                      <th>Subscriber DOB</th>
                                      <th>Carrier</th>
                                       <th>Carrier Phone</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td>{{formatSubscriberID(this.claimData.SubScriberID)}}</td>
                                      <td>{{formatName(this.claimData.SubscriberName)}}</td>
                                      <td>{{formatBirthDate(this.claimData.SubscriberBirthDate)}}</td>
                                      <td>{{this.claimData.CarrierName}}</td>
                                      <td>{{this.claimData.CarrierPhone}}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </b-tab>
                            <!-- beginning of Claim Status History tab -->
                            <b-tab title="Claim Status History" v-if="isOutstandingClaimsPage">
                              <div class="table-resposive">
                                <v-client-table v-if="existingClaimStatusHistory && existingClaimStatusHistory.length > 0 && allSettings" :data="existingClaimStatusHistory" :columns="claimStatusHistoryColumns" :options="claimStatusHistoryOptions">
                                  <div slot="h__DateTimeEntry">Date</div>
                                  <div slot="h__Description">Description</div>
                                  <div slot="h__LogNote">Log Note</div>
                                  <div slot="h__UserName">User</div>
                                  <div slot="DateTimeEntry" slot-scope="props">{{formatCommLogDateTime(props.row.DateTimeEntry)}}</div>
                                  <div slot="Description" slot-scope="props">
                                    <span class="badge text-black badge-gray">{{props.row.Description}}</span>
                                  </div>
                                  <div slot="LogNote" slot-scope="props">
                                    <truncate 
                                      collapsed-text-class="collapsed" 
                                      clamp="More" 
                                      :length="90" 
                                      less="Show Less" 
                                      type="html" 
                                      :text="props.row.LogNote">
                                    </truncate>
                                  </div>
                                  <div slot="UserName" slot-scope="props">
                                    <small>{{props.row.UserName}}</small>                                    
                                    <br />
                                  </div>
                                </v-client-table>
                                <span v-else>No existing Claim Status History found!</span>
                              </div>
                            </b-tab>
                            <!-- end of Claim status history tab -->
                            <b-tab title="Communication Log">
                              <div class="table-resposive">
                                <v-client-table v-if="commlog && commlog.length > 0 && allSettings" :data="commlog" :columns="columns" :options="options">
                                  <div slot="h__CommDateTime">DATE & TIME</div>
                                  <div slot="h__Note">NOTE</div>
                                  <div slot="h__Mode_">TYPE</div>
                                  <div slot="h__UserName">USER</div>
                                  <div slot="CommDateTime" slot-scope="props">{{formatCommLogDateTime(props.row.CommDateTime)}}</div>
                                  <div slot="Note" slot-scope="props">
                                    <truncate 
                                      collapsed-text-class="collapsed" 
                                      clamp="More" 
                                      :length="90" 
                                      less="Show Less" 
                                      type="html" 
                                      :text="props.row.Note">
                                    </truncate>
                                  </div>
                                  <div slot="Mode_" slot-scope="props">
                                    <span v-if="props.row.Mode_ === 5" class="comm-type-tag"><i class="fa fa-comment"></i> Text</span>
                                    <span v-if="props.row.Mode_ === 3" class="comm-type-tag"><i class="fa fa-phone"></i> Phone</span>
                                    <span v-if="props.row.Mode_ === 1" class="comm-type-tag"><i class="fa fa-envelope"></i> Email</span>
                                    <span v-if="props.row.Mode_ === 2" class="comm-type-tag"><i class="fa fa-envelope"></i> Email</span>
                                    <span v-if="props.row.Mode_ === 4" class="comm-type-tag"><i class="fa fa-envelope"></i> Email</span>
                                    <span v-if="props.row.Mode_ === 6" class="comm-type-tag"><i class="fa fa-envelope"></i> Email</span>
                                  </div>
                                  <div slot="UserName" slot-scope="props">
                                    <small>{{props.row.UserName}}</small>                                    
                                    <br />
                                  </div>
                                </v-client-table>
                                <span v-else>No Comm Log entries found!</span>
                              </div>
                            </b-tab>
                            
                            <!-- Scheduled Appointments for Recall Panel -->
                            <b-tab v-if="isOverdueRecallPage">                              
                              <template v-slot:title>
                                Scheduled Appointments  <span :class="addScheduledAppointmentClass">{{scheduledAppointmentsTabTitle}}</span>
                              </template>
                              <span class="empty-state-message" v-if="!scheduledAppointmentsForRecallPanel || (scheduledAppointmentsForRecallPanel && scheduledAppointmentsForRecallPanel.length === 0)"><i class="fa fa-info-circle"></i> No Scheduled appointments</span>
                              
                              <div class="table-resposive">
                                <table class="table" v-if="scheduledAppointmentsForRecallPanel && scheduledAppointmentsForRecallPanel.length > 0">
                                  <thead>
                                    <tr>
                                      <th>Procedure </th>
                                      <th>Provider</th>
                                      <th>Date</th>
                                      <th>Appointment Note</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(appt, index) in scheduledAppointmentsForRecallPanel" :key="index">
                                      <td>{{appt.ProcDescript}}</td>
                                      <td><span>{{appt.Provider}}</span></td>
                                      <td>{{appt.AptDateTime | dateformat(dateFormatStr)}}</td>
                                      <td>{{appt.AppointmentNotes}}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </b-tab>
                            <!-- Pending Tx for Recall Panel -->
                            <b-tab title="Unscheduled Treatment" v-if="isOverdueRecallPage">
                              <div class="table-resposive">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th>Priority</th>
                                      <th>Procedure Code</th>
                                      <th>Tooth/Surf</th>
                                      <th>Description</th>
                                      <th>Tx Plan Date</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(tx,index) in this.pendingTxForRecallPanel" :key="index">
                                      <td>{{tx.Priority}}</td>
                                      <td>{{tx.ProcCode}}</td>
                                      <td v-if="getCountry === 'CANADA'">{{convertUniversalToFDI(tx.ToothNum)}} {{tx.Surf}}</td>
                                      <td v-else>{{tx.ToothNum}} {{tx.Surf}}</td>
                                      <td>{{tx.Descript}}</td>
                                      <td>{{tx.DateTP | dateformat(dateFormatStr)}}</td>
                                    </tr>
                                </tbody>
                                </table>
                              </div>  
                            </b-tab>

                            <!-- Beginnig of tagged tasks  -->
                            <b-tab title="Tasks" v-if="taggedTasks && taggedTasks.length > 0">
                            <template v-slot:title>
                              Tasks
                              <span class="tab-count-badge tab-count-badge-info">{{taggedTasks.length}}</span>
                            </template>
                            <div class="table-resposive">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th>Created</th>
                                      <th>Task</th>
                                      <th>Status</th>
                                      <th>Created By</th>
                                      <th>Assigned To</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(task) in taggedTasks" :key="`task-${task.id}`">
                                      <td>{{task.createdAt}}</td>
                                      <td><span v-html="task.content"/></td>
                                      <td>{{task.status}}</td>
                                      <td>{{customer(task.createdByEmpID)}}</td>
                                      <td>{{customer(task.assignedToEmpID)}}</td>
                                      <td><a :href="`/app/todo/${task.id}`" target="_blank">View</a></td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </b-tab>
                            <!-- End of tagged tasks -->
                            
                            <!-- for Patient Balances page - outstanding claims for family tab -->
                            <b-tab title="Outstanding Claims for Family" v-if="isPatientBalancesPage && outstandingClaimsForFamily">
                              <div class="table-resposive">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th>Patient</th>
                                      <th>Claim Sent</th>
                                      <th>Date of Service</th>
                                      <th>Procedures Attached</th>
                                      <th>Insurance Estimate</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(claim,index) in outstandingClaimsForFamily" :key="`claim-${index}`">
                                      <td>{{formatName(claim.Patient)}}</td>
                                      <td>{{claim.ClaimSent | dateformat(dateFormatStr)}}</td>
                                      <td>{{claim.DateService | dateformat(dateFormatStr)}}</td>
                                      <td>{{claim.ProceduresAttached}}</td>
                                      <td>{{claim.InsuranceEstimate | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </b-tab>
                            <!-- Outstanding PreAuth Tab starts here -->
                            <b-tab title="Outstanding PreAuthorization Info" v-if="isOutstandingPreAuthPage">
                              <div class="table-resposive">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th>Claim Sent </th>
                                      <th>Provider</th>
                                      <th>Procedures Attached</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td>{{this.claimData.DateSent | dateformat(dateFormatStr)}}</td>
                                      <td>{{this.claimData.Provider}}</td>
                                      <td>{{this.claimData.AttachedProcedures}}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              <h6 class="fw-semi-bold">Carrier and Subscriber Info</h6>
                              <div class="table-resposive">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th>Carrier</th>
                                      <th>Carrier Phone</th>
                                      <th>Subscriber</th>
                                      <th>Subscriber DOB</th>
                                      <th>Subscriber ID</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td>{{this.claimData.Carrier}}</td>
                                      <td>{{this.claimData.Phone}}</td>
                                      <td>{{formatName(this.claimData.SubscriberName)}}</td>
                                      <td>{{formatBirthDate(this.claimData.SubscriberBirthDate)}}</td>
                                      <td>{{formatSubscriberID(this.claimData.SubScriberID)}}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </b-tab>
                             <!-- Outstanding PreAuth Tab ends here -->

                            <!-- RECEIVED/Processed PreAuth Tab starts here -->
                            <b-tab title="PreAuthorization Info" v-if="isProcessedPreAuthPage">
                              <div class="table-resposive">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th>Provider </th>
                                      <th>Procedure Code</th>
                                      <th>Tooth</th>
                                      <th>Description</th>
                                      <th>Insurance Estimate</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(proc,index) in this.patientProcedures" :key="`proc-${index}`">
                                      <td>{{proc.Provider}}</td>
                                      <td>{{proc.ProcedureCode}}</td>
                                      <td>{{proc.Tooth}}</td>
                                      <td>{{proc.Description}}</td>
                                      <td>{{proc.InsuranceEstimate | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </b-tab>
                            <!-- RECEIVED/Processed PreAuth Tab ends here -->
                            
                            <!-- Broken Appointment History Tab starts here -->
                            <b-tab title="Broken Appointment History" v-if="isBrokenAppointmentsPage">
                              <div class="table-resposive">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th>Date</th>
                                      <th>Procedure Code(S)/Adjustment(S)</th>
                                      <th>Fee</th>
                                      <th>Provider</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(appt,index) in this.brokenAppointmentsHistory" :key="index">
                                      <td>{{appt.Date | dateformat(dateFormatStr)}}</td>
                                      <td>{{appt.AdjustmentProcedureCode}}<br>
                                      {{appt.BrokenAppointmentProcDescript}}
                                      </td>
                                      <td>{{appt.Fee | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</td>
                                      <td>{{appt.Provider}}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </b-tab>
                            <!-- Broken Appointment History Tab ends here -->

                            <b-tab title="Unscheduled Treatment" v-if="isUnscheduledTreatmentPage">
                              <div class="table-resposive">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th>Priority</th>
                                      <th>Procedure Code</th>
                                      <th>Tooth/Surf</th>
                                      <th>Description</th>
                                      <th>Tx Plan Date</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(tx,index) in this.unscheduledTreatmentInPlan" :key="index">
                                      <td>{{tx.Priority}}</td>
                                      <td>{{tx.ProcCode}}</td>
                                      <td v-if="getCountry === 'CANADA'">{{convertUniversalToFDI(tx.ToothNum)}} {{tx.Surf}}</td>
                                      <td v-else>{{tx.ToothNum}} {{tx.Surf}}</td>
                                      <td>{{tx.Descript}}</td>
                                      <td>{{tx.DateTP | dateformat(dateFormatStr)}}</td>
                                    </tr>
                                </tbody>
                                </table>
                              </div>  
                            </b-tab>
                            <b-tab  v-if="isUnscheduledTreatmentPage">
                              <template v-slot:title>
                                <!--Planned Appointments  <span :class="addPlannedAppointmentClass">({{plannedAppointmentsTabTitle}})</span>-->
                                Planned Appointments
                                <span :class="addPlannedAppointmentClass">{{plannedAppointmentsTabTitle}}</span>
                              </template>

                              <span class="empty-state-message" v-if="!plannedAppointments || (plannedAppointments && plannedAppointments.length === 0)"><i class="fa fa-info-circle"></i> No Planned appointments</span>
                              <div class="table-resposive">
                                <table class="table" v-if="plannedAppointments && plannedAppointments.length > 0">
                                  <thead>
                                    <tr>
                                      <th>Procedure(s)</th>
                                      <th>Note</th>
                                      <th>Provider</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(appt, index) in plannedAppointments" :key="index">
                                      <td>{{appt.ProcDescript}}</td>
                                      <td>{{appt.Note}}</td>
                                      <td>{{appt.Provider}}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </b-tab>
                            <b-tab v-if="isUnscheduledTreatmentPage">                              
                              <template v-slot:title>
                                Scheduled Appointments  <span :class="addScheduledAppointmentClass">{{scheduledAppointmentsTabTitle}}</span>
                              </template>
                              <span class="empty-state-message" v-if="!scheduledAppointments || (scheduledAppointments && scheduledAppointments.length === 0)"><i class="fa fa-info-circle"></i> No Scheduled appointments</span>
                              
                              <div class="table-resposive">
                                <table class="table" v-if="scheduledAppointments && scheduledAppointments.length > 0">
                                  <thead>
                                    <tr>
                                      <th>Procedure </th>
                                      <th>Provider</th>
                                      <th>Date</th>
                                      <th>Appointment Note</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(appt, index) in scheduledAppointments" :key="index">
                                      <td>{{appt.ProcDescript}}</td>
                                      <td><span>{{appt.Provider}}</span></td>
                                      <td>{{appt.AptDateTime | dateformat(dateFormatStr)}}</td>
                                      <td>{{appt.AppointmentNotes}}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </b-tab>
                            <b-tab title="Past Appointments" v-if="isOverdueRecallPage && pastAppointments && pastAppointments.length > 0" >
                              <div class="table-resposive">
                                <table class="table table-three-cols">
                                  <thead>
                                    <tr>
                                      <th>Procedure</th>
                                      <th>Date</th>
                                      <th>Provider</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(appt, index) in pastAppointments" :key="index">
                                      <td>{{appt.ProcDescript}}</td>
                                      <td>{{appt.AppointmentDate | dateformat(dateFormatStr)}}</td>
                                      <td>{{appt.Provider}}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </b-tab>
                            <b-tab title="Family Recall Appointments" v-if="isOverdueRecallPage">
                              <div v-if="familyApptsWithoutCurrentPerson == null">
                                <p class="ml-4 fw-semi-bold">No Family Recall appointments found.</p>
                              </div>
                              <div v-else-if="familyApptsWithoutCurrentPerson && familyApptsWithoutCurrentPerson.length > 0" class="table-resposive">
                                  <table class="table">
                                    <thead>
                                      <tr>
                                        <th>Name</th>
                                        <th>Age</th>
                                        <th>Recall Scheduled</th>
                                        <th>Recall Due</th>
                                        <th>Last Called</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="(appt,index) in this.familyApptsWithoutCurrentPerson" :key="`appt-${index}`">
                                        <td><a href="#" @click="gotoPatientPage(appt.PatNum)">{{formatName(appt.LName + ', ' +  appt.FName)}}</a></td>
                                        <td>{{appt.Age}}</td>
                                        <td v-if="appt.AptDateTime">{{appt.AptDateTime | dateformat(dateFormatStr)}}</td>
                                        <td v-else><span class="status-tag status-tag-pending"><i class="fa fa-clock-o"></i> Not Scheduled</span></td>
                                        <td>{{appt.DateDue | dateformat(dateFormatStr)}}</td>
                                        <td>{{appt.LastCalled | dateformat(dateFormatStr)}}</td>
                                        <td>
                                          <span class="status-tag status-tag-default">{{appt.Status}}</span>
                                        </td>
                                      </tr> 
                                    </tbody>
                                  </table>
                                </div>   
                            </b-tab>
                            <!-- family recall appointmetns for unscheduled tx follow-up panel -->

                            <b-tab title="Family Recall Appointments" v-if="isUnscheduledTreatmentPage">
                              <div v-if="familyRecallAppointmentsForUnscheduledPanel == null">
                                <p class="ml-4 fw-semi-bold">No Family Recall appointments found.</p>
                              </div>
                              <div v-else-if="familyRecallAppointmentsForUnscheduledPanel && familyRecallAppointmentsForUnscheduledPanel.length > 0" class="table-resposive">
                                  <table class="table">
                                    <thead>
                                      <tr>
                                        <th>Name</th>
                                        <th>Age</th>
                                        <th>Recall Scheduled</th>
                                        <th>Recall Due</th>
                                        <th>Last Called</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="(appt,index) in this.familyRecallAppointmentsForUnscheduledPanel" :key="`appt-${index}`">
                                        <td><a href="#" @click="gotoPatientPage(appt.PatNum)">{{formatName(appt.LName + ', ' +  appt.FName)}}</a></td>
                                        <td>{{appt.Age}}</td>
                                        <td v-if="appt.AptDateTime">{{appt.AptDateTime | dateformat(dateFormatStr)}}</td>
                                        <td v-else><span class="status-tag status-tag-pending"><i class="fa fa-clock-o"></i> Not Scheduled</span></td>
                                        <td>{{appt.DateDue | dateformat(dateFormatStr)}}</td>
                                        <td>{{appt.LastCalled | dateformat(dateFormatStr)}}</td>
                                        <td>
                                          <span class="status-tag status-tag-default">{{appt.Status}}</span>
                                        </td>
                                      </tr> 
                                    </tbody>
                                  </table>
                                </div>   
                            </b-tab>

                            <!-- Family pending tx for Recall follow-up panel -->
                            <b-tab title="Family Pending Tx" v-if="isOverdueRecallPage">
                              <div v-if="unscheduledTxForFamilyForRecallPanel == null">
                                <p class="ml-4 fw-semi-bold">No Pending Treatment found for any family members.</p>
                              </div>
                              <div v-else-if="unscheduledTxForFamilyForRecallPanel && unscheduledTxForFamilyForRecallPanel.length > 0" class="table-resposive">
                                  <table class="table">
                                    <thead>
                                      <tr>
                                        <th>Name</th>
                                        <th>Priority</th>
                                        <th>Procedure Code</th>
                                        <th>Tooth/Surf</th>
                                        <th>Description</th>
                                        <th>Tx Plan Date</th>
                                        
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="(appt,index) in unscheduledTxForFamilyForRecallPanel" :key="index">
                                        <td><a href="#" @click="gotoPatientPage(appt.PatNum)">{{formatName(appt.LName + ', ' +  appt.FName)}}</a></td>
                                        <td>{{appt.Priority}}</td>
                                        <td>{{appt.ProcCode}}</td>
                                        <td>{{appt.ToothNum}} {{appt.Surf}}</td>
                                        <td>{{appt.Descript}}</td>
                                        <td>{{appt.DateTP | dateformat(dateFormatStr)}}</td>
                                      </tr> 
                                    </tbody>
                                  </table>
                                </div>   
                            </b-tab>

                            <!-- family pending tx for unscheduled panel -->
                            <b-tab title="Family Pending Tx" v-if="isUnscheduledTreatmentPage">
                              <div v-if="unscheduledTxForFamilyForUnscheduledPanel == null">
                                <p class="ml-4 fw-semi-bold">No Pending Treatment found for any family members.</p>
                              </div>
                              <div v-else-if="unscheduledTxForFamilyForUnscheduledPanel && unscheduledTxForFamilyForUnscheduledPanel.length > 0" class="table-resposive">
                                  <table class="table">
                                    <thead>
                                      <tr>
                                        <th>Name</th>
                                        <th>Priority</th>
                                        <th>Procedure Code</th>
                                        <th>Tooth/Surf</th>
                                        <th>Description</th>
                                        <th>Tx Plan Date</th>
                                        
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="(appt,index) in unscheduledTxForFamilyForUnscheduledPanel" :key="index">
                                        <td><a href="#" @click="gotoPatientPage(appt.PatNum)">{{formatName(appt.LName + ', ' +  appt.FName)}}</a></td>
                                        <td>{{appt.Priority}}</td>
                                        <td>{{appt.ProcCode}}</td>
                                        <td>{{appt.ToothNum}} {{appt.Surf}}</td>
                                        <td>{{appt.Descript}}</td>
                                        <td>{{appt.DateTP | dateformat(dateFormatStr)}}</td>
                                      </tr> 
                                    </tbody>
                                  </table>
                                </div>   
                            </b-tab>

                            <b-tab title="Insurance Info" v-if="this.patientInsurance && this.patientInsurance.length > 0 && this.patientInsurance[0].insurance !== null">
                              <div class="table-resposive">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th></th>
                                      <th>Carrier</th>
                                      <th>Max</th>
                                      <th>Used</th>
                                      <th>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td>Primary</td>
                                      <td>{{patientInsurance[0].insurance}}</td>
                                      <td>{{patientInsurance[0].annualMax | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</td>
                                      <td>{{patientInsurance[0].amountUsed | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</td>
                                      <td>{{patientInsurance[0].amountRemaining | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</td>
                                    </tr>
                                    <tr v-if="patientInsurance.length > 1">
                                      <td>Secondary</td>
                                      <td>{{patientInsurance[1].insurance}}</td>
                                      <td>{{patientInsurance[1].annualMax}}</td>
                                      <td>{{patientInsurance[1].amountUsed}}</td>
                                      <td>{{patientInsurance[1].amountRemaining}}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </b-tab>
                            <b-tab title="Family Members" v-if="isOverdueRecallPage && patientFamilyInfo && patientFamilyInfo.length > 0">
                              <div class="table-resposive">
                                <table class="table">
                                  <thead>
                                    <tr>
                                      <th>Name</th>
                                      <th>Age</th>
                                      <th>Contact</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(person,index) in this.patientFamilyInfo" :key="`person-${index}`">
                                      <td>{{formatName(person.LName  + ', ' + person.FName)}}</td>
                                      <td>{{getAge(person.Birthdate)}}</td>
                                      <td>
                                        <ul class="comunication-panel">
                                          <li v-if="person.HmPhone !== ''">
                                            <i class="fa fa-home fa-fw mr-1" style="font-size: 18px;" />{{formatPhone(person.HmPhone)}}
                                          </li>
                                          <li v-if="person.WirelessPhone !== ''">
                                            <i class="fa fa-mobile fa-fw mr-1" style="font-size: 18px;" />{{formatPhone(person.WirelessPhone)}}
                                          </li>
                                        </ul>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </b-tab>
          </b-tabs>
        </div>
        <!-- TABS ends here -->

       <!-- Family Recall Appointments Panel starts here 
        <div>
          <b-row>
            <b-col xl="12" xs="12">
                <section>
                  <Widget>
                        <div role="button" class="accordion-toggle cursor-pointer" @click="toggleBlock(3)">
                            <h6 class="fw-semi-bold"><a role="button" class="">Family Recall Appointments</a></h6>
                            <i :class="`ml-auto fa fa-angle-down ${this.isShown[3] ? 'expanded' : ''}`"></i>
                        </div>
                        <b-collapse id="accordion-assets" :visible="this.isShown[3]">
                            <div class="table-resposive">
                              <table class="table">
                                <thead>
                                  <tr>
                                    <th>Name</th>
                                    <th>Age</th>
                                    <th>Recall Scheduled</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td>Meloottu John, Bless</td>
                                    <td>36</td>
                                    <td><span class="badge mr-xs badge-danger badge-pill">Not Scheduled!</span></td>
                                  </tr>
                                  <tr>
                                    <td>Meloottu John, Bless</td>
                                    <td>36</td>
                                    <td><span class="badge mr-xs badge-danger badge-pill">Not Scheduled!</span></td>
                                  </tr> 
                                  <tr>
                                    <td>Meloottu John, Bless</td>
                                    <td>36</td>
                                    <td><span class="badge mr-xs badge-danger badge-pill">Not Scheduled!</span></td>
                                  </tr>  
                                </tbody>
                              </table>
                            </div>   
                      </b-collapse>
                  </Widget>
                </section>
            </b-col>
          </b-row>
        </div>
         Family Recall Appointments Panel ends here -->
    </section>

    <!-- AI Call Result Modal -->
    <div class="ai-call-result-overlay" v-if="isSuperAdmin && showAICallResultModal && aiCallResult">
      <div class="ai-call-result-modal">
        <div class="ai-call-result-header">
          <h5>
            <i class="fa fa-robot mr-2"></i>
            AI Call Result: <strong>{{ aiCallResult.outcome || 'unknown' }}</strong>
          </h5>
          <button type="button" class="close" @click="showAICallResultModal = false">&times;</button>
        </div>
        <div class="ai-call-result-meta">
          <span class="badge badge-secondary mr-2">Mode: {{ aiCallResult.mode || 'simulation' }}</span>
          <span class="badge badge-secondary" v-if="aiCallResult.duration">Duration: {{ aiCallResult.duration }}s</span>
        </div>
        <div class="ai-call-result-transcript" v-if="aiCallResult.transcript && aiCallResult.transcript.length">
          <div
            v-for="(msg, idx) in aiCallResult.transcript"
            :key="idx"
            class="transcript-msg"
            :class="msg.role === 'agent' ? 'transcript-agent' : 'transcript-patient'"
          >
            <span class="transcript-role">{{ msg.role === 'agent' ? 'AI Agent' : 'Patient' }}</span>
            <span class="transcript-text">{{ msg.text || msg.content || msg.message }}</span>
          </div>
        </div>
        <div class="ai-call-result-transcript" v-else-if="aiCallResult.reason">
          <p class="text-muted">{{ aiCallResult.reason }}</p>
        </div>
        <div class="ai-call-result-footer">
          <button type="button" class="followup-btn followup-btn-primary" @click="showAICallResultModal = false">Close</button>
        </div>
      </div>
    </div>

    <!-- Stripe Connect In-Office Payment Modal -->
    <StripePaymentModal
      v-if="isStripeSite"
      ref="stripePaymentModal"
      :siteID="siteID"
      :patNum="String(patNum)"
      :patientName="patientData ? (patientData.PatientName || (patientData.FName + ' ' + patientData.LName)) : ''"
      :patientBalance="patientBalance || 0"
      :patientPhone="patientData ? (patientData.WirelessPhone || '') : ''"
      @payment-success="handleStripeInOfficePaymentSuccess"
    />

  </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import DatePicker from "vue2-datepicker";
import Multiselect from "vue-multiselect";
import PopupErrorDisplay from "@/components/ErrorDisplay/PopupErrorDisplay";
import moment from 'moment-timezone';
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import mixin from '../../../mixin.js';
import Widget from '@/components/Widget/Widget';
import { isSmsEnabled, isEmailEnabled, hasSmsNumber, SMS_DISABLED_MESSAGE } from '@/utils/siteCapability';
import truncate from 'vue-truncate-collapsed';
import FeatureRequestButton from '@/components/FeatureRequestButton/FeatureRequestButton.vue';
import FeatureRequestNote from '@/components/FeatureRequestButton/FeatureRequestNote.vue';
import StripePaymentModal from '@/components/StripePaymentModal/StripePaymentModal.vue';
import CreateFollowupSkeleton from './CreateFollowupSkeleton.vue';
import SmsGuidance from '@/components/SmsGuidance.vue';
import { triggerAutorecallCall } from '@/api/autorecall';
import { pickVerifiedFromEmail } from '@/utils/verifiedSender.js';
import { withoutReviewRequestTemplates } from '@/utils/wikiTags';
import { isTextablePhone, describePhoneProblem } from '@/utils/patientPhone.js';
import { getTextBlockReason, isTextBlocked, optOutResultFromSendFailure } from '@/utils/textConsent.js';

// google-libphonenumber was dropped here on 2026-09-09 — see validatePhoneNumber().

export default {
    name: 'CreateFollowup',
    components: {Widget, DatePicker, truncate, Multiselect, PopupErrorDisplay, FeatureRequestButton, FeatureRequestNote, StripePaymentModal, CreateFollowupSkeleton, SmsGuidance},
    props: ['teamioPatientID', 'claimNum', 'results', 'detailPageName', 'query'],
    mixins: [mixin],

    mounted() {
      /*
      this.$root.$on('bv::popover::show', bvEventObj => {
        console.log('bvEventObj:', bvEventObj)
      })
      */
    },

    created() {
        this.init();
    },

    data(){
        return {
          loading: true,
          internalResults: null,
          overlayshow: false,
          overridden: false,
          dateBeforeOverride: null,
          followupDateVisible: true,
          
          isShown: [true],
          phoneAttemptFormVisible: false,
          smsAttemptFormVisible: false,
          emailAttemptFormVisible: false,
          collectPaymentFormVisible: false,
          notBefore: new Date(),
          followupDate: new Date(),
          followupMedium: '',
          copyToCommLog: '',
          ocCopyToClaimHistory: '',
          opCopyToClaimHistory: '',
          followupNote: '',
          smsBlocked: false, // set by <SmsGuidance> when the SMS/payment text is over the carrier limit
          lastGuidance: null, // latest <SmsGuidance> payload (followupNote), for the send-time cost confirm
          templateLinkMap: {},
          smsPreviewNote: '',
          statusID: 0,
          tabIndex: 0,
          columns: ["CommDateTime", "Note", "Mode_","UserName"],
          options: {
            filterable: false,
            skin: "table mt-1",
            sortable: [],
            perPage: 5,
            sortByColumn: false,
            pagination: { chunk: 5, dropdown: false },
            columnsClasses: {user:'text-center', note: 'note-col-width', Mode_: 'type-col'}
          },
          claimStatusHistoryColumns: ["DateTimeEntry", "Description", "LogNote","UserName"],
          claimStatusHistoryOptions: {
            filterable: false,
            skin: "table mt-1",
            sortable: [],
            perPage: 5,
            sortByColumn: false,
            pagination: { chunk: 5, dropdown: false },
            columnsClasses: {user:'text-center', note: 'note-col-width'}
          },
          attemptInProgress: false,
          taggedTasks: [],
          FollowupNoteUpdateInProgress: [].repeat(false, 50),
          oldFollowupNote: null,
          wikiPages: [],
          selectedWikiPageID: null,
          showEmailHelpPopOver: false,
          showSmsHelpPopOver: false,
          showCollectPaymentPopOver: false,
          //emailFromName: '',
          emailSubject: '',
          earlyFollowupDate: new Date(),
          earlyFollowupWizardStep: 1,
          earlyFollowupLoading: false,
          aiCallInProgress: false,
          aiCallResult: null,
          showAICallResultModal: false,
        }
    },
    methods: {

      ...mapActions('tasks', ['addUpdateFollowupTask', 'editFollowupNote', 'getTasksForEmployee', 'getTasksForSite', 'updateFollowupDate']),
      ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored2', 'setMessage2',]),
      ...mapActions('wiki', ['getWikiPages', 'getSiteTags']),
      ...mapActions('teamio', ['runQuery', 'sendTextMsg', 'sendEmailMsg', 'savePaymentInfo', 'createPaymentLink']),
      ...mapActions('moolah', ['createMoolahPaymentRecord']),

      // Shared SMS guidance (<SmsGuidance>): block the SMS / payment-SMS send when the
      // composed text is over the carrier limit. Wired into the Send buttons' :disabled.
      onSmsGuidance(g) {
        this.smsBlocked = !!(g && g.blocked);
        this.lastGuidance = g || null;
      },

      async init() {
        this.loading = true;
        try {
          this.resetAllErrors();

          // If results not passed as prop, fetch using query prop
          if (!this.results && this.query) {
            this.internalResults = await this.runQuery(this.query);
          }

          if(!this.tasksLoaded) {
            if(this.user.Role === 'Super Admin' || this.user.Role === 'Admin')
              await this.getTasksForSite({siteID: this.siteID, employeeID: this.userID});
            else
              await this.getTasksForEmployee({siteID: this.siteID, employeeID: this.userID});
          }
          this.copyToCommLog = this.getCommLogProperty();
          this.taggedTasks = this.getTasksTaggedWithPatient(this.patNum);
        } finally {
          this.loading = false;
        }
      },

      async onAICall() {
        if (this.aiCallInProgress || !this.canAICall) return;
        this.aiCallInProgress = true;
        this.aiCallResult = null;
        this.showAICallResultModal = false;
        try {
          const callReason = this.getFollowupType() === 'Recall' ? 'recall' : 'treatment';
          const result = await triggerAutorecallCall({
            siteID: this.siteID,
            patNum: this.patNum,
            taskID: String(this.getTaskTypeID()),
            callReason
          });
          if (result.success) {
            this.aiCallResult = result;
            this.showAICallResultModal = true;
          } else {
            this.$toast.error(result.reason || result.message || 'AI Call was blocked or failed');
          }
        } catch (err) {
          console.error('AI Call error:', err);
          const msg = (err.response && err.response.data && err.response.data.message) || err.message || 'AI Call failed';
          this.$toast.error(msg);
        } finally {
          this.aiCallInProgress = false;
        }
      },

      gotoPage() {
        /*
        this.$emit('closePanel');
        this.$router.push({
          name: 'patientprofile',
          params: {
            patientID : this.teamioPatientID
          }
        });
        */
        let routeData = this.$router.resolve({name: 'patientprofile', params: {patientID:  this.teamioPatientID}});
        window.open(routeData.href, '_blank');
      },

      async collectPayment() {
        if (!this.smsStatusID) {
          this.$toast.error('There is no mapping available for "texted" status. Please refer to your Settings or contact Teamio support');
          return false;
        }

        if (!this.validatePhoneNumber(this.patientData && this.patientData.WirelessPhone)) {
          this.$toast.error(describePhoneProblem(this.patientData && this.patientData.WirelessPhone));
          return false;
        }

        if (!this.followupNote || this.followupNote.trim() === '') {
          this.$toast.error('Please select a payment template first');
          return false;
        }

        const site = this.getSite(this.siteID);
        const provider = site && site.PaymentProvider;

        try {
          this.setLoading2(true);

          if (provider === 'moolah') {
            await this.sendMoolahPaymentSMS();
          } else if (provider === 'stripe') {
            await this.sendStripePaymentSMS();
          } else {
            this.$toast.error('No payment provider is configured for this site. Please set one up in Payment Settings before sending a payment link.');
            return false;
          }

          this.collectPaymentFormVisible = false;
        } catch (err) {
          console.error('Error in collectPayment:', err);
          this.$toast.error(err.response?.data?.message || err.message || 'Failed to send payment link');
        } finally {
          this.setLoading2(false);
        }
      },

      // Moolah text-to-pay: create a pending balance_payments row, then send the SMS
      // with the template variables left intact. The backend SQS worker substitutes
      // {{payment_link}} (and {{patient_balance}}) via utility.js processLink/getPaymentInfo,
      // which queries the same row we just inserted.
      // A /send-text-msg 200 body of { success:false, ... } is a REFUSAL (opt-out /
      // undeliverable), NOT a send — a successful send returns the bare string "success".
      // Throw the human reason (opt-out reason, else the body message) so collectPayment's
      // catch surfaces it instead of the "Payment link sent" toast, and the form stays open.
      assertTextSent(resp) {
        const data = resp && resp.data;
        if (data && data.success === false) {
          const optOut = optOutResultFromSendFailure(data);
          throw new Error((optOut && optOut.reason) || data.message || 'The text message was not sent.');
        }
      },

      async sendMoolahPaymentSMS() {
        await this.createMoolahPaymentRecord({
          siteID: this.siteID,
          patNum: this.patNum,
          balance: this.patientBalance,
          patientName: this.patientData.PatientName || `${this.patientData.FName} ${this.patientData.LName}`,
          patientPhone: this.patientData.WirelessPhone,
          // Delivered to the patient as a link below, so the backend logs an
          // Open Dental Account-module statement for it.
          sendsLink: true,
        });

        const resp = await this.sendTextMsg({
          siteID: this.siteID,
          patNum: this.patNum,
          smsText: this.followupNote,
          defaultDB: this.user.DefaultDB,
          userODNum: this.user.ODUserNum,
          // Marks this as a staff send ('manual') in sms_send_log + alerts, not 'unknown'.
          employeeName: this.user.Name || undefined,
        });
        this.assertTextSent(resp);

        this.$toast.success('Payment link sent to the patient.');
      },

      async sendStripePaymentSMS() {
        const paymentLinkResponse = await this.createPaymentLink({
          siteID: this.siteID,
          patNum: this.patNum,
          balance: this.patientBalance,
          patientName: this.patientData.PatientName || `${this.patientData.FName} ${this.patientData.LName}`,
          patientPhone: this.patientData.WirelessPhone,
        });

        if (!paymentLinkResponse || !paymentLinkResponse.paymentUrl) {
          throw new Error('Failed to create payment link - no URL returned');
        }

        let smsText = this.followupNote;
        smsText = smsText.replace(/\{\{payment_link\}\}/gi, paymentLinkResponse.paymentUrl);
        smsText = smsText.replace(/\{payment_link\}/gi, paymentLinkResponse.paymentUrl);
        smsText = smsText.replace(/\{\{total_amount\}\}/gi, `$${paymentLinkResponse.totalCharge.toFixed(2)}`);
        smsText = smsText.replace(/\{\{balance\}\}/gi, `$${paymentLinkResponse.originalBalance.toFixed(2)}`);

        const resp = await this.sendTextMsg({
          siteID: this.siteID,
          patNum: this.patNum,
          smsText,
          defaultDB: this.user.DefaultDB,
          userODNum: this.user.ODUserNum,
          employeeName: this.user.Name || undefined,
        });
        this.assertTextSent(resp);

        this.$toast.success(
          `Payment link sent! Patient will pay $${paymentLinkResponse.totalCharge.toFixed(2)} (includes $${paymentLinkResponse.stripeFee.toFixed(2)} processing fee)`
        );
      },

      gotoPatientPage(patNum) {
        let teamioPatientID = `${this.siteID}-${patNum}`;
        let routeData = this.$router.resolve({name: 'patientprofile', params: {patientID:  teamioPatientID}});
        window.open(routeData.href, '_blank');
      },

      doOverride() {
        this.overridden=true;
        this.dateBeforeOverride = this.followupDate;
        return false;
      },

      formatDateForDisplay(date) {
        if (!date) return '';
        return moment(date).format(this.dateFormatStr || 'MM/DD/YYYY');
      },

      async updateFollowupDateTo(newDate) {
        try {
          this.setLoading2(true);

          const payload = {
            SiteID: this.siteID,
            EmployeeID: this.userID,
            TaskTypeID: this.getTaskTypeID(),
            AssignedToEmpID: this.getAssignedToEmployeeID,
            TeamioPatientID: this.teamioPatientID,
            FollowupDate: moment(newDate).format('YYYY-MM-DD'),
            OldFollowupDate: this.tasksData && this.tasksData.length > 0 ? moment(this.tasksData[0].FollowupDate).format('MM-DD-YYYY') : null,
            EmployeeManuallyAssigned: 0,
            ClaimNum: this.claimNum || null,
          };

          await this.updateFollowupDate(payload);

          // Update local task data to reflect the change
          if (this.tasksData && this.tasksData.length > 0) {
            this.tasksData[0].FollowupDate = moment(newDate).format('YYYY-MM-DD');
          }

          return true;
        } catch (err) {
          console.error('Error updating followup date:', err);
          this.$toast.error(err.response?.data?.message || err.message || 'Failed to update follow-up date');
          return false;
        } finally {
          this.setLoading2(false);
        }
      },

      async onEarlyFollowupDateChange() {
        // Legacy method - kept for backwards compatibility
        if (!this.earlyFollowupDate) return;

        if (moment(this.earlyFollowupDate).isSameOrBefore(moment(), 'day')) {
          const success = await this.updateFollowupDateTo(this.earlyFollowupDate);
          if (success) {
            this.$toast.success('Follow-up date updated. You can now proceed with the follow-up.');
          }
        } else {
          this.$toast.warning('Please select today or an earlier date to proceed with the follow-up.');
        }
      },

      async onWizardDateChange() {
        if (!this.earlyFollowupDate) return;

        // Only update if the selected date is today or earlier
        if (moment(this.earlyFollowupDate).isSameOrBefore(moment(), 'day')) {
          this.earlyFollowupLoading = true;
          const success = await this.updateFollowupDateTo(this.earlyFollowupDate);
          this.earlyFollowupLoading = false;

          if (success) {
            this.$toast.success('Date updated successfully!');
            this.earlyFollowupWizardStep = 2;
          }
        } else {
          this.$toast.warning('Please select today or an earlier date.');
        }
      },

      async setDateToTodayAndProceed() {
        this.earlyFollowupLoading = true;
        const success = await this.updateFollowupDateTo(new Date());
        this.earlyFollowupLoading = false;

        if (success) {
          this.$toast.success('Date set to today!');
          this.earlyFollowupWizardStep = 2;
        }
      },

      completeWizardWithAction(actionType) {
        // Reset wizard for next time
        this.earlyFollowupWizardStep = 1;
        // Open the selected follow-up form
        this.$nextTick(() => {
          this.showAttempt(actionType);
        });
      },

      async setTodayAndStartPhoneFollowup() {
        // Legacy method - kept for backwards compatibility
        const success = await this.updateFollowupDateTo(new Date());
        if (success) {
          this.$toast.success('Follow-up date set to today.');
          this.$nextTick(() => {
            this.showAttempt('phone');
          });
        }
      },

      cancelOverride() {
        this.overridden=false; 
        this.followupDate = this.dateBeforeOverride; 
        this.updateFollowupNote();
        return false;
      },

      async closeSlideOutPanel() {
          let returnResults = {
            nextVisit: this.patientHasApptScheduledIn60Days
          }

          //let returnResults = {};
          this.$emit('closePanel',returnResults);
      },

      async save(followupMedium) {

        if(this.statusID === 0 && followupMedium.toUpperCase() !== 'SMS' && followupMedium.toUpperCase() !== 'EMAIL') {                    
          this.$toast.error("Please select a Follow-up Status of the call before saving");
          return;
        }

        if(this.followupDateVisible && !this.followupDate) {          
          this.$toast.error("Please select a Follow-up date before saving");          
          return;
        }

        if(followupMedium.toUpperCase() === 'SMS') {

          if(!this.smsStatusID) {            
            this.$toast.error("There is no mapping available for \"texted\" status. Please refer to your Settings or contact Teamio support");
            return false;
          }

          if(!this.smsTemplatesAvailable) {            
            this.$toast.error(`You do not have any template available for SMS related followups. Please <a href='/app/wiki'>click here</a> to create one now, tag it and then come back and try again`);
            return false;
          }

          if(this.selectedWikiPageID === null) {            
            this.$toast.error("You have not selected a template for this followup");
            return false;
          }

          if(!this.validatePhoneNumber(this.patientData && this.patientData.WirelessPhone)) {
            this.$toast.error(describePhoneProblem(this.patientData && this.patientData.WirelessPhone));
            return false;
          }

        } else if (followupMedium.toUpperCase() === 'EMAIL') {
          if(!this.emailStatusID) {            
            this.$toast.error('There is no mapping available for "emailed" status. Please refer to your Settings or contact Teamio support');
            return false;
          }

          if(!this.emailTemplatesAvailable) {            
            this.$toast.error(`You do not have any template available for Email related followups. Please <a href='/app/wiki'>click here</a> to create one now, tag it and then come back and try again`);
            return false;
          }
          
          if(this.selectedWikiPageID === null) {            
            this.$toast.error('You have not selected a template for this followup');
            return false;
          }
        }

        if(this.followupNote.trim() === '' && followupMedium.toUpperCase() !== 'EMAIL') {          
          this.$toast.error('Follow-up Note is empty. Please try again');
          return false;
        }

        let parentTask = this.tasksData && this.tasksData.length > 0 ? this.tasksData.filter(task => task.ParentTaskID === null)[0] : null;
        
        let belongsToListID;
        let belongsToList;

        if (this.isOverdueRecallPage || this.isUnscheduledTreatmentPage){
          belongsToListID = parentTask ? parentTask.BelongsToListID : this.patientList ? this.patientList[0].ListID : null;
          belongsToList = belongsToListID === null ? false: true;
        }
        
        let firstAttempt = false;
        let secondAttempt = false;
        
        let payload;
        let taskToUpdate;
        let taskToUpdate2;
        let parentTaskID;
        let attemptDetails;

        if(parentTask) {
          firstAttempt = parentTask.Attempts === 0;
          secondAttempt =  parentTask.Attempts === 1;
        } else {
          if(this.tasksData)
            firstAttempt = this.tasksData.length === 0;
          else
            firstAttempt = true;
        }

        let currentTask = this.tasksData && this.tasksData.length > 0 ? this.tasksData.filter(task => task.Attempts === this.totalAttemptsMade && task.Active === 1)[0] : null;

        /*
        console.log('belongsToListID:' + belongsToListID);
        console.log('belongsToList:' + belongsToList);
        console.log('firstAttempt:' + firstAttempt);
        console.log('secondAttempt:' + secondAttempt);
        console.log(`currentTask: ${JSON.stringify(currentTask)}`);
        */

        //console.log(`Attempt Type: ${followupMedium}`);
        //console.log(`Status Property: ${this.statusPropertyName}`);

        if(firstAttempt)
            parentTaskID = null;
        else
          parentTaskID = parentTask.TaskID;

        //console.log('ParentTaskID:' + parentTaskID);

        let fakeParentTask = {
          CreatedAt: moment().format('YYYY-MM-DDTHH:mm:ss'),
          //OverdueRecallStatusUpdatedAt: moment().format('YYYY-MM-DDTHH:mm:ss'),
          CreatedByEmpID: this.userID,
          AssignedToEmpID: this.userID,
          Attempts: firstAttempt ? 1 : parentTask.Attempts+1,
          TeamioPatientID : this.teamioPatientID,
          SiteID: this.siteID,
          // OverdueRecallStatusID: this.overdueRecallStatusID,
          FollowupDate: this.followupDate ? moment(this.followupDate).format('YYYY-MM-DD') : null,
          FollowupNote: '',
          DueDate : moment().format('YYYY-MM-DD'),
          StatusID: 1,  // We are marking the parent task done. This was never really a task but we're creating a fake one for the first attempt
          TaskTypeID: this.getTaskTypeID(),
          Active: 1,
          //TaskPurged: 1,  // because this is a fantom task - maybe not needed
          ParentTaskID: null,
          BelongsToListID: belongsToList ? belongsToListID : null,
          ClaimNum: this.claimNum,
          Overridden: this.overridden ? 1 : 0,
          DateBeforeOverride: moment(this.dateBeforeOverride).format('YYYY-MM-DD')
        };


        if(followupMedium.toUpperCase() === 'SMS') {
          attemptDetails = 'S';
          fakeParentTask[this.statusPropertyName] = this.smsStatusID;
          fakeParentTask['AttemptDetails'] = attemptDetails;
        } else if(followupMedium.toUpperCase() === 'EMAIL') {
          attemptDetails = 'E';
          fakeParentTask[this.statusPropertyName] = this.emailStatusID;
          fakeParentTask['AttemptDetails'] = attemptDetails;
        } else {
          attemptDetails = 'P';
          fakeParentTask[this.statusPropertyName] = this.statusID;
          fakeParentTask['AttemptDetails'] = attemptDetails;
        }
        
        fakeParentTask[this.statusUpdatedAtPropertyName] = moment().format('YYYY-MM-DDTHH:mm:ss');

        let taskToCreate = {
          CreatedAt: moment().format('YYYY-MM-DDTHH:mm:ss'),
          //OverdueRecallStatusUpdatedAt: moment().format('YYYY-MM-DDTHH:mm:ss'),
          CreatedByEmpID: this.userID,
          AssignedToEmpID: this.userID,
          Attempts: firstAttempt ? 1 : parentTask.Attempts+1,
          FollowupMedium: followupMedium,
          TeamioPatientID : this.teamioPatientID,
          SiteID: this.siteID,
          //OverdueRecallStatusID: this.overdueRecallStatusID,
          FollowupDate: this.followupDate ? moment(this.followupDate).format('YYYY-MM-DD') : null,
          FollowupNote: this.followupNote.trim() || '',
          DueDate : this.overrideEnabled ? moment(this.overrideDate).format('YYYY-MM-DD') : moment(this.followupDate).format('YYYY-MM-DD'),
          StatusID: 1,  // INCOMPLETE
          TaskTypeID: this.getTaskTypeID(),
          Active: 1,
          BelongsToListID: belongsToList ? belongsToListID : null,
          ClaimNum: this.claimNum,
          Overridden: this.overridden ? 1 : 0,
          DateBeforeOverride: moment(this.dateBeforeOverride).format('YYYY-MM-DD')
        };

        if(followupMedium.toUpperCase() === 'SMS') {
          taskToCreate['SmsTemplateID'] = this.selectedWikiPageID;
          // Restore [Link: ...] placeholders back to HTML anchors so backend processLink() can resolve short URLs
          let smsText = this.followupNote.trim() || '';
          if (this.templateLinkMap && Object.keys(this.templateLinkMap).length > 0) {
            for (const [placeholder, html] of Object.entries(this.templateLinkMap)) {
              smsText = smsText.replace(placeholder, html);
            }
          }
          taskToCreate['SmsText'] = smsText;

          // NOTE (2026-08-11): {{recall_due_date}} is deliberately NOT sent from here.
          // When SmsTemplateID is present, teamio-utility discards SmsText and re-renders the
          // template server-side, resolving the variable itself — and its resolver is better
          // informed than we are: it knows the site's configured recall types
          // (APPT_REMINDER_SETTINGS.recallReminderTypeRules) and the template being sent, so it can
          // narrow to the recall the reminder is actually about. patientData.RecallDue comes from
          // overdue-recall-details.js, which has no type restriction, so passing it would sometimes
          // OVERRIDE the better answer with a worse one (a patient whose 12M recall falls due before
          // their hygiene recall would get the 12M date). The preview below may therefore differ
          // from the sent message for such patients — closing that gap means teaching this page
          // about the configured recall types, or carrying the clicked RecallNum end to end.

          // let's find out the status called TEXTED
          taskToCreate[this.statusPropertyName] = this.smsStatusID;
        } else if(followupMedium.toUpperCase() === 'EMAIL') {
          taskToCreate['EmailTemplateID'] = this.selectedWikiPageID;
          /*
          taskToCreate['EmailFromName'] = this.emailFromName;
          taskToCreate['EmailFromEmail'] = this.emailFromEmail;
          taskToCreate['EmailToName'] = this.patientData.PatientName;
          taskToCreate['EmailToEmail'] = this.patientData.Email;
          taskToCreate['EmailSubject'] = this.emailSubject;
          taskToCreate['EmailHtml'] = this.followupNote.trim() || '';
          */

          // let's find out the status called EMAILED
          taskToCreate[this.statusPropertyName] = this.emailStatusID;
        }
        else {          
          taskToCreate[this.statusPropertyName] = this.statusID;
        }

        taskToCreate['AttemptDetails'] = attemptDetails;
        taskToCreate[this.statusUpdatedAtPropertyName] = moment().format('YYYY-MM-DDTHH:mm:ss');

        if(!parentTaskID) {
          payload = {
            type: this.getTaskTypeAllCaps(),
            firstAttempt: firstAttempt,
            //OverdueRecallStatusID: this.overdueRecallStatusID,
            fakeParentTask: fakeParentTask,
            taskToCreate: taskToCreate,
            copyToCommLog: this.copyToCommLog,
            ocCopyToClaimHistory: this.getTaskTypeID() === 2 && this.allSettings.OUTSTANDING_CLAIM_COPY_TO_OD_CLAIM_HISTORY ? this.allSettings.OUTSTANDING_CLAIM_COPY_TO_OD_CLAIM_HISTORY.PropertyValue : null,
            opCopyToClaimHistory: this.getTaskTypeID() === 5 && this.allSettings.OUTSTANDING_PREAUTHS_COPY_TO_OD_CLAIM_HISTORY ? this.allSettings.OUTSTANDING_PREAUTHS_COPY_TO_OD_CLAIM_HISTORY.PropertyValue : null,
            siteID: this.siteID,
            patNum: this.patNum,
            userODNum: this.user.ODUserNum,
            // Forwarded by main-service to the follow-up SMS so it logs as a staff send ('manual').
            employeeName: this.user.Name || undefined,
            defaultDB: this.user.DefaultDB,
          };
          if(followupMedium.toUpperCase() === 'SMS')          
            payload[this.statusPropertyName] = this.smsStatusID;
          else if(followupMedium.toUpperCase() === 'Email')
            payload[this.statusPropertyName] = this.emailStatusID;
          else
            payload[this.statusPropertyName] = this.statusID;
        } else {
          taskToUpdate = {
            taskID : parentTaskID,
            TaskTypeID: this.getTaskTypeID(),
            ClaimNum: this.claimNum ? this.claimNum: null,
            task : {
              UpdatedAt: moment().format('YYYY-MM-DDTHH:mm:ss'),
              //OverdueRecallStatusUpdatedAt: moment().format('YYYY-MM-DDTHH:mm:ss'),
              Attempts: parentTask.Attempts+1,
              AttemptDetails: `${parentTask.AttemptDetails},${attemptDetails}`, // Append the current attempt details to the parent task's attempt details so we have the whole history here
              //OverdueRecallStatusID: this.overdueRecallStatusID,
              FollowupDate: this.followupDate ? moment(this.followupDate).format('YYYY-MM-DD') : null,
              StatusID: 3, // Complete
              Overridden: this.overridden ? 1 : 0,
              DateBeforeOverride: moment(this.dateBeforeOverride).format('YYYY-MM-DD')
            }
          }
          if(followupMedium.toUpperCase() === 'SMS')          
            taskToUpdate.task[this.statusPropertyName] = this.smsStatusID;
          else if(followupMedium.toUpperCase() === 'EMAIL')          
            taskToUpdate.task[this.statusPropertyName] = this.emailStatusID;
          else
            taskToUpdate.task[this.statusPropertyName] = this.statusID;

          taskToUpdate.task[this.statusUpdatedAtPropertyName] = moment().format('YYYY-MM-DDTHH:mm:ss');
          //console.log('Updating main parent task:' + JSON.stringify(taskToUpdate));

          taskToUpdate2 = {
            taskID : currentTask.TaskID,
            TeamioPatientID: this.teamioPatientID,
            ClaimNum: this.claimNum ? this.claimNum: null,
            TaskTypeID: this.getTaskTypeID(),
            task : {
              UpdatedAt: moment().format('YYYY-MM-DDTHH:mm:ss'),
              Attempts: parentTask.Attempts+1,
              AttemptDetails: `${parentTask.AttemptDetails},${attemptDetails}`, // Append the current attempt details to the parent task's attempt details so we have the whole history here
              StatusID: 3, // Complete
              Overridden: this.overridden ? 1 : 0,
              DateBeforeOverride: moment(this.dateBeforeOverride).format('YYYY-MM-DD')
            }
          }

          if(followupMedium.toUpperCase() === 'SMS')          
            taskToUpdate2.task[this.statusPropertyName] = this.smsStatusID;
          else if(followupMedium.toUpperCase() === 'EMAIL')          
            taskToUpdate2.task[this.statusPropertyName] = this.emailStatusID;
          else
            taskToUpdate2.task[this.statusPropertyName] = this.statusID;

          taskToUpdate2.task[this.statusUpdatedAtPropertyName] = moment().format('YYYY-MM-DDTHH:mm:ss');

          payload = {
            type: this.getTaskTypeAllCaps(),
            firstAttempt: firstAttempt,
            copyToCommLog: this.copyToCommLog,
            ocCopyToClaimHistory: this.getTaskTypeID() === 2 && this.allSettings.OUTSTANDING_CLAIM_COPY_TO_OD_CLAIM_HISTORY ? this.allSettings.OUTSTANDING_CLAIM_COPY_TO_OD_CLAIM_HISTORY.PropertyValue : null,
            opCopyToClaimHistory: this.getTaskTypeID() === 5 && this.allSettings.OUTSTANDING_PREAUTHS_COPY_TO_OD_CLAIM_HISTORY ? this.allSettings.OUTSTANDING_PREAUTHS_COPY_TO_OD_CLAIM_HISTORY.PropertyValue : null,
            siteID: this.siteID,
            patNum: this.patNum,
            userODNum: this.user.ODUserNum,
            employeeName: this.user.Name || undefined,
            defaultDB: this.user.DefaultDB,
            taskToCreate: taskToCreate,
            taskToUpdate: taskToUpdate,
            taskToUpdate2: taskToUpdate2            
          };
          if(followupMedium.toUpperCase() === 'SMS')
            payload[this.statusPropertyName] = this.smsStatusID;
          else if(followupMedium.toUpperCase() === 'EMAIL')
            payload[this.statusPropertyName] = this.emailStatusID;
          else
            payload[this.statusPropertyName] = this.statusID;
        }

        //console.log('payload:' + JSON.stringify(payload));

        try {
          this.attemptInProgress = true;
          await this.addUpdateFollowupTask(payload);
            let returnResults = {
              Status: this.selectedFollowupStatusName,
              FollowupDate: this.followupDate ? moment(this.followupDate).format('YYYY-MM-DD') : null,
              Attempts: firstAttempt ? 1 : parentTask.Attempts+1,
              AttemptDetails: parentTask && parentTask.AttemptDetails ? `${parentTask.AttemptDetails},${attemptDetails}` : attemptDetails, // Append the current attempt details to the parent task's attempt details so we have the whole history here
              TeamioFollowupStatusID: this.getTeamioFollowupStatusID,
              StatusUpdatedAtPropertyName: this.statusUpdatedAtPropertyName,
              StatusUpdatedAtPropertyNameValue: moment().format('YYYY-MM-DDTHH:mm:ss'),
              nextVisit: this.patientHasApptScheduledIn60Days,
              Overridden: this.overridden ? 1 : 0,
            }
            //console.log(`Returning ${JSON.stringify(returnResults, null, 4)}`);
          this.$emit('closePanel', returnResults);
        } catch (err) {
            console.log(err);
            this.$toast.error('Failed to save follow-up attempt. Please try again.');
        } finally {
          this.attemptInProgress = false;
        }
      },

     getAge(birthDate) {
        return moment().diff(birthDate, 'years',false);
      },

      getGender(gender) {
        return gender === 1 ? 'F' : 'M';
      },

      async showAttempt(followupMedium) {
        if(!this.statuses || this.statuses.length === 0) {          
          this.$toast.error('You have not set up statuses yet. Please contact your administrator or contact teamio');
          return false;
        }

        if(this.isResponsiblePersonMakingAttempt 
          && this.attemptsAllowed && !this.doNotFollowup 
          && !this.maxAttemptsReached && this.followupAllowedToday) {

            this.followupMedium = followupMedium;
            
            let res;

            switch(followupMedium) {   
              case 'phone': 
                            this.followupNote = '';
                            this.phoneAttemptFormVisible = true; 
                            this.smsAttemptFormVisible = false;
                            this.emailAttemptFormVisible = false;
                            this.collectPaymentFormVisible = false;
                            break;
              case 'sms':
                            if(!this.canSendSMS) {
                              // Opt-out (texted STOP, OD Text-OK = No, or a server/HQ block) renders the
                              // shared message; otherwise diagnose the phone number. Same source of truth
                              // as the button state and the disabled-reasons popover, so all three agree.
                              const block = getTextBlockReason({ patient: this.patientData, timezone: this.smsOptOutTimezone });
                              const msg = block.blocked
                                ? block.message
                                : describePhoneProblem(this.patientData && this.patientData.WirelessPhone);
                              this.$toast.error(msg);
                              return false;
                            }
                            this.followupNote = '';
                            this.templateLinkMap = {};
                            this.smsPreviewNote = '';
                            this.smsAttemptFormVisible = true;
                            this.phoneAttemptFormVisible = false;
                            this.collectPaymentFormVisible = false;
                            res = await this.getWikiPages({siteID: this.siteID});
                            this.wikiPages = res[0];
                            break;
              case 'email': 
                            this.followupNote = '';
                            this.emailAttemptFormVisible = true;                           
                            this.phoneAttemptFormVisible = false;
                            this.smsAttemptFormVisible = false;
                            this.collectPaymentFormVisible = false;                                                        
                            res = await this.getWikiPages({siteID: this.siteID});
                            this.wikiPages = res[0];
                            break;              
            }
          
          //this.$nextTick(() => this.$refs.fstatus.$el.focus());
          this.calculateNextFollowupDate();
        }
      },

      async showPaymentAttempt() {
        if (this.isStripeSite) {
          this.$refs.stripePaymentModal.open();
          return;
        }

        // Moolah and any other provider: SMS payment link only. Terminal
        // charging now lives on the Patient Profile "Collect Payment" button.
        this.phoneAttemptFormVisible = false;
        this.smsAttemptFormVisible = false;
        this.emailAttemptFormVisible = false;
        this.collectPaymentFormVisible = true;
        let res = await this.getWikiPages({siteID: this.siteID});
        this.wikiPages = res[0];
      },

      handleStripeInOfficePaymentSuccess(paymentInfo) {
        this.$toast.success('Payment of $' + paymentInfo.amount.toFixed(2) + ' collected successfully!');
        // Close the slide-out panel and refresh the task list
        this.closeSlideOutPanel();
      },

      toggleBlock(index) {
        //console.log(`index: ${index}`);
        Vue.set(this.isShown, index, !this.isShown[index]);
      },

      getRecallActivityLabel(reminderType) {
        switch (reminderType) {
          case 'TEXT':  return 'Recall Text Reminder';
          case 'EMAIL': return 'Recall Email Reminder';
          default:      return 'Recall Reminder';
        }
      },

      getRecallActivityIcon(reminderType) {
        switch (reminderType) {
          case 'TEXT':  return 'fa fa-mobile';
          case 'EMAIL': return 'fa fa-envelope';
          default:      return 'fa fa-bell';
        }
      },

      getRecallIconClass(reminderType) {
        switch (reminderType) {
          case 'TEXT':  return 'text-icon';
          case 'EMAIL': return 'email-icon';
          default:      return 'default-icon';
        }
      },

      getRecallSentDate(activity) {
        if (activity.ReminderType === 'TEXT' && activity.SmsSentOn) return activity.SmsSentOn;
        if (activity.ReminderType === 'EMAIL' && activity.EmailSentOn) return activity.EmailSentOn;
        return activity.ReminderSentDate;
      },

      calculateNextFollowupDate() {
        // Only calculate the next follow-up date if this is not the last attempt
        if(!this.isLastAttempt) {
          let tz = this.allSettings.GENERAL_TIMEZONE ? this.allSettings.GENERAL_TIMEZONE.PropertyValue : '';
          let count,freq;

          // Now check to see if there's auto frequency set or custom frequency
          
          let auto = this.getAutoFrequency();
          if (auto !== null && auto !== '') {
            count = auto.split(' ')[0];
            freq = auto.split(' ')[1];
          } else {
            // custom frequency
            let temp2 = this.totalAttemptsMade + 1;
            let temp = this.getFollowupName() + temp2;

            //for Outstanding Claims, there's no settings so we're just letting them choose, but we need defaults

            if (this.isOutstandingClaimsPage || this.isOutstandingPreAuthPage || this.isPatientBalancesPage ) {
              count = this.allSettings[temp] ? this.allSettings[temp].PropertyValue.split(' ')[0] : 1;
              freq = this.allSettings[temp] ? this.allSettings[temp].PropertyValue.split(' ')[1] : 'WEEK';
            }
            else{
              //for other pages
              count = this.allSettings[temp].PropertyValue.split(' ')[0];
              freq = this.allSettings[temp].PropertyValue.split(' ')[1];
            }
          }
          //this.followupDate = moment().tz(tz).add(count, freq);
          this.followupDate = moment().add(count, freq);
        } else {
          // it's the last attempt
          this.followupDateVisible = false;
          this.followupDate = null;
        }
      },

      updateFollowupNote() {
        if(!this.selectedFollowupStatusName) {          
          this.$toast.error('Please select a status of the call first');
          return false;
        }

        // if the appointment was made or if they chose a status that doesn't require a follow-up then set it to null and hide the field
        if(this.isAppointmentScheduled || this.isClaimProcessed || this.followupNotRequired) {
          this.followupDateVisible = false;
          this.followupDate = null;
        } else {
          this.followupDateVisible = true;
          //don't need to call this again for Outstanding claims or Outstanding preAuths or Patient Balances page
          if (!this.overridden && (this.isOverdueRecallPage || this.isUnscheduledTreatmentPage || this.isBrokenAppointmentsPage)){
            this.calculateNextFollowupDate();
          }
        }
        
        let msg = `${this.getFollowupType()}`;
        
        if(this.isOutstandingClaimsPage) {
          msg = msg + ` (DOS: ${this.dosFormatted})`;
        }

        msg = msg + ` Follow-up #${this.totalAttemptsMade+1}: Followed up on ${moment().format(`${this.dateFormatStr} hh:mm A`)}. Follow-up status is ${this.selectedFollowupStatusName}.`;
        
        if(this.followupDate !== null)
          msg = msg + ` Next follow up date is ${moment(this.followupDate).format(this.dateFormatStr)}.`;
        if(this.isLastAttempt)
          msg = msg + ' Maximum allowed attempts completed.';
        // console.log(`msg:${msg}`);
        if(this.followupNote.trim() === "")
          this.followupNote = msg + '\n\n';
        else {
          let existingNote = this.followupNote;
          
          let regStatus = new RegExp("Follow-up status is (.*?)\\.");
          let regFollowupDate = new RegExp("Next follow up date is (.*?)\\.");
          let regMatches = regStatus.exec(this.followupNote);

          let statusWordToReplace, fuDateToReplace;

          if(regMatches && regMatches.length > 0)
            statusWordToReplace = regMatches[1];

          let fuDateMatches = regFollowupDate.exec(this.followupNote);
          if(fuDateMatches && fuDateMatches.length > 0)
            fuDateToReplace = fuDateMatches[1];

          //console.log(statusWordToReplace);
          //console.log(fuDateToReplace);

          if(statusWordToReplace)
            this.followupNote = this.followupNote.replace(statusWordToReplace, this.selectedFollowupStatusName);

          if(fuDateToReplace)
            this.followupNote = this.followupNote.replace(fuDateToReplace, moment(this.followupDate).format(this.dateFormatStr));

          if(!statusWordToReplace) {
            this.followupNote = msg + '\n\n' + this.followupNote;
          }

          //this.followupNote = msg + '\n\n' + this.followupNote;
        }
        this.$nextTick(() => this.$refs.fnote.focus());
      },

      updateSMSFollowupNote() {
        let wiki = this.wikiPages.find(w => w.WikiPageID === this.selectedWikiPageID);
        if(wiki) {
          let jsonContent = JSON.parse(wiki.Content);
          let linkMap = {};

          // Convert <a> tags to [Form: ...] or [Link: ...] placeholders (same pattern as PatientSidebar)
          const convertLinksToPlaceholders = (html) => {
            return html.replace(/<a\s+href="([^"]*)"[^>]*>(.*?)<\/a>/gi, (match, href, displayText) => {
              if (!displayText || displayText.trim() === '') return '';
              const cleanText = displayText.replace(/<[^>]*>/g, '').trim();
              let placeholder;
              if (href.indexOf('patient_link_for_form') >= 0) {
                placeholder = `[Form: ${cleanText}]`;
              } else if (href.indexOf('patient_link_for_packet') >= 0) {
                placeholder = `[Packet: ${cleanText}]`;
              } else {
                placeholder = `[Link: ${cleanText}]`;
              }
              linkMap[placeholder] = match;
              return placeholder;
            });
          };

          let allpara = jsonContent.blocks.filter(b => b.type === 'paragraph');
          let smsText = '';

          allpara.forEach(function(b) {
            let text = convertLinksToPlaceholders(b.data.text);
            let tmp = document.createElement("DIV");
            tmp.innerHTML = text;
            let tmpText = tmp.textContent || tmp.innerText || "";
            smsText = smsText + tmpText;
          });
          smsText = smsText.trimRight();

          this.templateLinkMap = linkMap;

          let site = this.getSite(this.siteID);

          let availableVariables = {
            patient_first_name: this.patientData.FName,
            patient_last_name: this.patientData.LName,
            patient_name: this.patientData.PatientName,
            patient_wireless_phone: this.patientData.WirelessPhone,
            site_id: this.siteID,
            site_name : this.user && this.user.SiteName ? this.user.SiteName : '',
            site_address1: site.Address1,
            site_address2: site.Address2,
            site_city: site.City,
            site_state: site.State,
            site_zip: site.Zip,
            site_officephone: site.OfficePhone,
            employee_name: this.user && this.user.Name ? this.user.Name : ''
          };

          if (this.patientBalance)
            availableVariables['patient_balance'] = this.patientBalance.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');

          if (this.isOverdueRecallPage && this.patientData.RecallDue)
            availableVariables['recall_due_date'] = this.formatDate(this.patientData.RecallDue);

          this.followupNote = this.replaceTemplateVariables(smsText, availableVariables);

          // Generate preview note showing what patient will receive (short URL instead of placeholder)
          if (Object.keys(linkMap).length > 0) {
            let preview = this.followupNote;
            for (const placeholder of Object.keys(linkMap)) {
              preview = preview.replace(placeholder, 'https://teamio.app/b/xxxxxxx');
            }
            this.smsPreviewNote = preview;
          } else {
            this.smsPreviewNote = '';
          }
        }
      },

      updateEmailFollowupNote() {
        let wiki = this.wikiPages.find(w => w.WikiPageID === this.selectedWikiPageID);
        if(wiki) {

          let msg = `${this.getFollowupType()}`;

          msg = msg + ` Follow-up #${this.totalAttemptsMade+1}: Followed up on ${moment().format(`${this.dateFormatStr} hh:mm A`)}. Follow-up status is ${this.selectedFollowupStatusName}.`;

          msg = msg + ` (template: ${wiki.Name}).`;
          
          if(this.followupDate !== null)
            msg = msg + ` Next follow up date is ${moment(this.followupDate).format(this.dateFormatStr)}.`;
          if(this.isLastAttempt)
            msg = msg + ' Maximum allowed attempts completed.';
          // console.log(`msg:${msg}`);
          if(this.followupNote.trim() === "")
            this.followupNote = msg + '\n\n';
          else
            this.followupNote = msg + '\n\n' + this.followupNote;
          this.$nextTick(() => this.$refs.fnote.focus());
          /*          
          console.log('selected wiki', wiki);
          this.emailSubject = wiki.Name;
          let jsonContent = JSON.parse(wiki.Content);
          console.log('content', jsonContent);
          //let emailHtml = jsonContent.blocks[1].data.text;

          let allpara = jsonContent.blocks.filter(b => b.type === 'paragraph');
          let emailHtml = '';
          let tmp, tmpText;

          allpara.forEach(function(b) {
            tmp = document.createElement("DIV");
            tmp.innerHTML = b.data.text;
            tmpText = tmp.textContent || tmp.innerText || "";
            emailHtml = emailHtml + tmpText + '\n\n';
          });
          emailHtml = emailHtml.trimRight();

          console.log('emailHtml', emailHtml);

          let site = this.getSite(this.siteID);
          //console.log(site);
          
          let availableVariables = {
            patient_first_name: this.patientData.FName,
            patient_last_name: this.patientData.LName,
            patient_name: this.patientData.PatientName,
            patient_wireless_phone: this.patientData.WirelessPhone,
            site_id: this.siteID,            
            site_name : this.user && this.user.SiteName ? this.user.SiteName : '',
            site_address1: site.Address1,
            site_address2: site.Address2,
            site_city: site.City,
            site_state: site.State,
            site_zip: site.Zip,
            site_officephone: site.OfficePhone,
            employee_name: this.user && this.user.Name ? this.user.Name : ''
          };

          //console.log('available variables', availableVariables);          
          this.followupNote = this.replaceTemplateVariables(emailHtml, availableVariables);   
          */       
        }        
      },
      

      getCommLogProperty() {
        // this.allSettings.OUTSTANDING_CLAIM_COPY_TO_COMMLOG.PropertyValue
        switch(this.detailPageName) {
          case 'OverdueRecallDetails' : return this.allSettings.OVERDUE_RECALL_COPY_TO_COMMLOG ? this.allSettings.OVERDUE_RECALL_COPY_TO_COMMLOG.PropertyValue : ''; break;
          case 'unscheduledPatientDetails': return this.allSettings.UNSCHEDULED_TREATMENT_COPY_TO_COMMLOG ? this.allSettings.UNSCHEDULED_TREATMENT_COPY_TO_COMMLOG.PropertyValue : ''; break;
          case 'BrokenAppointmentDetails': return this.allSettings.BROKEN_APPOINTMENTS_COPY_TO_COMMLOG ? this.allSettings.BROKEN_APPOINTMENTS_COPY_TO_COMMLOG.PropertyValue : ''; break;
          case 'AgingDetails': return this.allSettings.OUTSTANDING_CLAIM_COPY_TO_COMMLOG ? this.allSettings.OUTSTANDING_CLAIM_COPY_TO_COMMLOG.PropertyValue : ''; break;
          case 'OutstandingPreAuthDetails': return this.allSettings.OUTSTANDING_PREAUTHS_COPY_TO_COMMLOG ? this.allSettings.OUTSTANDING_PREAUTHS_COPY_TO_COMMLOG.PropertyValue : ''; break;
          case 'ProcessedPreAuthDetails': return this.allSettings.PROCESSED_PREAUTHS_COPY_TO_COMMLOG ? this.allSettings.PROCESSED_PREAUTHS_COPY_TO_COMMLOG.PropertyValue : ''; break;
          case 'PatientBalancesDetails': return this.allSettings.PATIENT_BALANCES_COPY_TO_COMMLOG ? this.allSettings.PATIENT_BALANCES_COPY_TO_COMMLOG.PropertyValue : ''; break;
          default: return ''; break;
        }
      },

      getTaskTypeID() {
        switch(this.detailPageName) {
          case 'OverdueRecallDetails' : return 3; break;
          case 'unscheduledPatientDetails': return 4; break;
          case 'BrokenAppointmentDetails': return 8; break;
          case 'AgingDetails': return 2; break;
          case 'OutstandingPreAuthDetails': return 5; break;
          case 'ProcessedPreAuthDetails': return 6; break;
          case 'PatientBalancesDetails': return 7; break;
          default: return null; break;
        }
      },

      getFollowupType() {
        switch(this.detailPageName) {
          case 'OverdueRecallDetails' : return 'Recall'; break;
          case 'unscheduledPatientDetails': return 'Unscheduled Treatment'; break;
          case 'BrokenAppointmentDetails': return 'Broken Appointment'; break;
          case 'AgingDetails': return 'Outstanding Claim'; break;
          case 'OutstandingPreAuthDetails': return 'Outstanding PreAuth'; break;
          case 'ProcessedPreAuthDetails': return 'Processed PreAuth'; break;
          case 'PatientBalancesDetails': return 'Patient Balances'; break;
          default: return ''; break;
        }
      },

      getTaskTypeAllCaps() {
        switch(this.detailPageName) {
          case 'OverdueRecallDetails' : return 'OVERDUE_RECALL'; break;
          case 'unscheduledPatientDetails': return 'UNSCHEDULED_TREATMENT'; break;
          case 'BrokenAppointmentDetails': return 'BROKEN_APPOINTMENTS'; break;
          case 'AgingDetails': return 'OUTSTANDING_CLAIMS'; break;
          case 'OutstandingPreAuthDetails': return 'OUTSTANDING_PREAUTHS'; break;
          case 'ProcessedPreAuthDetails': return 'PROCESSED_PREAUTHS'; break;
          case 'PatientBalancesDetails': return 'PATIENT_BALANCES'; break;
          default: return null; break;
        }
      },
       
      getFollowupName() {
        if(this.getTaskTypeAllCaps() !== null)
          return `${this.getTaskTypeAllCaps()}_FOLLOWUP_`; 
      },

      getAutoFrequency() {
        switch(this.detailPageName) {
          case 'OverdueRecallDetails' : return this.allSettings.OVERDUE_RECALL_AUTO_FREQUENCY ? this.allSettings.OVERDUE_RECALL_AUTO_FREQUENCY.PropertyValue : ''; break;
          case 'unscheduledPatientDetails': return this.allSettings.UNSCHEDULED_TREATMENT_AUTO_FREQUENCY ? this.allSettings.UNSCHEDULED_TREATMENT_AUTO_FREQUENCY.PropertyValue : ''; break;
          case 'BrokenAppointmentDetails': return this.allSettings.BROKEN_APPOINTMENTS_AUTO_FREQUENCY ? this.allSettings.BROKEN_APPOINTMENTS_FREQUENCY.PropertyValue : ''; break;
          case 'AgingDetails':
                              let freq;
                              if(this.allSettings.OUTSTANDING_CLAIM_FOLLOWUP_DURATION && this.allSettings.OUTSTANDING_CLAIM_FOLLOWUP_DURATION !== '')
                                freq = this.allSettings.OUTSTANDING_CLAIM_FOLLOWUP_DURATION.PropertyValue + ' ' + this.allSettings.OUTSTANDING_CLAIM_FOLLOWUP_DURATION_TYPE.PropertyValue;
                              else
                                freq = '';                              
                              return freq;                              
          case 'OutstandingPreAuthDetails': return this.allSettings.OUTSTANDING_PREAUTHS_AUTO_FREQUENCY ? this.allSettings.OUTSTANDING_PREAUTHS_AUTO_FREQUENCY.PropertyValue : ''; break;
          case 'ProcessedPreAuthDetails': return this.allSettings.PROCESSED_PREAUTHS_AUTO_FREQUENCY ? this.allSettings.PROCESSED_PREAUTHS_AUTO_FREQUENCY.PropertyValue : ''; break;
          case 'PatientBalancesDetails': return this.allSettings.PATIENT_BALANCES_AUTO_FREQUENCY ? this.allSettings.PATIENT_BALANCES_AUTO_FREQUENCY.PropertyValue : ''; break;
          default: return ''; break;
        }
      },

      getStatus(status) {
        switch(this.detailPageName) {
          case 'OverdueRecallDetails' : return status.OverdueRecallStatus; break;
          case 'unscheduledPatientDetails': return status.UnscheduledTreatmentStatus; break;
          case 'BrokenAppointmentDetails': return status.BrokenAppointmentStatus; break;
          case 'AgingDetails': return status.OutstandingClaimStatus; break;
          case 'OutstandingPreAuthDetails': return status.OutstandingPreAuthStatus; break;
          case 'ProcessedPreAuthDetails': return status.ProcessedPreAuthStatus; break;
          case 'PatientBalancesDetails': return status.PatientBalancesStatus; break;
          default: return null; break;
        }   
      },

      async _editFollowupNote(activity, index) {        
        let payload = {
          SiteID: this.siteID,
          TeamioPatientID: this.teamioPatientID,
          EmployeeID: this.userID,
          TaskTypeID: this.getTaskTypeID(),
          ClaimNum: this.claimNum,
          OldFollowupNote: this.oldFollowupNote,
          FollowupNote: activity.FollowupNote,
          UserODNum: this.user.ODUserNum,
          PatNum: this.patNum,
          TaskID: activity.TaskID,
          DefaultDB: this.user.DefaultDB,
        }

        let p = `${this.getTaskTypeAllCaps()}_COPY_TO_COMMLOG`;
        if(p === 'OUTSTANDING_CLAIMS_COPY_TO_COMMLOG')
          p = 'OUTSTANDING_CLAIM_COPY_TO_COMMLOG';
        //console.log(`Property: ${p}`);
        payload.CopyToCommLog = this.allSettings[p] ? this.allSettings[p].PropertyValue : 'TRUE';
        //console.log(`Payload: ${JSON.stringify(payload)}`);
        try {
          await this.editFollowupNote(payload);
          this.$refs[`${index}-edit-dropdown`][0].hide();
        } catch(err) {
          console.log(err);
          activity.FollowupNote = this.oldFollowupNote; // restore the old note back if there's an error
        }
      },

      closeEditDropdown(index) {
        this.$refs[`${index}-edit-dropdown`][0].hide();
      },

      beforeShowEditNote(activity) {
        this.oldFollowupNote = activity.FollowupNote;
      },

      readyToSaveFollowupNote(activity) {
        return activity.FollowupNote !== this.oldFollowupNote
      },

      replaceTemplateVariablesORIG(str, availableVariables) {        
        let pattern = /{{.+?}}/g;    
        let result = str.match(pattern);
        //console.log(`replaceTemplateVariables result: ${JSON.stringify(result, null, ' ')}`);         
        
        if(result && result.length > 0) {
          result.forEach(function(match) {              
            let temp = match.replace('{{', '');
            temp = temp.replace('}}', '');
            if(availableVariables[temp]) {            
              str = str.replace(temp, availableVariables[temp]);
              str = str.replace('{{', '');
              str = str.replace('}}', '');
            }              
          });
        }        
        return str;
      },

      replaceTemplateVariables(str, availableVariables) {
        let pattern = /{{(.+?)}}/g;
        let result;
        let word;

        do {
          result = pattern.exec(str);    
          if (result) {
            word = result[1];      
            if (availableVariables[word]) {
              str = str.replace(result[0], availableVariables[word]);        
            }
          }
        } while (result);
        return str;
      },

      autoRecallReminderType(activity) {
        if(activity.ReminderType === 'TEXT')
          return `<span class="fw-normal">Text Sent On ${activity.SmsSentOn}</span>`;
        else
        return `<span class="fw-normal">Email Sent On ${activity.EmailSentOn}</span>`;
      },

      // Would the send endpoint accept this patient's number? Shared rule —
      // src/utils/patientPhone.js, identical to main-service getPatientPhoneByPatNum.
      //
      // This used to be google-libphonenumber's isValidNumberForRegion(x, 'US'). Measured
      // against the backend on 2026-09-09, that was wrong three ways: it REJECTED the
      // Edmonton overlay 8255551234 that the backend texts fine (the same false-negative
      // class that blocked real patient texts on 2026-06-01 and made teamio-utility stop
      // gating on this library), it ACCEPTED "555-123-4567 x123" that the backend 400s,
      // and phoneUtil.parse THREW an unhandled exception on the literal string "none" —
      // which is exactly what gets typed into an empty phone field.
      validatePhoneNumber(phoneNum) {
        return isTextablePhone(phoneNum);
      },

      resetAllErrors() {
        this.setErrored2(false);
        this.setMessage2('');
      },

      // Feature request event handlers
      onFeatureRequestSent(featureName) {
        this.$toast.success(`${featureName} request sent successfully! Our team will contact you soon.`);
      },

      onFeatureRequestError(error) {
        this.$toast.error(`Failed to send request: ${error.message || 'Please try again or contact support directly.'}`);
      },
    },

    computed: {

      ...mapGetters({
            'getTasksTaggedWithPatient' : 'tasks/getTasksTaggedWithPatient',
      }),

      formattedSmsNumber() {
        if (this.allSettings && this.allSettings.GENERAL_PHONE && this.allSettings.GENERAL_PHONE.PropertyValue) {
          const raw = this.allSettings.GENERAL_PHONE.PropertyValue.trim();
          const digits = raw.replace(/\D/g, '');
          if (digits.length === 11 && digits.startsWith('1')) {
            return `(${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7, 11)}`;
          }
          if (digits.length === 10) {
            return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
          }
          return raw;
        }
        return '';
      },
      // Site capability via src/utils/siteCapability.js (single implementation).
      // NOTE the `this.allSettings &&` guard is DELIBERATELY preserved: when settings
      // have not loaded yet this gate fails OPEN (button enabled), and the helper is
      // default-deny. Dropping the guard would disable the button during the
      // pre-settings window — a behavior change, not a refactor.
      shouldEnableSMSFollowup() {
        let t = this.getFollowupType();
        // Opt-out (texted STOP, OD Text-OK = No, or a server/HQ block) now disables the
        // BUTTON too, not just the click guard — so an opted-out patient no longer shows an
        // enabled button that blocks on click. src/utils/textConsent.js is the one rule the
        // button, the click guard and the disabled-reasons popover all share.
        if (!this.patientData.WirelessPhone || this.patientData.WirelessPhone === '' || t === 'Outstanding Claim' || t === 'Outstanding PreAuth' || (this.allSettings && !isSmsEnabled(this.allSettings)) || isTextBlocked({ patient: this.patientData, timezone: this.smsOptOutTimezone }))
          return false;
        else
          return true;
      },

      // Same guard, and additionally a CORRECTNESS fix: this used to test
      // `PropertyValue === ''`, which called a site email-enabled for a non-blank but
      // senderless value (e.g. "[]"). isEmailEnabled runs the real five-shape parser —
      // the same one used ~80 lines below to build the From address, which previously
      // could resolve to nothing while this gate said "enabled".
      shouldEnableEmailFollowup() {
        let t = this.getFollowupType();
        if (!this.patientData.Email || this.patientData.Email === '' || t === 'Outstanding Claim' || t === 'Outstanding PreAuth' || (this.allSettings && !isEmailEnabled(this.allSettings)))
          return false;
        else
          return true;
      },

      shouldShowCollectPayment() {
        // Visible on the Patient Balances followup panel for ALL roles.
        // (Was Super-Admin-only; opened up 2026-06-18. Access is now gated by
        // `collectPaymentDisabledReasons`, which disables the button + explains
        // exactly what's missing for anyone who can't use it yet.)
        return this.getFollowupType() === 'Patient Balances';
      },

      // Specific, human-readable reasons the Collect Payment button is disabled.
      // Empty array === button enabled. Drives BOTH `shouldEnableCollectPayment`
      // and the help popover so the operator sees exactly what's missing.
      //
      // This panel's action is always "Send Payment SMS" (text-to-pay) for both
      // Moolah and Stripe sites — the in-office terminal charge lives on the
      // Patient Profile — so we require a recipient mobile regardless of provider.
      collectPaymentDisabledReasons() {
        const reasons = [];
        const s = this.allSettings || {};

        // 1) Payment integration master toggle (Settings → Payment). Backend
        //    writes PAYMENT_ENABLED ('1' / '0'); legacy PAYMENT_INTEGRATION is
        //    no longer written by current code.
        const paymentOn = !!(s.PAYMENT_ENABLED && s.PAYMENT_ENABLED.PropertyValue === '1');
        if (!paymentOn) {
          reasons.push('Payment integration is not turned on for this practice (Settings → Payment).');
        }

        // 2) Practice texting number (GENERAL_PHONE) — the payment SMS is sent
        //    FROM this number, so it must be configured.
        const generalPhoneSet = isSmsEnabled(s);
        if (!generalPhoneSet) {
          // WhatsApp-only office: the number exists, texting is off on purpose (drawerMethods agrees).
          reasons.push(this.smsOutboundOff
            ? "Texting is turned off for this office, so a payment link can't be texted."
            : 'This practice does not have a texting phone number set up yet.');
        }

        // 3) Patient mobile (WirelessPhone) — the payment link is texted TO the
        //    patient, so they must have a mobile/text number on file.
        const patientMobile = this.patientData && this.patientData.WirelessPhone;
        const patientHasMobile = !!(patientMobile && String(patientMobile).trim() !== '');
        if (!patientHasMobile) {
          reasons.push('This patient has no mobile/text number on file to send the payment link to.');
        }

        return reasons;
      },

      shouldEnableCollectPayment() {
        return this.collectPaymentDisabledReasons.length === 0;
      },

      canShowAICall() {
        if (!this.isSuperAdmin) return false;
        // Only show for Recall and Unscheduled Treatment followup types
        const t = this.getFollowupType();
        return t === 'Recall' || t === 'Unscheduled Treatment';
      },

      canAICall() {
        // Needs a wireless phone number to call
        if (!this.patientData.WirelessPhone || this.patientData.WirelessPhone === '') return false;
        return true;
      },

      isMoolahSite() {
        var site = this.getSite(this.siteID);
        return site && site.PaymentProvider === 'moolah';
      },

      isStripeSite() {
        var site = this.getSite(this.siteID);
        return site && site.PaymentProvider === 'stripe' && !!site.StripeAccountId;
      },

      emailFromEmail() {
        if(this.allSettings) {
          // Parsed via the shared helper — GENERAL_VERIFIED_EMAILS can be a CSV
          // list (teamio-admin writes one when a second sender is added) and a
          // whole list as a From header is rejected by SendGrid.
          return pickVerifiedFromEmail(this.allSettings.GENERAL_VERIFIED_EMAILS) || '';
        }
      },

      emailFromName() {
        let site = this.getSite(this.siteID);
        return site.Name;
      },

      // Site timezone (GENERAL_TIMEZONE) for the shared text-consent message.
      smsOptOutTimezone() {
        return (this.allSettings && this.allSettings.GENERAL_TIMEZONE && this.allSettings.GENERAL_TIMEZONE.PropertyValue) || '';
      },

      canSendSMS() {
        // The ONE opt-out rule (texted STOP, OD Text-OK = No, or a server/HQ block) —
        // src/utils/textConsent.js. Replaces the old inline OD-flag comparison, which saw
        // only the OD Text-OK flag and missed the texted-STOP ledger.
        if(isTextBlocked({ patient: this.patientData, timezone: this.smsOptOutTimezone })) {
          return false;
        }
        // ...and the number has to be one the send endpoint will accept. Checked here, at
        // tab selection, so the operator is told BEFORE composing a follow-up note that
        // cannot be delivered — the same reason the send itself is guarded below.
        return isTextablePhone(this.patientData && this.patientData.WirelessPhone);
      },

      SMSWikiPagesForFollowup() {
        let tag;

        switch(this.getTaskTypeAllCaps()) {
          case 'OVERDUE_RECALL' : tag = 'recall-follow-up-sms-template'; break;
          case 'UNSCHEDULED_TREATMENT' : tag = 'unscheduled-tx-follow-up-sms-template'; break;
          case 'BROKEN_APPOINTMENTS' : tag = 'broken-apt-follow-up-sms-template'; break;          
          case 'PATIENT_BALANCES' : tag = 'patient-balance-follow-up-sms-template'; break;          
        }

        console.log('tag', tag);

        if(this.wikiPages) {
          return this.wikiPages.filter(w => w.Tags && w.Tags.includes(tag));
        }
      },

      EmailWikiPagesForFollowup() {
        let tag = 'email-template';

        /*
        switch(this.getTaskTypeAllCaps()) {
          case 'OVERDUE_RECALL' : tag = 'recall-follow-up-email-template'; break;
          case 'UNSCHEDULED_TREATMENT' : tag = 'unscheduled-tx-follow-up-email-template'; break;
          case 'BROKEN_APPOINTMENTS' : tag = 'broken-apt-follow-up-email-template'; break;          
          case 'PATIENT_BALANCES' : tag = 'patient-balance-follow-up-email-template'; break;
        }
        */

        if(this.wikiPages) {
          // Reviews templates stay out: only the Reviews send path can fill their {{review_link}}.
          return withoutReviewRequestTemplates(this.wikiPages).filter(w => w.Tags && w.Tags.includes(tag));
        }
      },

      paymentWikiPages() {
        if(this.wikiPages) {
          return this.wikiPages.filter(w => w.Tags && w.Tags.includes('payment'));
        }
      },

      smsTemplatesAvailable() {
        if(this.SMSWikiPagesForFollowup && this.SMSWikiPagesForFollowup.length > 0)
          return true;
        else
          return false;
      },

      emailTemplatesAvailable() {
        if(this.EmailWikiPagesForFollowup && this.EmailWikiPagesForFollowup.length > 0)
          return true;
        else
          return false;
      },

      dosFormatted() {
        if(this.claimData && this.claimData.DateService)
          return moment(this.claimData.DateService).format('MM/DD/YYYY');
      },

      followupDateEditable() {
        // return this.getTaskTypeID === 2 || this.getTaskTypeID === 5 || this.getTaskTypeID === 7;
        return (this.isOutstandingClaimsPage || this.isOutstandingPreAuthPage || this.isPatientBalancesPage );
      },

      /* Decide which setting to look at to see who's reponsible for this based on what page we're on */

      printTitleOfFollowUp(){
        switch(this.detailPageName) {
          case 'OverdueRecallDetails' : return 'Overdue Recall'; break;
          case 'unscheduledPatientDetails': return 'Unscheduled Treatment'; break;
          case 'BrokenAppointmentDetails': return 'Broken Appointment'; break;
          case 'AgingDetails': return 'Outstanding Claim'; break;
          case 'OutstandingPreAuthDetails': return 'Outstanding Pre-Authorization'; break;
          case 'ProcessedPreAuthDetails': return 'PROCESSED_PREAUTHS'; break;
          case 'PatientBalancesDetails': return 'Patient Balances'; break;
          default: return null; break;
        }  
      },

      responsibleEmployeeID() {
        if(this.allSettings) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : return this.allSettings.OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID ? this.allSettings.OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID.PropertyValue.split('|') : null; break;
            case 'unscheduledPatientDetails': return this.allSettings.UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID ? this.allSettings.UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID.PropertyValue.split('|') : null; break;
            case 'BrokenAppointmentDetails': return this.allSettings.BROKEN_APPOINTMENTS_RESPONSIBLE_EMPLOYEEID ? this.allSettings.BROKEN_APPOINTMENTS_RESPONSIBLE_EMPLOYEEID.PropertyValue.split('|') : null; break;
            case 'AgingDetails': return this.allSettings.OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID ? this.allSettings.OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID.PropertyValue.split('|') : null; break;
            case 'OutstandingPreAuthDetails': return this.allSettings.OUTSTANDING_PREAUTHS_RESPONSIBLE_EMPLOYEEID ? this.allSettings.OUTSTANDING_PREAUTHS_RESPONSIBLE_EMPLOYEEID.PropertyValue.split('|') : null; break;
            case 'ProcessedPreAuthDetails': return this.allSettings.PROCESSED_PREAUTHS_RESPONSIBLE_EMPLOYEEID ? this.allSettings.PROCESSED_PREAUTHS_RESPONSIBLE_EMPLOYEEID.PropertyValue.split('|') : null; break;
            case 'PatientBalancesDetails': return this.allSettings.PATIENT_BALANCES_RESPONSIBLE_EMPLOYEEID ? this.allSettings.PATIENT_BALANCES_RESPONSIBLE_EMPLOYEEID.PropertyValue.split('|') : null; break;
            default: return null; break;
          }   
        }
      },

      /*
        which statuses to show in dropdown depending on the page we're on
      */
      statuses() {
        switch(this.detailPageName) {
          case 'OverdueRecallDetails' : return this.overdueRecallStatuses; break;
          case 'unscheduledPatientDetails': return this.unscheduledTreatmentStatuses; break;
          case 'BrokenAppointmentDetails': return this.brokenAppointmentStatuses; break;
          case 'AgingDetails': return this.outstandingClaimStatuses; break;
          case 'OutstandingPreAuthDetails': return this.outstandingPreAuthStatuses; break;
          case 'ProcessedPreAuthDetails': return this.processedPreAuthStatuses; break;
          case 'PatientBalancesDetails': return this.patientBalancesStatuses; break;
          default: return null; break;
        }   
      },

      smsStatusID() {
        let tempStatus;        
        switch(this.detailPageName) {
          case 'OverdueRecallDetails' : tempStatus = this.statuses.find(s => s.OverdueRecallStatus.toUpperCase() === "TEXTED"); break;
          case 'unscheduledPatientDetails': tempStatus = this.statuses.find(s => s.UnscheduledTreatmentStatus.toUpperCase() === "TEXTED"); break;
          case 'BrokenAppointmentDetails': tempStatus = this.statuses.find(s => s.BrokenAppointmentStatus.toUpperCase() === "TEXTED"); break;            
          case 'PatientBalancesDetails': tempStatus = this.statuses.find(s => s.PatientBalancesStatus.toUpperCase() === "TEXTED"); break;
          default: return false;
        }
        if(tempStatus)
          return tempStatus.FollowupStatusID;
        else
          return null;
      },

      emailStatusID() {
        let tempStatus;        
        switch(this.detailPageName) {
          case 'OverdueRecallDetails' : tempStatus = this.statuses.find(s => s.OverdueRecallStatus.toUpperCase() === "EMAILED"); break;
          case 'unscheduledPatientDetails': tempStatus = this.statuses.find(s => s.UnscheduledTreatmentStatus.toUpperCase() === "EMAILED"); break;
          case 'BrokenAppointmentDetails': tempStatus = this.statuses.find(s => s.BrokenAppointmentStatus.toUpperCase() === "EMAILED"); break;            
          case 'PatientBalancesDetails': tempStatus = this.statuses.find(s => s.PatientBalancesStatus.toUpperCase() === "EMAILED"); break;
          default: return false;
        }
        if(tempStatus)
          return tempStatus.FollowupStatusID;
        else          
          return null;
      },

      overdueRecallStatuses() {
        if(this.dataToDisplay && this.dataToDisplay[5].length > 0)
          return this.dataToDisplay[5];
      },

      unscheduledTreatmentStatuses() {
        if(this.isUnscheduledTreatmentPage && this.dataToDisplay && this.dataToDisplay[4].length > 0)
          return this.dataToDisplay[4];
      },

      outstandingClaimStatuses() {
        if(this.isOutstandingClaimsPage && this.dataToDisplay && this.dataToDisplay[0].length > 0)
          return this.dataToDisplay[2];
      },

      outstandingPreAuthStatuses() {
        if(this.isOutstandingPreAuthPage && this.dataToDisplay && this.dataToDisplay[0].length > 0)
          return this.dataToDisplay[2];
      },

      processedPreAuthStatuses() {
        if(this.isProcessedPreAuthPage && this.dataToDisplay && this.dataToDisplay[0].length > 0)
          return this.dataToDisplay[3];
      },

      patientBalancesStatuses() {
        if(this.isPatientBalancesPage && this.dataToDisplay && this.dataToDisplay[4].length > 0)
          return this.dataToDisplay[4];
      },

      outstandingClaimsForFamily() {
      if(this.isPatientBalancesPage && this.dataToDisplay && this.dataToDisplay[2].length > 0)
        return this.dataToDisplay[2];
      },

      patientBalance() {
      if(this.isPatientBalancesPage && this.dataToDisplay && this.dataToDisplay[1].length > 0)
        return this.dataToDisplay[1][0].PatientAmount;
      },

      brokenAppointmentStatuses() {
        if(this.isBrokenAppointmentsPage && this.dataToDisplay && this.dataToDisplay[4].length > 0)
          return this.dataToDisplay[4];
      },

      selectedFollowupStatusName() {
        if(this.statuses) {
          console.log('this.statusID', this.statusID);
          console.log('this.smsStatusID', this.smsStatusID);
          console.log('this.emailStatusID', this.emailStatusID);

          //let compareToStatusID = this.statusID === 0 ? this.smsStatusID : this.statusID;

          let compareToStatusID;

          if(this.statusID === 0) {
            if(this.followupMedium.toUpperCase() === 'SMS')
              compareToStatusID =  this.smsStatusID;
            else if (this.followupMedium.toUpperCase() === 'EMAIL')
              compareToStatusID =  this.emailStatusID;
          } else
            compareToStatusID = this.statusID;

          console.log(`compareToStatusID: ${compareToStatusID}`);
          console.log(`this.statuses:`, this.statuses);
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : return this.statuses.filter(s => s.FollowupStatusID === compareToStatusID)[0].OverdueRecallStatus; break;
            case 'unscheduledPatientDetails': return this.statuses.filter(s => s.FollowupStatusID === compareToStatusID)[0].UnscheduledTreatmentStatus; break;
            case 'BrokenAppointmentDetails': return this.statuses.filter(s => s.FollowupStatusID === compareToStatusID)[0].BrokenAppointmentStatus; break;
            case 'AgingDetails': return this.statuses.filter(s => s.FollowupStatusID === compareToStatusID)[0].OutstandingClaimStatus; break;
            case 'OutstandingPreAuthDetails': return this.statuses.filter(s => s.FollowupStatusID === compareToStatusID)[0].OutstandingPreAuthStatus; break;
            case 'ProcessedPreAuthDetails': return this.statuses.filter(s => s.FollowupStatusID === compareToStatusID)[0].ProcessedPreAuthStatus; break;
            case 'PatientBalancesDetails': return this.statuses.filter(s => s.FollowupStatusID === compareToStatusID)[0].PatientBalancesStatus; break;
            default: return null; break;
          }  
        }
      },

      statusPropertyName() {        
        switch(this.detailPageName) {
          case 'OverdueRecallDetails' : return 'OverdueRecallStatusID'; break;
          case 'unscheduledPatientDetails': return 'UnscheduledTreatmentStatusID'; break;
          case 'BrokenAppointmentDetails': return 'BrokenAppointmentStatusID'; break;
          case 'AgingDetails': return 'OutstandingClaimStatusID'; break;
          case 'OutstandingPreAuthDetails': return 'OutstandingPreAuthStatusID'; break;
          case 'ProcessedPreAuthDetails': return 'ProcessedPreAuthStatusID'; break;
          case 'PatientBalancesDetails': return 'PatientBalancesStatusID'; break;
          default: return null; break;
        }
      },

      maxFollowups() {
        if(this.allSettings) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : return this.allSettings.OVERDUE_RECALL_MAX_FOLLOWUPS ? this.allSettings.OVERDUE_RECALL_MAX_FOLLOWUPS.PropertyValue : null; break;
            case 'unscheduledPatientDetails': return this.allSettings.UNSCHEDULED_TREATMENT_MAX_FOLLOWUPS ? this.allSettings.UNSCHEDULED_TREATMENT_MAX_FOLLOWUPS.PropertyValue : null; break;
            case 'BrokenAppointmentDetails': return this.allSettings.BROKEN_APPOINTMENTS_MAX_FOLLOWUPS ? this.allSettings.BROKEN_APPOINTMENTS_MAX_FOLLOWUPS.PropertyValue : null; break;
            case 'AgingDetails': return this.allSettings.OUTSTANDING_CLAIMS_FOLLOWUPS ? this.allSettings.OUTSTANDING_CLAIMS_MAX_FOLLOWUPS.PropertyValue : null; break;
            case 'OutstandingPreAuthDetails': return this.allSettings.OUTSTANDING_PREAUTHS_MAX_FOLLOWUPS ? this.allSettings.OUTSTANDING_PREAUTHS_MAX_FOLLOWUPS.PropertyValue : null; break;
            case 'ProcessedPreAuthDetails': return this.allSettings.PROCESSED_PREAUTHS_MAX_FOLLOWUPS ? this.allSettings.PROCESSED_PREAUTHS_MAX_FOLLOWUPS.PropertyValue : null; break;
            case 'PatientBalancesDetails': return this.allSettings.PATIENT_BALANCES_MAX_FOLLOWUPS ? this.allSettings.PATIENT_BALANCES_MAX_FOLLOWUPS.PropertyValue : null; break;
            default: return null; break;
          } 
        }
      },

      statusUpdatedAtPropertyName() {
        if(this.allSettings) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : return 'OverdueRecallStatusUpdatedAt'; break;
            case 'unscheduledPatientDetails': return 'UnscheduledTreatmentStatusUpdatedAt'; break;
            case 'BrokenAppointmentDetails': return 'BrokenAppointmentStatusUpdatedAt'; break;
            case 'AgingDetails': return 'OutstandingClaimStatusUpdatedAt'; break;
            case 'OutstandingPreAuthDetails': return 'OutstandingPreAuthStatusUpdatedAt'; break;
            case 'ProcessedPreAuthDetails': return 'ProcessedPreAuthStatusUpdatedAt'; break;
            case 'PatientBalancesDetails': return 'PatientBalancesStatusUpdatedAt'; break;
            default: return null; break;
          } 
        }
      },

      isOverdueRecallPage() {
        return this.detailPageName === 'OverdueRecallDetails';
      },

      isUnscheduledTreatmentPage() {
        return this.detailPageName === 'unscheduledPatientDetails';
      },

      isBrokenAppointmentsPage() {
        return this.detailPageName === 'BrokenAppointmentDetails';
      },

      isOutstandingClaimsPage() {
        return this.detailPageName === 'AgingDetails';
      },

      isOutstandingPreAuthPage() {
        return this.detailPageName === 'OutstandingPreAuthDetails';
      },

      isProcessedPreAuthPage() {
        return this.detailPageName === 'ProcessedPreAuthDetails';
      },

      isPatientBalancesPage() {
        return this.detailPageName === 'PatientBalancesDetails';
      },

      isResponsiblePersonMakingAttempt() {
        if(this.responsibleEmployeeID && this.responsibleEmployeeID.length > 0){

          //take care of Outstanding claims or Outstanding PreAuths - they both look at the same indice [1][0] - hence the OR statement
          if ((this.detailPageName === 'AgingDetails' || this.detailPageName === 'OutstandingPreAuthDetails')  && this.dataToDisplay && this.dataToDisplay[1].length > 0 ){
            if (typeof this.dataToDisplay[1][0] === 'object' && this.dataToDisplay[1][0] !== null){
              //now access the object property to see if the claim has been manually assigned
              if (this.dataToDisplay[1][0].EmployeeManuallyAssigned === 1){
                return this.dataToDisplay[1][0].AssignedToEmpID === this.userID;
              }
              else 
                return this.responsibleEmployeeID.includes(this.userID);
            }
          }
          // take care of Outstanding Patient balances - looks at the indice [3][0]
          else if (this.detailPageName === 'PatientBalancesDetails'  && this.dataToDisplay && this.dataToDisplay[3].length > 0 ){
            if (typeof this.dataToDisplay[3][0] === 'object' && this.dataToDisplay[3][0] !== null){
              //now access the object property to see if the pt balance follow-up has been manually assigned
              if (this.dataToDisplay[3][0].EmployeeManuallyAssigned === 1){
                return this.dataToDisplay[3][0].AssignedToEmpID === this.userID;
              }
              else 
                return this.responsibleEmployeeID.includes(this.userID);
            }

          }
          else //if not outstanding claims page
            return this.responsibleEmployeeID.includes(this.userID);
        }
        else 
          return false;

      },

      isAppointmentScheduled() {
        // if the status they selected matches to the one where TeamioFollowupStatusID = 1 then that means appointment is scheduled
        if(this.statuses)
          return this.statuses.filter(s => s.FollowupStatusID === this.statusID)[0].TeamioFollowupStatusID === 1;
      },

      isClaimProcessed() {
      // if the status they selected matches to the one where TeamioFollowupStatusID = 1 then that means appointment is scheduled
      if(this.statuses && this.statusID !== 0)
        return this.statuses.filter(s => s.FollowupStatusID === this.statusID)[0].TeamioFollowupStatusID === 5;
    },

      followupNotRequired() {
        if(this.statuses && this.statusID !== 0)
          return this.statuses.filter(s => s.FollowupStatusID === this.statusID)[0].TeamioFollowupStatusID === 3;
      },

      getTeamioFollowupStatusID() {
        if(this.statuses) {
          if(this.statusID !== 0) {
            if(this.followupMedium.toUpperCase() === 'PHONE')
              return this.statuses.filter(s => s.FollowupStatusID === this.statusID)[0].TeamioFollowupStatusID;
            else if(this.followupMedium.toUpperCase() === 'SMS')
              return this.statuses.filter(s => s.FollowupStatusID === this.smsStatusID)[0].TeamioFollowupStatusID;
            else if (this.followupMedium.toUpperCase() === 'EMAIL')
              return this.statuses.filter(s => s.FollowupStatusID === this.emailStatusID)[0].TeamioFollowupStatusID;
          }
        }
      },

      doNotFollowup() {
        return this.tasksData && this.tasksData.length > 0 && (this.tasksData[0].TeamioFollowupStatusID === 3 || this.tasksData[0].TeamioFollowupStatusID === 1 || this.tasksData[0].TeamioFollowupStatusID === 5 || this.mainParentTaskData.FollowupStopped === 1);
      },

      isLastAttempt() {
        if(this.maxFollowups)
          return this.totalAttemptsMade + 1 === parseInt(this.maxFollowups,10);
      },

      totalAttemptsMade() {
        if(this.mainParentTaskData)
          return this.mainParentTaskData.Attempts;
        else
          return 0;
      },

      currentPatientFromFamily() {
        if(this.patNum !== null && this.familyAppts && this.familyAppts.length > 0) {
          let t = this.familyAppts.filter(p => p.PatNum === parseInt(this.patNum,10))[0];
          return t;
        }
      },

      // does this patient have recall appointment scheduled
      patientHasRecallScheduled() {
        let retVal = false;
        if(this.currentPatientFromFamily && this.currentPatientFromFamily !== null && this.currentPatientFromFamily.AptDateTime) {
          let daysDiff = moment(this.currentPatientFromFamily.AptDateTime).diff(moment(), 'days');
          /* 
            
          */
          if (daysDiff <= 60 && daysDiff >= 0 )
            retVal = true;
        }
        return retVal;
      },

      //does this patient have ANY appointment scheduled within next 30 days including today 
      //if the appointment exists, then it return the appointment, otherwise it return null
      patientHasApptScheduledIn60Days(){
        let ret = null; //false;

        /*the following fails on outstanding claim, outstanding preauth and pt balances pages, but we ONLY need to do it for 
        unscheduled tx page anyways, otherwise we'll just return null;
        */
        if (this.isUnscheduledTreatmentPage){
          if (this.scheduledAppointments && this.scheduledAppointments.length > 0){ // there are scheduled appointments
            //let's iterate through them and as soon as we find one that is within 30 days from today, we return TRUE
            this.scheduledAppointments.some(appointment => {
              // console.log('appointment date time is '+appointment.AptDateTime);
              let daysDiff = moment(appointment.AptDateTime).diff(moment(), 'days'); 
              if (daysDiff <= 60 && daysDiff >= 0){
                ret = appointment.AptDateTime; //true;
                return true; //to exit out of the for loop
              }
              return false; //to continue to next iteration of for loop
            });

          }
          else { // no scheduled appointments
            ret = null;
          }
        } //end of checking if this is for unscheduled tx plage
        
        return ret;

      },

      attemptsAllowed() {
        if(this.allSettings) {

          switch(this.detailPageName) {

            case 'OverdueRecallDetails' : 
                                              if(!this.patientHasRecallScheduled && (!this.tasksData || this.tasksData.length === 0))
                                                  return true;
                                              else           
                                                  return this.tasksData && this.tasksData.length > 0 && !moment(this.tasksData[0][this.statusUpdatedAtPropertyName]).isSame(moment(), 'day');
                                              break;

            case 'unscheduledPatientDetails': 
                                              /*
                                              we are commenting out the following check to see if patient has apt in next 60 days. 
                                              Because if they did, we were not leting them follow-up, but we are removing this check.
                                              */
                                              /*
                                              if(this.patientHasApptScheduledIn60Days)
                                                  return false;
                                              else{
                                              */
                                                  if (!this.tasksData || this.tasksData.length === 0)
                                                    return true;
                                                  else
                                                    return this.tasksData && this.tasksData.length > 0 && !moment(this.tasksData[0][this.statusUpdatedAtPropertyName]).isSame(moment(), 'day');
                                              //}
                                              break;
                                              
            case 'BrokenAppointmentDetails':  
                                              if(!this.tasksData || this.tasksData.length === 0)
                                                return true;
                                              else 
                                                return this.tasksData && this.tasksData.length > 0 && !moment(this.tasksData[0][this.statusUpdatedAtPropertyName]).isSame(moment(), 'day');
                                              break;
            case 'AgingDetails':  
                                              if(!this.tasksData || this.tasksData.length === 0)
                                                return true;
                                              else 
                                                return this.tasksData && this.tasksData.length > 0 && !moment(this.tasksData[0][this.statusUpdatedAtPropertyName]).isSame(moment(), 'day');
                                              break;
            case 'OutstandingPreAuthDetails':  if(!this.tasksData || this.tasksData.length === 0)
                                                return true;
                                              else 
                                                return this.tasksData && this.tasksData.length > 0 && !moment(this.tasksData[0][this.statusUpdatedAtPropertyName]).isSame(moment(), 'day');
                                              break;
            
            case 'ProcessedPreAuthDetails':  if(!this.tasksData || this.tasksData.length === 0)
                                                return true;
                                              else 
                                                return this.tasksData && this.tasksData.length > 0 && !moment(this.tasksData[0][this.statusUpdatedAtPropertyName]).isSame(moment(), 'day');
                                              break;
            
            case 'PatientBalancesDetails':  if(!this.tasksData || this.tasksData.length === 0)
                                                return true;
                                              else 
                                                return this.tasksData && this.tasksData.length > 0 && !moment(this.tasksData[0][this.statusUpdatedAtPropertyName]).isSame(moment(), 'day');
                                              break;
            
            default: return null; break;
          } 
        }
        
      },

      /*
      waitingForUnscheduledTx(){
        if (this.tasksData && this.tasksData.length > 0 && this.tasksData[0].CreatedAutomatically === 1 && this.tasksData[0].FollowupDate === null && this.tasksData[0].PendingUnscheduledTx === 1 && this.tasksData[0].TaskPurged === 0)
          return true;
        else 
          return false;
      },
      */

      followupAllowedToday() {
        let ret;
        if(!this.tasksData || this.tasksData.length === 0)
          ret = true;
        else if (this.isOutstandingClaimsPage || this.isOutstandingPreAuthPage)
          ret = true;
        else
          ret = this.tasksData && this.tasksData.length > 0 && moment(this.tasksData[0].FollowupDate).isSameOrBefore(moment(), 'day');
        return ret;
      },

      maxAttemptsReached() {
        if(this.maxFollowups)
          return this.totalAttemptsMade >= parseInt(this.maxFollowups,10);
      },

      addAttemptClass() {
        return this.isResponsiblePersonMakingAttempt && this.attemptsAllowed && !this.doNotFollowup && this.followupAllowedToday && this.statuses ? 
          'btn mb-xs mr-xs btn-dark fw-semi-bold' : 'btn mb-xs mr-xs btn-dark fw-semi-bold disabled-btn';
      },

      addPlannedAppointmentClass(){
        return this.plannedAppointments && this.plannedAppointments.length > 0 ? 'tab-count-badge tab-count-badge-primary' : 'tab-count-badge tab-count-badge-danger';
      },

      addScheduledAppointmentClass(){
        if (this.isOverdueRecallPage)
          return this.scheduledAppointmentsForRecallPanel && this.scheduledAppointmentsForRecallPanel.length > 0 ? 'tab-count-badge tab-count-badge-primary' : 'tab-count-badge tab-count-badge-danger'
        else if (this.isUnscheduledTreatmentPage)
          return this.scheduledAppointments && this.scheduledAppointments.length > 0 ? 'tab-count-badge tab-count-badge-primary' : 'tab-count-badge tab-count-badge-danger';
      },

      mainParentTaskData() {
        if(this.tasksData)
          return this.tasksData.filter(task => task.ParentTaskID === null)[0];
      },

      dataToDisplay() {
        const r = this.results || this.internalResults;
        if(r && r.data && r.data.length > 0)
          return r.data;
      },

      claimData() {
        if(this.dataToDisplay && this.dataToDisplay[0].length > 0)
          return this.dataToDisplay[0][0];
      },

      patientProcedures() {
        if(this.isProcessedPreAuthPage && this.dataToDisplay && this.dataToDisplay[0].length > 0)
          return this.dataToDisplay[0];
      },

      brokenAppointmentsHistory() {
        if(this.isBrokenAppointmentsPage && this.dataToDisplay && this.dataToDisplay[2].length > 0)
          return this.dataToDisplay[2];
      },

      brokenAppointmentsCount() {
        if(this.isBrokenAppointmentsPage && this.dataToDisplay && this.dataToDisplay[1].length > 0)
          return this.dataToDisplay[1][0].CountOfBrokenAppointments;
      },

      apptReminderPaidFor() {
        if(this.allSettings && this.allSettings.APPT_REMINDER_PAID_FOR && this.allSettings.APPT_REMINDER_PAID_FOR.PropertyValue === 'TRUE')
          return true;
        else
          return false;
      },

      autoRecallReminderActivity() {
        if (this.detailPageName === 'OverdueRecallDetails') {
          if(this.dataToDisplay)
            return this.dataToDisplay[11];
        }
      },

      nextAutoRecallReminderDue() {
        if(this.autoRecallReminderActivity && this.autoRecallReminderActivity[0]) {          
          return this.autoRecallReminderActivity[0].NextReminderDue;
        }
      },

      patientData() {
        let ret;
        if(this.dataToDisplay) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : this.dataToDisplay.length > 0 ? ret = this.dataToDisplay[0][0] : ret = null; break;
            case 'unscheduledPatientDetails': this.dataToDisplay.length > 0 ? ret = this.dataToDisplay[0][0] : ret = null; break;
            case 'BrokenAppointmentDetails': this.dataToDisplay[0].length > 0 ? ret = this.dataToDisplay[0][0] : ret = null; break;
            case 'AgingDetails': this.claimData? ret = this.claimData : ret = null; break;
            case 'OutstandingPreAuthDetails': this.dataToDisplay.length > 0 ? ret = this.dataToDisplay[0][0] : ret = null; break;
            case 'ProcessedPreAuthDetails': this.dataToDisplay.length > 0 ? ret = this.dataToDisplay[0][2] : ret = null; break;
            case 'PatientBalancesDetails': this.dataToDisplay[0].length > 0 ? ret = this.dataToDisplay[0][0] : ret = null; break;
            default: ret = null; break;
          }          
        }
        return ret;
      },

      familyAppts() {
        let ret;
        if(this.patNum !== null && this.dataToDisplay) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'unscheduledPatientDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'AgingDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'OutstandingPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'ProcessedPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            //case 'PatientBalancesDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            default: ret = null; break;
          }          
        }
        return ret;
      },

      familyApptsWithoutCurrentPerson() {
        if(this.patNum !== null && this.familyAppts)
          return this.familyAppts.filter(p => p.PatNum !== parseInt(this.patNum,10));
      },

      patientInsurance() {
        let ret;
        if(this.patNum !== null && this.dataToDisplay) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : this.dataToDisplay[2].length > 0 ? ret = this.dataToDisplay[2] : ret = null; break;
            /*
            case 'unscheduledPatientDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'BrokenAppointmentDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'AgingDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'OutstandingPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'ProcessedPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'PatientBalancesDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            default: ret = null; break;
            */
          }          
        }
        return ret;
      },

      unscheduledTxForFamilyForRecallPanel (){
        let ret;
        if(this.patNum !== null && this.dataToDisplay) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : this.dataToDisplay[12].length > 0 ? ret = this.dataToDisplay[12] : ret = null; break;
            /*
            case 'unscheduledPatientDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'BrokenAppointmentDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'AgingDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'OutstandingPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'ProcessedPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'PatientBalancesDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            default: ret = null; break;
            */
          }          
        }
        return ret;
      },

      unscheduledTxForFamilyForUnscheduledPanel (){
        let ret;
        if(this.patNum !== null && this.dataToDisplay) {
          switch(this.detailPageName) {
            case 'unscheduledPatientDetails' : this.dataToDisplay[13].length > 0 ? ret = this.dataToDisplay[13] : ret = null; break;
            /*
            case 'unscheduledPatientDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'BrokenAppointmentDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'AgingDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'OutstandingPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'ProcessedPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'PatientBalancesDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            default: ret = null; break;
            */
          }          
        }
        return ret;
      },

      familyRecallAppointmentsForUnscheduledPanel(){
        let ret;
        if(this.patNum !== null && this.dataToDisplay) {
          switch(this.detailPageName) {
            case 'unscheduledPatientDetails' : this.dataToDisplay[12].length > 0 ? ret = this.dataToDisplay[12] : ret = null; break;
            /*
            case 'unscheduledPatientDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'BrokenAppointmentDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'AgingDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'OutstandingPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'ProcessedPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'PatientBalancesDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            default: ret = null; break;
            */
          }          
        }
        return ret;  
      },

      pastAppointments(){
       /* let ret;
        if (this.patNum !== null & this.dataToDisplay){
          switch(this.detailPageName){
            case 'OverdueRecallDetails' : this.dataToDisplay[10].length > 0 ? ret = this.dataToDisplay[10] : ret = null; break;
          }
        }
        return ret;
        */
       if(this.dataToDisplay && this.dataToDisplay[10].length > 0)
          return this.dataToDisplay[10];
      },

      patientFamilyInfo() {
        if(this.dataToDisplay && this.dataToDisplay[3].length > 0)
          return this.dataToDisplay[3];
      },

      tasksData() {
        let ret;
        if(this.dataToDisplay) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : this.dataToDisplay[4].length > 0 ? ret = this.dataToDisplay[4] : ret = null; break;
            case 'unscheduledPatientDetails': this.dataToDisplay[3].length > 0 ? ret = this.dataToDisplay[3] : ret = null; break;
            case 'BrokenAppointmentDetails': this.dataToDisplay[3].length > 0 ? ret = this.dataToDisplay[3] : ret = null; break;
            case 'AgingDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'OutstandingPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1] : ret = null; break;
            case 'ProcessedPreAuthDetails': this.dataToDisplay[4].length > 0 ? ret = this.dataToDisplay[4] : ret = null; break;
            case 'PatientBalancesDetails': this.dataToDisplay[3].length > 0 ? ret = this.dataToDisplay[3] : ret = null; break;
            default: ret = null; break;
          }          
        }
        return ret;
      },

      followupActivities() {
        let ret;
        if(this.dataToDisplay) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : this.dataToDisplay[4].length > 0 ? ret = this.dataToDisplay[4].filter(a => a.ParentTaskID !== null) : ret = null; break;
            case 'unscheduledPatientDetails': this.dataToDisplay[3].length > 0 ? ret = this.dataToDisplay[3].filter(a => a.ParentTaskID !== null) : ret = null; break;
            case 'BrokenAppointmentDetails': this.dataToDisplay[3].length > 0 ? ret = this.dataToDisplay[3].filter(a => a.ParentTaskID !== null) : ret = null; break;
            case 'AgingDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1].filter(a => a.ParentTaskID !== null) : ret = null; break;
            case 'OutstandingPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1].filter(a => a.ParentTaskID !== null) : ret = null; break;
            case 'ProcessedPreAuthDetails': this.dataToDisplay[1].length > 0 ? ret = this.dataToDisplay[1].filter(a => a.ParentTaskID !== null) : ret = null; break;
            case 'PatientBalancesDetails': this.dataToDisplay[3].length > 0 ? ret = this.dataToDisplay[3].filter(a => a.ParentTaskID !== null) : ret = null; break;
            default: ret = null; break;
          }          
        }
        return ret;
      },

      unscheduledTreatmentInPlan() {
        if(this.isUnscheduledTreatmentPage && this.dataToDisplay && this.dataToDisplay[5].length > 0)
          return this.dataToDisplay[5];
      },

      pendingTxForRecallPanel() {
        if(this.isOverdueRecallPage && this.dataToDisplay && this.dataToDisplay[14].length > 0)
          return this.dataToDisplay[14];
      },

      scheduledAppointments() {
        if(this.dataToDisplay && this.dataToDisplay[6].length > 0)
          return this.dataToDisplay[6];
      },

      scheduledAppointmentsForRecallPanel() {
        if(this.dataToDisplay && this.dataToDisplay[13].length > 0)
          return this.dataToDisplay[13];
      },

      plannedAppointments() {
        if(this.isUnscheduledTreatmentPage && this.dataToDisplay && this.dataToDisplay[7].length > 0)
          return this.dataToDisplay[7];
      },

      plannedAppointmentsTabTitle(){
        let ret;
        if (this.plannedAppointments && this.plannedAppointments.length > 0)
          ret = this.plannedAppointments.length;
        else
          ret = 0;

        return ret;
      },

      scheduledAppointmentsTabTitle(){
        let ret = 0;

        if (this.isOverdueRecallPage && this.scheduledAppointmentsForRecallPanel && this.scheduledAppointmentsForRecallPanel.length > 0) 
          ret = this.scheduledAppointmentsForRecallPanel.length;

        else if (this.isUnscheduledTreatmentPage && this.scheduledAppointments && this.scheduledAppointments.length > 0)
          ret = this.scheduledAppointments.length;
        
        return ret;
      },

      upcomingFollowups() {
        let ret;
        if(this.dataToDisplay) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : this.dataToDisplay[6].length > 0 ? ret = this.dataToDisplay[6] : ret = null; break;
            case 'unscheduledPatientDetails': this.dataToDisplay[8].length > 0 ? ret = this.dataToDisplay[8] : ret = null; break;
            case 'BrokenAppointmentDetails': this.dataToDisplay[5].length > 0 ? ret = this.dataToDisplay[5] : ret = null; break;
            case 'AgingDetails': this.dataToDisplay[6].length > 0 ? ret = this.dataToDisplay[6] : ret = null; break;
            case 'OutstandingPreAuthDetails': this.dataToDisplay[6].length > 0 ? ret = this.dataToDisplay[6] : ret = null; break;
            case 'ProcessedPreAuthDetails': this.dataToDisplay[4].length > 0 ? ret = this.dataToDisplay[4] : ret = null; break;
            //case 'PatientBalancesDetails': this.dataToDisplay[7].length > 0 ? ret = this.dataToDisplay[7] : ret = null; break;
            default: ret = null; break;
          }          
        }
        return ret;
      },

      /* this one below is probably wrong and can be removed. Right one is the one below that called patientList */
      lists() {
        let ret;
        if(this.dataToDisplay) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : this.dataToDisplay[7].length > 0 ? ret = this.dataToDisplay[7] : ret = null; break;
            case 'unscheduledPatientDetails': this.dataToDisplay[10].length > 0 ? ret = this.dataToDisplay[10] : ret = null; break;
            case 'AgingDetails': this.dataToDisplay[6].length > 0 ? ret = this.dataToDisplay[6] : ret = null; break;
            case 'OutstandingPreAuthDetails': this.dataToDisplay[6].length > 0 ? ret = this.dataToDisplay[6] : ret = null; break;
            case 'ProcessedPreAuthDetails': this.dataToDisplay[5].length > 0 ? ret = this.dataToDisplay[5] : ret = null; break;
            default: ret = null; break;
          }          
        }
        return ret;
      },

      patientList() {
        let ret;
        if(this.dataToDisplay) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : this.dataToDisplay[8].length > 0 ? ret = this.dataToDisplay[8] : ret = null; break;
            case 'unscheduledPatientDetails': this.dataToDisplay[10].length > 0 ? ret = this.dataToDisplay[10] : ret = null; break;
            case 'AgingDetails': this.dataToDisplay[6].length > 0 ? ret = this.dataToDisplay[6] : ret = null; break;
            case 'OutstandingPreAuthDetails': this.dataToDisplay[6].length > 0 ? ret = this.dataToDisplay[6] : ret = null; break;
            case 'ProcessedPreAuthDetails': this.dataToDisplay[6].length > 0 ? ret = this.dataToDisplay[6] : ret = null; break;
            //case 'PatientBalancesDetails': this.dataToDisplay[8].length > 0 ? ret = this.dataToDisplay[8] : ret = null; break;
            default: ret = null; break;
          }          
        }
        return ret;
      },

      commlog() {
        let ret;
        if(this.dataToDisplay) {
          switch(this.detailPageName) {
            case 'OverdueRecallDetails' : this.dataToDisplay[9].length > 0 ? ret = this.dataToDisplay[9] : ret = null; break;
            case 'unscheduledPatientDetails': this.dataToDisplay[11].length > 0 ? ret = this.dataToDisplay[11] : ret = null; break;
            case 'BrokenAppointmentDetails': this.dataToDisplay[6].length > 0 ? ret = this.dataToDisplay[6] : ret = null; break;
            case 'AgingDetails': this.dataToDisplay[5].length > 0 ? ret = this.dataToDisplay[5] : ret = null; break;
            case 'OutstandingPreAuthDetails': this.dataToDisplay[5].length > 0 ? ret = this.dataToDisplay[5] : ret = null; break;
            case 'ProcessedPreAuthDetails': this.dataToDisplay[7].length > 0 ? ret = this.dataToDisplay[7] : ret = null; break;
            case 'PatientBalancesDetails': this.dataToDisplay[6].length > 0 ? ret = this.dataToDisplay[6] : ret = null; break;
            default: ret = null; break;
          }          
        }
        return ret;
      },

      existingClaimStatusHistory() {
        let ret;
        if (this.dataToDisplay && this.dataToDisplay[6].length > 0 && this.isOutstandingClaimsPage){
            ret = this.dataToDisplay[6];
        }
        else{
          ret = null;
        }
        return ret;
      },

      // Feature-level detection (separate from patient data) — src/utils/siteCapability.js.
      // These drive the "why is this disabled" copy, so they are default-deny (unlike the
      // shouldEnable* gates above, which fail open while settings load). Both now return a
      // real boolean; the old expressions could yield undefined.
      smsFollowupFeatureEnabled() {
        return isSmsEnabled(this.allSettings);
      },

      // WhatsApp-only office (SMS_OUTBOUND_DISABLED): a number, but SMS is off on purpose — the
      // popover gives that reason instead of "request activation" (and no upsell).
      smsOutboundOff() {
        return hasSmsNumber(this.allSettings) && !isSmsEnabled(this.allSettings);
      },

      emailFollowupFeatureEnabled() {
        return isEmailEnabled(this.allSettings);
      },

      // Dynamic reasons why SMS followup is disabled
      smsFollowupDisabledReasons() {
        const reasons = [];
        const followupType = this.getFollowupType();
        
        // Feature-level issues (these show the request-to-enable button)
        if (!this.smsFollowupFeatureEnabled && this.smsOutboundOff) {
          reasons.push({ icon: 'fa-ban', text: SMS_DISABLED_MESSAGE, isFeatureIssue: true });
        } else if (!this.smsFollowupFeatureEnabled) {
          reasons.push({
            icon: 'fa-exclamation-triangle',
            text: 'SMS followup feature is not enabled for your account. Request activation to send SMS follow-ups to patients.',
            isFeatureIssue: true
          });
        }
        
        // Patient-level issues — opt-out via the shared rule (texted STOP, OD Text-OK = No,
        // or a server/HQ block). The rendered message matches the click guard and the
        // backend refusal, so the popover, the button and the send all agree.
        const block = getTextBlockReason({ patient: this.patientData, timezone: this.smsOptOutTimezone });
        if (block.blocked) {
          reasons.push({
            icon: 'fa-user-times',
            text: block.message,
            isFeatureIssue: false
          });
        }
        if (!this.patientData.WirelessPhone || this.patientData.WirelessPhone === '') {
          reasons.push({
            icon: 'fa-mobile',
            text: 'Patient does not have a cell phone number. Add a wireless number in your practice management system.',
            isFeatureIssue: false
          });
        }
        
        // Context restrictions
        if (followupType === 'Outstanding Claim' || followupType === 'Outstanding PreAuth') {
          reasons.push({
            icon: 'fa-ban',
            text: 'SMS follow-ups are not allowed for Outstanding Claims and Outstanding Pre-Authorizations.',
            isFeatureIssue: false
          });
        }
        
        return reasons;
      },

      // Dynamic reasons why Email followup is disabled
      emailFollowupDisabledReasons() {
        const reasons = [];
        const followupType = this.getFollowupType();
        
        // Feature-level issues (these show the request-to-enable button)
        if (!this.emailFollowupFeatureEnabled) {
          reasons.push({
            icon: 'fa-exclamation-triangle',
            text: 'Email followup feature is not enabled for your account. Request activation to send professional email follow-ups.',
            isFeatureIssue: true
          });
        }
        
        // Patient-level issues
        if (!this.patientData.Email || this.patientData.Email === '') {
          reasons.push({
            icon: 'fa-envelope',
            text: 'Patient does not have an email address. Add an email address in your practice management system.',
            isFeatureIssue: false
          });
        }
        
        // Context restrictions
        if (followupType === 'Outstanding Claim' || followupType === 'Outstanding PreAuth') {
          reasons.push({
            icon: 'fa-ban',
            text: 'Email follow-ups are not allowed for Outstanding Claims and Outstanding Pre-Authorizations.',
            isFeatureIssue: false
          });
        }
        
        return reasons;
      },
    },

    watch: {
      tasksLoaded(newValue, oldValue) {        
        //console.log(`watch changed. Old value: ${JSON.stringify(oldValue)}, \n New value: ${JSON.stringify(newValue)}`);
        this.taggedTasks = this.getTasksTaggedWithPatient(this.patNum);
        /*
        if(!oldValue) {
          this.init();
        } 
        */
      }
    }    
}
</script>

<style lang='scss' scoped>

  .post-comments .comment-body {
    overflow: visible;
  }

  .featureNotEnabled {
    font-weight: bold;
    color: rgb(226, 15, 15);
  }

  /* SMS Short URL Preview Note */
  .sms-short-url-note {
    margin-top: 10px;
    padding: 10px 14px;
    background-color: #fff3f3;
    border: 1px solid #e74c3c;
    border-left: 4px solid #e74c3c;
    border-radius: 4px;
    font-size: 13px;
    color: #c0392b;
    line-height: 1.5;
  }

  .sms-short-url-note strong {
    color: #e74c3c;
  }

  .sms-preview-text {
    display: block;
    margin-top: 6px;
    font-style: italic;
    color: #555;
    word-break: break-word;
  }

  /* SMS Sender Info Banner */
  .sms-sender-info {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border: 1px solid #dee2e6;
    border-left: 4px solid #17a2b8;
    border-radius: 6px;
    padding: 12px 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .sms-sender-info .fa-mobile-phone {
    color: #17a2b8 !important;
  }

  /* CSS for feature help popovers */
  .feature-help-popover {
    min-width: 350px !important;
    max-width: 450px !important;
    width: auto !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    text-orientation: mixed !important;
    writing-mode: horizontal-tb !important;
  }

  .feature-help-popover .popover-body {
    min-width: 300px !important;
    width: 100% !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    text-orientation: mixed !important;
    writing-mode: horizontal-tb !important;
  }

  .feature-help-popover .popover-body * {
    white-space: normal !important;
    word-wrap: break-word !important;
    text-orientation: mixed !important;
    writing-mode: horizontal-tb !important;
  }

  .feature-help-popover .help_popover {
    min-width: 300px !important;
    width: 100% !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    text-orientation: mixed !important;
    writing-mode: horizontal-tb !important;
  }

  .feature-help-popover .help_popover * {
    white-space: normal !important;
    word-wrap: break-word !important;
    text-orientation: mixed !important;
    writing-mode: horizontal-tb !important;
  }

  /* Global popover overrides */
  .popover {
    min-width: 300px !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    text-orientation: mixed !important;
    writing-mode: horizontal-tb !important;
  }

  .popover-body {
    white-space: normal !important;
    word-wrap: break-word !important;
    text-orientation: mixed !important;
    writing-mode: horizontal-tb !important;
  }

  .popover-body * {
    white-space: normal !important;
    word-wrap: break-word !important;
    text-orientation: mixed !important;
    writing-mode: horizontal-tb !important;
  }

  /* ========================================
     Modern Followup Panel Styles
     ======================================== */

  .followup-panel {
    font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
    color: #334155;
    background-color: #f8fafc;
    min-height: 100%;
  }

  /* Error State */
  .followup-error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    text-align: center;
  }

  .error-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #fee2e2, #fecaca);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;

    i {
      font-size: 2.5rem;
      color: #dc2626;
    }
  }

  .error-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 0.75rem;
  }

  .error-message {
    color: #64748b;
    margin-bottom: 0.5rem;
  }

  .error-hint {
    color: #10b981;
    font-weight: 500;
    margin-bottom: 1.5rem;
  }

  /* Header */
  .followup-header {
    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
    padding: 1rem 1.25rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
  }

  .followup-header-content {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }

  .followup-header-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 1.25rem;
      color: white;
    }
  }

  .followup-header-text {
    display: flex;
    flex-direction: column;
  }

  .followup-title {
    color: white;
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0;
    line-height: 1.3;
  }

  .followup-subtitle {
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.8125rem;
  }

  .followup-close-btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }

    i {
      font-size: 1rem;
    }
  }

  /* Content Area */
  .followup-content {
    padding: 1rem;
  }

  /* Patient Info Card */
  .patient-info-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
    padding: 1.25rem;
    margin-bottom: 1rem;
  }

  .patient-header {
    display: flex;
    gap: 1rem;
  }

  .patient-avatar {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: linear-gradient(135deg, #e0e7ff, #c7d2fe);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 1.5rem;
      color: #4f46e5;
    }
  }

  .patient-details {
    flex: 1;
    min-width: 0;
  }

  .patient-name-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .patient-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #0f172a;
    text-decoration: none;

    &:hover {
      color: #3b82f6;
    }
  }

  .patient-demographics {
    color: #64748b;
    font-size: 0.875rem;
  }

  .premed-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: linear-gradient(135deg, #dc2626, #b91c1c);
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.25rem 0.625rem;
    border-radius: 6px;

    i {
      font-size: 0.6875rem;
    }
  }

  /* Contact Pills */
  .contact-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .contact-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: #f1f5f9;
    color: #475569;
    font-size: 0.8125rem;
    padding: 0.375rem 0.75rem;
    border-radius: 20px;
    transition: all 0.15s ease;

    i {
      color: #64748b;
      font-size: 0.875rem;
    }

    &:hover {
      background: #e2e8f0;
    }
  }

  .contact-pill-email {
    i {
      color: #3b82f6;
    }
  }

  .patient-address {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    color: #64748b;
    font-size: 0.8125rem;
    margin-top: 0.375rem;

    i {
      color: #94a3b8;
      margin-top: 2px;
    }
  }

  /* Status Indicators */
  .status-indicators {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
  }

  .status-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .status-balance {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    border: 1px solid #fcd34d;
  }

  .status-recall {
    background: linear-gradient(135deg, #fee2e2, #fecaca);
    border: 1px solid #fca5a5;
  }

  .status-scheduled {
    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
    border: 1px solid #86efac;
  }

  .status-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #64748b;
  }

  .status-value {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #0f172a;
    margin-left: auto;
  }

  /* Actions Card */
  .followup-actions-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
    padding: 1.25rem;
    margin-bottom: 1rem;
  }

  .actions-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .actions-title {
    font-size: 1rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    i {
      color: #3b82f6;
    }
  }

  .attempts-counter {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    background: #f1f5f9;
    padding: 0.5rem 0.875rem;
    border-radius: 20px;
  }

  .attempts-current {
    font-size: 1.125rem;
    font-weight: 700;
    color: #10b981;
  }

  .attempts-divider {
    color: #94a3b8;
    font-size: 0.8125rem;
  }

  .attempts-max {
    font-size: 1.125rem;
    font-weight: 700;
    color: #3b82f6;
  }

  .attempts-label {
    color: #64748b;
    font-size: 0.8125rem;
    margin-left: 0.25rem;
  }

  /* Action Buttons Grid */
  .action-buttons-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.75rem;
  }

  .action-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1rem 0.75rem;
    border-radius: 10px;
    border: 2px solid transparent;
    cursor: pointer;
    transition: all 0.2s ease;
    font-weight: 500;
    font-size: 0.8125rem;
    position: relative;

    i {
      font-size: 1.25rem;
    }

    span {
      text-align: center;
    }
  }

  .action-btn-phone {
    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
    color: #166534;
    border-color: #86efac;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #bbf7d0, #86efac);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(34, 197, 94, 0.25);
    }
  }

  .action-btn-sms {
    background: linear-gradient(135deg, #e0f2fe, #bae6fd);
    color: #0369a1;
    border-color: #7dd3fc;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #bae6fd, #7dd3fc);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(14, 165, 233, 0.25);
    }
  }

  .action-btn-email {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    color: #92400e;
    border-color: #fcd34d;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #fde68a, #fcd34d);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(245, 158, 11, 0.25);
    }
  }

  .action-btn-payment {
    background: linear-gradient(135deg, #e0e7ff, #c7d2fe);
    color: #4338ca;
    border-color: #a5b4fc;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #c7d2fe, #a5b4fc);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
    }
  }

  .action-btn-disabled,
  .action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }

  .help-icon {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    color: #f59e0b;
    font-size: 0.875rem !important;
  }

  /* Alert Cards */
  .followup-alerts {
    margin-top: 1rem;
  }

  .alert-card {
    display: flex;
    gap: 0.875rem;
    padding: 1rem;
    border-radius: 10px;
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .alert-icon {
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .alert-content {
    flex: 1;

    strong {
      display: block;
      font-size: 0.9375rem;
      margin-bottom: 0.25rem;
    }

    p {
      margin: 0;
      font-size: 0.8125rem;
      opacity: 0.9;
    }
  }

  .alert-list {
    margin: 0.5rem 0 0 1rem;
    padding: 0;
    font-size: 0.8125rem;

    li {
      margin-bottom: 0.25rem;
    }
  }

  .alert-card-danger {
    background: linear-gradient(135deg, #fee2e2, #fecaca);
    border: 1px solid #fca5a5;

    .alert-icon {
      color: #dc2626;
    }

    .alert-content {
      color: #991b1b;
    }
  }

  .alert-card-warning {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    border: 1px solid #fcd34d;

    .alert-icon {
      color: #d97706;
    }

    .alert-content {
      color: #92400e;
    }
  }

  .alert-card-success {
    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
    border: 1px solid #86efac;

    .alert-icon {
      color: #16a34a;
    }

    .alert-content {
      color: #166534;
    }
  }

  /* Followup Buttons */
  .followup-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .followup-btn-primary {
    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
    color: white;

    &:hover {
      background: linear-gradient(135deg, #2563eb, #1e40af);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35);
    }
  }

  .followup-btn-secondary {
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;

    &:hover {
      background: #e2e8f0;
    }
  }

  .followup-btn-success {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;

    &:hover {
      background: linear-gradient(135deg, #059669, #047857);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35);
    }
  }

  /* Status Info Style */
  .status-info {
    background: #f8fafc;
    border: 1px solid #e2e8f0;

    .status-icon {
      color: #64748b;
    }
  }

  .status-icon {
    font-size: 1rem;
    flex-shrink: 0;
  }

  /* Attempts Counter - Improved */
  .attempts-counter {
    display: inline-flex;
    align-items: baseline;
    gap: 0.25rem;
    background: linear-gradient(135deg, #f0fdf4, #dcfce7);
    border: 1px solid #86efac;
    border-radius: 20px;
    padding: 0.375rem 0.875rem;
  }

  .attempts-current {
    font-size: 1.125rem;
    font-weight: 700;
    color: #15803d;
  }

  .attempts-separator {
    font-size: 0.875rem;
    font-weight: 500;
    color: #86efac;
  }

  .attempts-max {
    font-size: 1.125rem;
    font-weight: 700;
    color: #15803d;
  }

  .attempts-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #166534;
    margin-left: 0.25rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  /* New Action Buttons Row Style */
  .action-buttons-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  /* Unified Follow-up Action Button */
  .followup-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.875rem;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: 500;
    transition: all 0.2s ease;
    white-space: nowrap;
    background: #10b981;
    color: white;

    i:first-child {
      font-size: 0.6875rem;
    }

    &:hover:not(:disabled):not(.followup-action-btn-disabled) {
      background: #059669;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
    }
  }

  .followup-action-btn-disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: #10b981;

    &:hover {
      transform: none !important;
      box-shadow: none !important;
      background: #10b981 !important;
    }
  }

  .btn-help-icon {
    font-size: 0.875rem;
    margin-left: 0.125rem;
    opacity: 0.85;
  }

  /* Form Card Styles */
  .followup-form-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
    margin-top: 1rem;
    overflow: hidden;
  }

  .form-card-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: #f8fafc;
    border-bottom: 1px solid #e5e7eb;
  }

  .form-card-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 1.25rem;
      color: white;
    }
  }

  .form-card-icon-phone {
    background: linear-gradient(135deg, #10b981, #059669);
  }

  .form-card-icon-sms {
    background: linear-gradient(135deg, #f59e0b, #d97706);
  }

  .form-card-icon-email {
    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  }

  .form-card-title {
    h5 {
      margin: 0 0 0.125rem 0;
      font-size: 1rem;
      font-weight: 600;
      color: #0f172a;
    }

    span {
      font-size: 0.8125rem;
      color: #64748b;
    }
  }

  .form-info-banner {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.75rem 1.25rem;
    background: linear-gradient(135deg, #eff6ff, #dbeafe);
    border-bottom: 1px solid #bfdbfe;

    i {
      color: #3b82f6;
      font-size: 1.125rem;
    }

    span {
      font-size: 0.8125rem;
      color: #1e40af;
    }
  }

  .form-card-body {
    padding: 1.25rem;
  }

  .form-field {
    margin-bottom: 1.25rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .form-field-label {
    display: block;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .form-field-select {
    width: 100%;
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    color: #374151;
    background-color: white;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
    }
  }

  .form-field-input {
    width: 100%;
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
  }

  .form-field-textarea {
    width: 100%;
    padding: 0.75rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    color: #374151;
    resize: vertical;
    min-height: 100px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
    }

    &::placeholder {
      color: #9ca3af;
    }
  }

  .form-field-hint {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0.5rem 0 0 0;
  }

  .form-field-warning {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: #fef3c7;
    border: 1px solid #fcd34d;
    border-radius: 8px;
    color: #92400e;
    font-size: 0.8125rem;

    i {
      color: #d97706;
    }

    a {
      color: #b45309;
      font-weight: 500;
    }
  }

  .form-checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    color: #6b7280;
    cursor: pointer;
    margin-top: 0.75rem;

    input[type="checkbox"] {
      width: 16px;
      height: 16px;
      accent-color: #3b82f6;
    }
  }

  .date-field-wrapper {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .date-display {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.875rem;
    background: #f1f5f9;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #334155;

    i {
      color: #10b981;
    }
  }

  .override-link {
    font-size: 0.8125rem;
    color: #3b82f6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .help-tooltip {
    i {
      color: #f59e0b;
      font-size: 1rem;
      cursor: help;
    }
  }

  .form-card-actions {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    background: #f8fafc;
    border-top: 1px solid #e5e7eb;
  }

  .btn-form {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1.5rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-form-success {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #059669, #047857);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .btn-form-cancel {
    background: white;
    color: #64748b;
    border: 1px solid #e2e8f0;

    &:hover {
      background: #f1f5f9;
      color: #475569;
    }
  }

  /* Tabs Container - Modern Card Design */
  .followup-tabs-container {
    margin-top: 1.5rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
    overflow: hidden;
  }

  .followup-tabs {
    /* Tab Navigation - Segmented Control Style */
    .nav-tabs,
    > .nav {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
      padding: 0.75rem;
      margin: 0;
      background: #f8fafc;
      border-bottom: 1px solid #e5e7eb;
      list-style: none;

      .nav-item {
        margin: 0;
      }

      .nav-link {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        border: 1px solid transparent;
        border-radius: 6px;
        padding: 0.5rem 0.875rem;
        font-size: 0.8125rem;
        font-weight: 500;
        color: #64748b;
        white-space: nowrap;
        transition: all 0.15s ease;
        background: transparent;
        text-decoration: none;

        &:hover {
          color: #475569;
          background: white;
          border-color: #e2e8f0;
        }

        &.active {
          color: #1e40af;
          background: white;
          border-color: #e2e8f0;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          font-weight: 600;
        }

        /* Badge styling inside tabs */
        .badge {
          font-size: 0.625rem;
          padding: 0.125rem 0.375rem;
          border-radius: 10px;
          font-weight: 600;
          min-width: 16px;
          text-align: center;
        }

        .badge-pill {
          background: #ef4444;
          color: white;
        }
      }
    }

    /* Tab Content */
    .tab-content {
      padding: 1rem;
      background: white;

      .tab-pane {
        animation: fadeIn 0.15s ease;
      }

      /* Tables inside tabs - Clean Modern Design */
      .table {
        width: 100%;
        margin-bottom: 0;
        font-size: 0.875rem;
        border-collapse: collapse;

        thead {
          th {
            font-weight: 600;
            color: #374151;
            background: #f8fafc;
            border-bottom: 1px solid #e2e8f0;
            border-top: none;
            padding: 0.75rem 1rem;
            text-transform: uppercase;
            font-size: 0.75rem;
            letter-spacing: 0.3px;
            text-align: left;
          }
        }

        tbody {
          td {
            padding: 0.75rem 1rem;
            vertical-align: middle;
            border-bottom: 1px solid #f1f5f9;
            color: #374151;
            font-size: 0.875rem;
            line-height: 1.5;
          }

          tr:hover td {
            background: #fafbfc;
          }

          tr:last-child td {
            border-bottom: none;
          }
        }
      }

      /* Three column tables - equal spacing */
      .table-three-cols {
        th:nth-child(1), td:nth-child(1) { width: 40%; }
        th:nth-child(2), td:nth-child(2) { width: 25%; }
        th:nth-child(3), td:nth-child(3) { width: 35%; }
      }

      /* Four column tables */
      .table-four-cols {
        th:nth-child(1), td:nth-child(1) { width: 30%; }
        th:nth-child(2), td:nth-child(2) { width: 25%; }
        th:nth-child(3), td:nth-child(3) { width: 20%; }
        th:nth-child(4), td:nth-child(4) { width: 25%; }
      }

      /* VueTable styles override */
      .VueTables {
        .table-responsive {
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        /* Table styling for VueTables */
        table.table {
          tbody td {
            padding: 0.5rem 0.75rem !important;
            vertical-align: middle;
            line-height: 1.4;
            font-size: 0.875rem !important;
            color: #374151 !important;
          }
          thead th {
            padding: 0.625rem 0.75rem !important;
            font-size: 0.75rem !important;
            color: #374151 !important;
          }
        }

        .VueTables__search-field {
          margin-bottom: 1rem;

          input {
            border-radius: 6px;
            border: 1px solid #d1d5db;
            padding: 0.5rem 0.75rem;
            font-size: 0.875rem;

            &:focus {
              outline: none;
              border-color: #3b82f6;
              box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
            }
          }
        }

        .VueTables__limit-field {
          select {
            border-radius: 6px;
            border: 1px solid #d1d5db;
            padding: 0.375rem 0.5rem;
            font-size: 0.8125rem;
          }
        }

        .VuePagination {
          margin-top: 1rem;

          .pagination {
            justify-content: center;
            gap: 0.25rem;

            .page-item {
              .page-link {
                border: 1px solid #e5e7eb;
                border-radius: 6px;
                padding: 0.375rem 0.75rem;
                font-size: 0.8125rem;
                color: #64748b;

                &:hover {
                  background: #f1f5f9;
                  border-color: #d1d5db;
                }
              }

              &.active .page-link {
                background: #3b82f6;
                border-color: #3b82f6;
                color: white;
              }
            }
          }
        }
      }

      /* No data message */
      span:only-child {
        display: block;
        text-align: center;
        padding: 2rem 1rem;
        color: #94a3b8;
        font-size: 0.875rem;
      }
    }

    /* Card styles removed - using clean layout */
    .card {
      border: none;
      background: transparent;
    }

    .card-header {
      display: none;
    }

    .card-body {
      padding: 0;
    }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* Communication Type Tags - Compact pill style matching TaskManager */
  /* Using ::v-deep to penetrate scoped styles into VueTables */
  ::v-deep .type-col {
    width: 70px !important;
    text-align: center !important;
  }

  ::v-deep .comm-type-tag {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.5rem;
    background-color: #e8f4ea !important;
    border-radius: 10rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #3d8b4f;
    white-space: nowrap;
    line-height: 1.2;

    i {
      font-size: 0.625rem;
      color: #3d8b4f;
    }
  }


  /* Early Follow-up Wizard */
  .early-followup-wizard {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #93c5fd;
    overflow: hidden;
  }

  .wizard-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
    border-bottom: 1px solid #93c5fd;

    .wizard-icon {
      font-size: 1.5rem;
      color: #2563eb;
    }

    .wizard-title {
      strong {
        display: block;
        font-size: 1rem;
        color: #1e40af;
        margin-bottom: 0.25rem;
      }

      p {
        margin: 0;
        font-size: 0.8125rem;
        color: #1d4ed8;
      }
    }
  }

  .wizard-steps {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    padding: 1rem 1.25rem;
    background: #eff6ff;
    border-bottom: 1px solid #dbeafe;
  }

  .wizard-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;

    .step-number {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #e5e7eb;
      color: #6b7280;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 0.875rem;
      transition: all 0.2s ease;
    }

    .step-label {
      font-size: 0.75rem;
      font-weight: 500;
      color: #6b7280;
      transition: all 0.2s ease;
    }

    &.active {
      .step-number {
        background: #3b82f6;
        color: white;
      }
      .step-label {
        color: #1e40af;
        font-weight: 600;
      }
    }

    &.completed {
      .step-number {
        background: #10b981;
        color: white;
      }
      .step-label {
        color: #059669;
      }
    }
  }

  .wizard-step-connector {
    width: 60px;
    height: 2px;
    background: #e5e7eb;
    margin: 0 0.5rem;
    margin-bottom: 1.25rem;
    transition: all 0.2s ease;

    &.active {
      background: #10b981;
    }
  }

  .wizard-content {
    padding: 1.25rem;
  }

  .wizard-step-card {
    h4 {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin: 0 0 0.5rem 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: #374151;

      i {
        color: #3b82f6;
      }
    }

    p {
      margin: 0 0 1rem 0;
      font-size: 0.8125rem;
      color: #6b7280;
    }

    &.wizard-step-card-success {
      h4 i {
        color: #10b981;
      }
    }
  }

  .wizard-date-options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .wizard-date-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 1rem 1.5rem;
    background: white;
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.15s ease;

    i {
      font-size: 1.25rem;
      color: #6b7280;
    }

    span {
      font-size: 0.875rem;
      font-weight: 600;
      color: #374151;
    }

    small {
      font-size: 0.75rem;
      color: #9ca3af;
    }

    &:hover {
      border-color: #3b82f6;
      background: #eff6ff;

      i {
        color: #3b82f6;
      }
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .wizard-date-btn-primary {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    border-color: #3b82f6;

    i, span {
      color: white;
    }

    small {
      color: rgba(255, 255, 255, 0.8);
    }

    &:hover {
      background: linear-gradient(135deg, #2563eb, #1d4ed8);
      border-color: #2563eb;
    }
  }

  .wizard-date-divider {
    display: flex;
    align-items: center;
    gap: 1rem;

    &::before,
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: #e5e7eb;
    }

    span {
      font-size: 0.75rem;
      color: #9ca3af;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .wizard-date-picker {
    .wizard-date-input {
      width: 100%;
      padding: 0.625rem 0.875rem;
      font-size: 0.875rem;
      border: 2px solid #e5e7eb;
      border-radius: 8px;
      background: white;

      &:focus {
        outline: none;
        border-color: #d97706;
        box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.15);
      }
    }
  }

  .wizard-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem;
    margin-top: 1rem;
    background: #f3f4f6;
    border-radius: 8px;
    font-size: 0.8125rem;
    color: #6b7280;

    i {
      color: #d97706;
    }
  }

  .wizard-action-buttons {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .wizard-action-btn {
    flex: 1;
    min-width: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    padding: 1rem;
    background: white;
    border: 2px solid #10b981;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.15s ease;

    i {
      font-size: 1.25rem;
      color: #10b981;
    }

    span {
      font-size: 0.8125rem;
      font-weight: 600;
      color: #374151;
    }

    &:hover {
      background: #ecfdf5;
      border-color: #059669;
    }

    &:disabled,
    &.wizard-action-btn-disabled {
      opacity: 0.5;
      cursor: not-allowed;
      border-color: #d1d5db;
      background: #f9fafb;

      i {
        color: #9ca3af;
      }

      &:hover {
        background: #f9fafb;
        border-color: #d1d5db;
      }
    }

    .wizard-btn-hint {
      font-size: 0.6875rem;
      color: #9ca3af;
      font-weight: 400;
    }
  }

  .wizard-back-link {
    margin-top: 1rem;
    text-align: center;

    a {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.8125rem;
      color: #6b7280;
      text-decoration: none;

      &:hover {
        color: #374151;
      }
    }
  }

  /* Status Tags - Compact pill style like "Apt on Schedule" badge */
  .status-tag {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.5rem;
    border-radius: 10rem;
    font-size: 0.6875rem;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1.2;

    i {
      font-size: 0.625rem;
    }
  }

  .status-tag-pending {
    background: #fef3c7;
    color: #92400e;

    i {
      color: #d97706;
    }
  }

  .status-tag-success {
    background: #dcfce7;
    color: #166534;

    i {
      color: #16a34a;
    }
  }

  .status-tag-default {
    background: #f1f5f9;
    color: #475569;
  }

  /* Empty State Message */
  .empty-state-message {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.8125rem;
    color: #64748b;

    i {
      color: #94a3b8;
    }
  }

  .form-actions {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
    padding-top: 1rem;
  }

  /* AI Call button accent */
  .followup-action-btn-ai {
    border-left: 3px solid #6366f1;
  }

  /* AI Call Result Modal Overlay */
  .ai-call-result-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 10500;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ai-call-result-modal {
    background: #fff;
    border-radius: 0.75rem;
    width: 520px;
    max-width: 90vw;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }

  .ai-call-result-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;

    h5 {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
    }

    .close {
      font-size: 1.5rem;
      opacity: 0.6;
      &:hover { opacity: 1; }
    }
  }

  .ai-call-result-meta {
    padding: 0.75rem 1.25rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .ai-call-result-transcript {
    flex: 1;
    overflow-y: auto;
    padding: 1rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .transcript-msg {
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    max-width: 85%;
  }

  .transcript-agent {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    align-self: flex-start;

    .transcript-role { color: #2563eb; }
  }

  .transcript-patient {
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    align-self: flex-end;

    .transcript-role { color: #6b7280; }
  }

  .transcript-role {
    display: block;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 0.15rem;
  }

  .transcript-text {
    font-size: 0.875rem;
    line-height: 1.4;
  }

  .ai-call-result-footer {
    padding: 0.75rem 1.25rem;
    border-top: 1px solid #e5e7eb;
    text-align: right;
  }

  /* Collect Payment — method chooser & terminal sub-form
     Match the followup-form-card pattern used by Phone/SMS/Email forms. */

  .form-card-icon-payment {
    background: linear-gradient(135deg, #14b8a6, #0d9488);
  }
  .form-card-icon-terminal {
    background: linear-gradient(135deg, #6366f1, #4f46e5);
  }

  .payment-method-buttons {
    display: flex;
    gap: 0.875rem;
    flex-wrap: wrap;
  }
  .payment-method-btn {
    flex: 1 1 200px;
    min-height: 156px;
    padding: 1.25rem 1rem;
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, transform 0.05s, box-shadow 0.15s;
    text-align: center;

    &:hover {
      border-color: #6366f1;
      background: #f5f3ff;
      box-shadow: 0 1px 3px rgba(99, 102, 241, 0.15);
    }
    &:active { transform: scale(0.98); }
  }
  .payment-method-btn-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.875rem;
    i { font-size: 1.4rem; color: white; }
  }
  .payment-method-btn-icon-sms {
    background: linear-gradient(135deg, #f59e0b, #d97706);
  }
  .payment-method-btn-icon-terminal {
    background: linear-gradient(135deg, #6366f1, #4f46e5);
  }
  .payment-method-btn-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 0.25rem;
  }
  .payment-method-btn-sub {
    font-size: 0.8125rem;
    color: #64748b;
    line-height: 1.35;
  }

  /* Terminal sub-form */
  .terminal-amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
  }
  .terminal-receipt-options {
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0.75rem 0.875rem;
    .form-checkbox-label {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin: 0;
      padding: 0.25rem 0;
      font-size: 0.875rem;
      color: #334155;
      cursor: pointer;
      input[type="checkbox"] { margin: 0; }
    }
  }
  .terminal-receipt-email-block {
    margin-top: 0.625rem;
    padding-top: 0.625rem;
    border-top: 1px dashed #cbd5e1;
  }
  .terminal-warning-row {
    display: flex;
    align-items: center;
    font-size: 0.8125rem;
    color: #92400e;
    background: #fef3c7;
    border: 1px solid #fde68a;
    border-radius: 6px;
    padding: 0.4rem 0.625rem;
    margin: 0;
    i { color: #d97706; }
  }
  .terminal-error-row {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 0.5rem 0.75rem;
  }
  .terminal-link-btn {
    border: none;
    background: none;
    color: #4f46e5;
    font-size: 0.8125rem;
    font-weight: 500;
    padding: 0;
    cursor: pointer;
    text-decoration: underline;
    &:hover { color: #4338ca; }
  }

  /* Terminal state blocks (sending / waiting / success / declined / cancelled / error) */
  .terminal-state-block {
    text-align: center;
    padding: 1.75rem 1.25rem 1.25rem !important;
  }
  .terminal-state-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    margin: 0 auto 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    i { font-size: 1.75rem; color: white; }
  }
  .terminal-state-icon-spin    { background: linear-gradient(135deg, #6366f1, #4f46e5); }
  .terminal-state-icon-success { background: linear-gradient(135deg, #10b981, #059669); }
  .terminal-state-icon-danger  { background: linear-gradient(135deg, #ef4444, #dc2626); }
  .terminal-state-icon-warning { background: linear-gradient(135deg, #f59e0b, #d97706); }
  .terminal-state-icon-muted   { background: linear-gradient(135deg, #94a3b8, #64748b); }

  .terminal-state-title {
    margin: 0 0 0.5rem;
    font-size: 1.0625rem;
    font-weight: 600;
    color: #0f172a;
  }
  .terminal-state-amount {
    font-size: 1.75rem;
    font-weight: 700;
    color: #059669;
    margin: 0.5rem 0;
  }
  .terminal-state-sub {
    font-size: 0.8125rem;
    color: #64748b;
    line-height: 1.5;
    margin: 0.25rem 0 0;
    max-width: 380px;
    margin-left: auto;
    margin-right: auto;
  }

  .recall-activity-timeline { padding: 8px 0; }
  .recall-timeline-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #f0f2f5;
  }
  .recall-timeline-item:last-child { border-bottom: none; }
  .recall-timeline-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #fff;
  }
  .recall-timeline-icon.text-icon    { background: linear-gradient(135deg, #3abf94, #2d9474); }
  .recall-timeline-icon.email-icon   { background: linear-gradient(135deg, #667eea, #764ba2); }
  .recall-timeline-icon.default-icon { background: linear-gradient(135deg, #718096, #4a5568); }
  .recall-timeline-content { flex: 1; }
  .recall-timeline-header {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .recall-activity-type { font-weight: 600; }
  .recall-attempt-badge {
    font-size: 0.75rem;
    color: #6c757d;
    background: #f1f3f5;
    padding: 2px 8px;
    border-radius: 10px;
  }
  .recall-timeline-details {
    color: #6c757d;
    font-size: 0.875rem;
    margin-top: 4px;
  }
  .recall-timeline-details i { margin-right: 6px; }

</style>

<!-- Unscoped styles for VueTables components that ::v-deep can't reach -->
<style lang="scss">
/* Reset any inherited styles on the type column cell and its wrapper div */
.VueTables__row td.type-col,
.VueTables__row td.type-col > div,
td.type-col,
td.type-col > div {
  background: none !important;
  background-color: transparent !important;
  border-radius: 0 !important;
}

/* Comm type tag pill styling - direct targeting */
span.comm-type-tag {
  display: inline-flex !important;
  align-items: center !important;
  gap: 0.25rem !important;
  padding: 0.1875rem 0.5rem !important;
  background: #e8f4ea !important;
  background-color: #e8f4ea !important;
  border-radius: 10rem !important;
  font-size: 0.6875rem !important;
  font-weight: 600 !important;
  color: #3d8b4f !important;
  white-space: nowrap !important;
  line-height: 1.2 !important;
  width: auto !important;
  height: auto !important;
  position: relative !important;
  overflow: visible !important;
}

span.comm-type-tag::before,
span.comm-type-tag::after {
  display: none !important;
  content: none !important;
}

span.comm-type-tag i {
  font-size: 0.625rem !important;
  color: #3d8b4f !important;
  background: none !important;
}

/* Status tags - compact pill style */
span.status-tag {
  display: inline-flex !important;
  align-items: center !important;
  gap: 0.25rem !important;
  padding: 0.1875rem 0.5rem !important;
  border-radius: 10rem !important;
  font-size: 0.6875rem !important;
  font-weight: 600 !important;
  white-space: nowrap !important;
  line-height: 1.2 !important;
  width: auto !important;
  height: auto !important;
  border: none !important;
}

span.status-tag::before,
span.status-tag::after {
  display: none !important;
  content: none !important;
}

span.status-tag i {
  font-size: 0.625rem !important;
  background: none !important;
}

span.status-tag-pending {
  background: #fef3c7 !important;
  background-color: #fef3c7 !important;
  color: #92400e !important;
}

span.status-tag-pending i {
  color: #d97706 !important;
}

span.status-tag-success {
  background: #dcfce7 !important;
  background-color: #dcfce7 !important;
  color: #166534 !important;
}

span.status-tag-success i {
  color: #16a34a !important;
}

span.status-tag-default {
  background: #f1f5f9 !important;
  background-color: #f1f5f9 !important;
  color: #475569 !important;
}

/* Tab count badges - compact pill style */
.tab-count-badge {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 1.125rem !important;
  height: 1.125rem !important;
  padding: 0 0.375rem !important;
  margin-left: 0.375rem !important;
  border-radius: 10rem !important;
  font-size: 0.6875rem !important;
  font-weight: 600 !important;
  line-height: 1 !important;
}

.tab-count-badge-primary {
  background: #3b82f6 !important;
  background-color: #3b82f6 !important;
  color: white !important;
}

.tab-count-badge-danger {
  background: #ef4444 !important;
  background-color: #ef4444 !important;
  color: white !important;
}

.tab-count-badge-success {
  background: #10b981 !important;
  background-color: #10b981 !important;
  color: white !important;
}

.tab-count-badge-info {
  background: #0ea5e9 !important;
  background-color: #0ea5e9 !important;
  color: white !important;
}
</style>
